/**
 * Category Research Management - Super Admin Only
 *
 * Unified interface for managing AI-researched category characteristics:
 * - Product types, brand tiers, use cases, premium features, personas, tier vocabulary
 * - View freshness and status of each research aspect
 * - Trigger research refresh for stale categories
 * - Monitor research costs
 *
 * Date: January 10, 2026
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { PersonaReconciliationPanel } from '@/components/personas/PersonaReconciliationPanel';
import { LeafRetirementPanel } from '@/components/category-research/LeafRetirementPanel';
import { ResearchGap, type ResearchGapReceipt } from '@/components/category-research/ResearchGap';
import { researchJobBlocks, researchJobHasCatalogScope, researchJobsOverlap, researchJobRetired, type ResearchHandoff } from '@/components/category-research/researchJobScope';
import { ResearchContinuationPanel } from '@/components/category-research/ResearchContinuationPanel';
import { catalogResearchRows, readCatalogSubmission, compactResearchHistory, type CatalogSubmission } from '@/components/category/catalogResearchRows';
import { categoryWriteKey, catalogueRootsFrom } from '@/components/category/categoryWriteKey';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Layers,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Loader2,
  DollarSign,
  TrendingUp,
  Package,
  Tag,
  Users,
  Sparkles,
  MessageSquare,
  ChevronRight,
  Eye,
  AlertTriangle,
  Settings,
  Zap,
  Database,
  Link2,
  Heart,
  Edit3,
  Plus,
  Trash2,
  X,
  Save,
  Lightbulb,
  Search,
  Check,
  Wand2,
  AlertTriangle as WarningIcon,
  ArrowRight,
  ArrowLeft,
  Play,
  Square,
  CheckSquare,
  ListChecks,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { authFetch } from '@/utils/auth-fetch';
import { RefreshConfirmDialog } from '@/components/category/RefreshConfirmDialog';
import { resolvedSectorForWrite } from '@/components/category/resolveSectorResponse';
import { sectorResolveKey } from '@/components/category/sectorResolveKey';
import { refreshTargetPath, rowKey } from '@/components/category/refreshTarget';
import { classifyRefreshResult, classifyPollResult, toastForOutcome, pollExhaustionToast, recordPollFailure, NO_POLL_FAILURES, storedKeyFrom } from '@/components/category/refreshOutcome';
import { useToast } from '@/components/ui/use-toast';
import { API_URL } from '@/config/api';
import { decideNearMissResponse, CanonicalNearMiss } from './canonicalNearMissAdvisory';
import { useSite } from '@/contexts/SiteContext';
import {
  runRegeneratePersonasChain,
  describeChainSuccess,
  describeChainFailure,
  type RegenPhase,
} from './regeneratePersonasChain';
import { useAuth } from '@/context/auth-context';
import { cn } from '@/lib/utils';
import { batchStepDotClass, batchStepTitle, batchResearchGapCount } from './batchResearchStepStatus';
import { classifyChatOperationOutcome, type ChatOperationOutcome } from './chatOperationOutcome';
// Shared with pages/CategoryResearch.tsx — see PersonaHealth.tsx's header for why this is imported
// and not re-implemented (task-es9vcil1gn).
import { PersonaHealthBadges, PersonaGrid, type PersonaLike } from '@/components/category/PersonaHealth';
import { PersonaRewriteEnqueue } from '@/components/personas/PersonaRewriteEnqueue';
import { AttributeIntelligenceBulkResearch } from '@/components/category-research/AttributeIntelligenceBulkResearch';
// The repair action and the guard on the refresh beside it — shared for the same reason
// (task-htf7zulfpv). This is the DISCOVERABLE page, so the repair has to be here.
import {
  PersonaRepairButton,
  PersonaRefreshConfirmDialog,
  weightedCountOf,
  shouldConfirmRefresh,
} from '@/components/category/PersonaRepair';

interface ResearchDimensionState {
  status: string;
  label: string;
  freshness?: {basis: string; oldestAt: string | null; newestAt: string | null};
  stages: {stage: string; status: string; inPlan: boolean; receipt?: unknown}[];
}

export function ResearchDimensionDetails({states,names}: {states?: ResearchDimensionState[] | null; names: string[]}) {
  if(!states)return null;
  return <details className="mt-1 text-xs" onClick={event=>event.stopPropagation()}>
    <summary>Research steps</summary>
    <ul className="space-y-1 mt-1">
      {states.map((state,index)=><li key={names[index]||index}>
        <span>{names[index]}: {state.label}</span>
        {state.freshness&&<span className="block text-muted-foreground">
          {state.freshness.oldestAt
            ? 'Original '+(state.freshness.basis==='original_publication'?'publication':'source update')+': '+state.freshness.oldestAt+
              (state.freshness.newestAt!==state.freshness.oldestAt?' – '+state.freshness.newestAt:'')
            : 'Original freshness not recorded'}
        </span>}
      </li>)}
    </ul>
  </details>;
}
function dimensionStateTooltip(states?: ResearchDimensionState[] | null,names: string[]=[]) {
  return states?.map((state,index)=>names[index]+': '+state.label+
    (state.freshness?' (original '+(state.freshness.oldestAt||'freshness not recorded')+')':'')).join('; ');
}

interface CategoryCharacteristics {
  id: string;
  sectorId: number;
  categoryPath: string;
  categoryName: string;
  status: 'pending' | 'researching' | 'active' | 'stale' | 'failed';
  aspects: {
    product_types: { hasData: boolean; count: number; data: any[] };
    brand_tiers: { hasData: boolean; tierCount: number; data: any };
    use_cases: { hasData: boolean; count: number; data: any[] };
    premium_features: { hasData: boolean; count: number; data: any[] };
    personas: { hasData: boolean; count: number; data: any[] };
    tier_vocabulary: { hasData: boolean; tierCount: number; data: any };
    price_tiers: { hasData: boolean; tierCount: number; data: any };
    spec_semantics?: { hasData: boolean; specCount: number; data: any };
    measurement_units?: { hasData: boolean; unitCount: number; data: any };
    validation_rules?: { hasData: boolean; data: any };
  };
  researchDimensionStates?: ResearchDimensionState[] | null;
  researchDimensions: boolean[] | null; // 7-dimension completeness array
  researchReadiness?: {ready: boolean; manifestId: string; code?: string; reason?: string; openItems?: {attribute: string | null; sourceIdentity?: string; status: string; reason: string; valueCount: number; provenanceCount: number}[]} | null;
  freshness: {
    status: 'fresh' | 'aging' | 'stale' | 'none' | 'failed' | 'researching';
    message: string;
    daysOld: number | null;
    error?: string;
  };
  // Always present when the backend has written one, regardless of `status` — unlike
  // freshness.error, which the backend only sets when status === 'failed'. A refresh can
  // return 200 with status still 'active' and a non-empty researchError (partial write,
  // e.g. CANONICAL_SCHEMA_WRITE_INCOMPLETE) — that case has freshness.error === undefined.
  researchError?: string | null;
  lastResearchedAt: string | null;
  staleAfter: string | null;
  researchModel: string | null;
  researchCostUsd: number | null;   // null = researched but the cost could not be measured (task-px4ret1um8)
  // Grounding: catalogue products the research was based on. Served by
  // routes/category-characteristics.js:242 for BOTH the by-category route and the list route this
  // page uses (:335 SELECT *, formatted at :425). null = not recorded; 0 = the personas were
  // invented from general knowledge, not the catalogue (task-em3cnl32tq / task-es9vcil1gn).
  sampleProductCount: number | null;
  /** Served by formatCharacteristicsResponse (routes:284) for both the list and by-category
   *  routes. It is the stamp the operator is looking at, and the regenerate chain sends it as an
   *  optimistic precondition so a row that moved since the page was drawn refuses rather than
   *  being clobbered. Was present in the payload but undeclared here, so nothing used it. */
  updatedAt: string | null;
  timesUsed: number;
  lastUsedAt: string | null;
}

interface SummaryStats {
  total: number;
  byStatus: {
    active: number;
    stale: number;
    pending: number;
    failed: number;
    researching: number;
  };
  totalCostUsd: number;
  avgUsage: number;
  oldestResearch: string | null;
  newestResearch: string | null;
}

interface AvailableCategory {
  categoryPath: string;
  categoryName: string;
  productCount: number;
  brandCount: number;
  priceRange: { min: number; max: number } | null;
  hasResearch: boolean;
  researchStatus: string | null;
  lastResearchedAt: string | null;
  /**
   * ⛔⛔ WHICH RESEARCH ROW BACKS THIS LINE, AND HOW HEALTHY IT IS.
   * The available-categories join matches a research row by LEAF NAME **or** by FULL PATH, so a
   * category with both shapes comes back TWICE — and every other field on the two lines is
   * identical because it all comes from the same feed-side row. They were indistinguishable on
   * screen while each carried its own destructive Refresh. (task-fx7gfghirm.)
   * ⚠️ AND NEITHER SHAPE IS RELIABLY THE GOOD ONE — see the badge below. Do not use these fields
   * to pick a row to delete.
   */
  researchCategoryPath: string | null;
  researchKeyIsLeaf: boolean;
  /**
   * ⛔ THE RESEARCH ROW'S id — the ONLY key that opens the right detail from this tab.
   * `categoryPath` cannot do it: the column holds two conventions, both lines are returned when
   * both shapes exist, and a path lookup would pick one arbitrarily. A uuid names one row.
   * null = no research row joined, which is a real and renderable state here.
   */
  characteristicsId: string | null;
  personaCount: number | null;
  /** null = no research row joined at all, which is NOT the same fact as zero weighted personas. */
  weightedPersonaCount: number | null;
}

interface TechnicalResearchStatus {
  sectorId?: number;
  readiness?: {ready: boolean; reason?: string; code?: string; manifestId: string};
  categoryName: string;
  categoryPath: string;
  research: {
    canonicalAttributes: { count: number; hasData: boolean };
    attributeMappings: { count: number; hasData: boolean };
    perceivedBenefits: { count: number; hasData: boolean };
    /** ⛔ `sectorIds` — PLURAL, and the plurality is the contract. The count ranges over EVERY
     *  sector the selected site owns, because that is what `AEOProductPageService` can consume;
     *  the click-through viewer reads the identical set. This field was `sectorId?: number`
     *  naming only the VIEWED sector, which described a narrower scope than the number it
     *  labelled and reinforced the single-sector reading in the UI. */
    attributeIntelligence: { count: number; hasData: boolean; sectorIds?: number[] };
    /** ⛔ NULLABLE ON PURPOSE. category_spec_cache is site-scoped, so with no site selected there
     *  is no honest per-category count and the API sends null + `unavailableReason` rather than a
     *  fleet-wide total. Render the reason; do NOT coalesce a null to 0. */
    extractionHints?: {
      count: number | null;
      withHints: number | null;
      hasData: boolean | null;
      siteId?: string | null;
      unavailableReason?: string;
    };
  };
}

interface ExtractionHint {
  specName: string;
  displayName: string;
  guidance: string;
  typicalRange: string | null;
  unit: string;
  fieldDisambiguation: {
    prefer_fields?: string[];
    avoid_fields?: string[];
    notes?: string;
  } | null;
  qualifierRules: {
    preserve?: string[];
    examples?: string[];
  } | null;
  semanticClarifications: {
    mislabels?: Record<string, string>;
    notes?: string;
  } | null;
  isMultiSelect: boolean;
  isKeyAttribute: boolean;
  priorityRank: number;
  confidenceScore: number;
  hasDisambiguation: boolean;
  hasQualifierRules: boolean;
  hasSemanticClarifications: boolean;
}

interface CanonicalAttribute {
  shared_definition?: CanonicalAttribute;
  write_scope?: 'shared';
  id: string;
  product_category: string;
  attribute_name: string;
  canonical_name: string;
  data_type: string;
  unit: string | null;
  typical_min: number | null;
  typical_max: number | null;
  enum_values: string[] | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface AttributeIntelligenceEntry {
  id: string;
  title: string;
  scope_attribute_name: string;
  scope_category_path: string;
  research_findings: string;
  knowledge_text: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Sector {
  id: string;
  name: string;
  description: string;
  parent_sector_id: string | null;
  created_at: string;
  updated_at: string;
}

const ASPECT_CONFIG = {
  product_types: {
    label: 'Product Types',
    icon: Package,
    description: 'Subcategories and product type patterns',
    color: 'blue',
  },
  brand_tiers: {
    label: 'Brand Tiers',
    icon: Tag,
    description: 'Premium, mid, entry brand classifications',
    color: 'purple',
  },
  use_cases: {
    label: 'Use Cases',
    icon: TrendingUp,
    description: 'Product use case patterns and regex',
    color: 'green',
  },
  premium_features: {
    label: 'Premium Features',
    icon: Sparkles,
    description: 'Features that define premium products',
    color: 'yellow',
  },
  personas: {
    label: 'Personas',
    icon: Users,
    description: 'Buyer personas for this category',
    color: 'pink',
  },
  tier_vocabulary: {
    label: 'Tier Vocabulary',
    icon: MessageSquare,
    description: 'Language guidance per price tier',
    color: 'orange',
  },
  price_tiers: {
    label: 'Price Tiers',
    icon: DollarSign,
    description: 'Market-based price tier thresholds',
    color: 'green',
  },
};

function FreshnessIndicator({ freshness }: { freshness: CategoryCharacteristics['freshness'] }) {
  const config = {
    fresh: { color: 'bg-green-500', icon: Clock, text: 'text-green-700 dark:text-green-400' },
    aging: { color: 'bg-yellow-500', icon: Clock, text: 'text-yellow-700 dark:text-yellow-400' },
    stale: { color: 'bg-orange-500', icon: AlertTriangle, text: 'text-orange-700 dark:text-orange-400' },
    none: { color: 'bg-gray-400', icon: XCircle, text: 'text-gray-500 dark:text-gray-400' },
    failed: { color: 'bg-red-500', icon: AlertCircle, text: 'text-red-700 dark:text-red-400' },
    researching: { color: 'bg-blue-500', icon: Loader2, text: 'text-blue-700 dark:text-blue-400' },
  };

  const cfg = config[freshness.status];
  const Icon = cfg.icon;

  // Fix message for negative days
  let message = freshness.message;
  if (['fresh','aging','stale'].includes(freshness.status)) {
    message = freshness.daysOld == null ? 'Record update time unknown'
      : freshness.daysOld <= 0 ? 'Record updated today' : `Record updated ${freshness.daysOld} days ago`;
  }

  return (
    <div className="flex items-center gap-2">
      <div className={cn('w-2 h-2 rounded-full', cfg.color)} />
      <Icon className={cn('h-4 w-4', cfg.text, freshness.status === 'researching' && 'animate-spin')} />
      <span className={cn('text-sm', cfg.text)}>{message}</span>
    </div>
  );
}

function AspectStatus({ aspect, data, sampleProductCount }: {
  aspect: keyof typeof ASPECT_CONFIG;
  data: any;
  /** Grounding for the personas aspect: how many catalogue products the research was based on.
   *  ⛔ null and undefined both mean NOT RECORDED and must NOT become 0 — 0 is the distinct, known
   *  state "invented from general knowledge". Only the personas aspect reads this. */
  sampleProductCount?: number | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const config = ASPECT_CONFIG[aspect];
  const Icon = config.icon;
  const hasData = data?.hasData || false;
  const count = data?.count || data?.tierCount || 0;
  const actualData = data?.data;

  const renderDataPreview = () => {
    if (!actualData) return null;

    switch (aspect) {
      case 'product_types':
        return (
          <div className="space-y-2">
            {Array.isArray(actualData) && actualData.map((pt: any, i: number) => (
              <div key={i} className="p-2 bg-white dark:bg-gray-900 rounded border">
                <div className="font-medium text-sm">{pt.type_name?.en || pt.type_key}</div>
                {pt.keywords && <div className="text-xs text-muted-foreground">Keywords: {pt.keywords.slice(0, 5).join(', ')}</div>}
                {pt.semantic_anchors?.en ? (
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    <Search className="inline h-3 w-3 mr-1" />
                    Anchors: {pt.semantic_anchors.en.slice(0, 3).join(', ')}
                  </div>
                ) : (
                  <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Missing semantic anchors - needs re-research
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'brand_tiers':
        return (
          <div className="grid grid-cols-3 gap-2">
            {['premium', 'mid', 'entry'].map(tier => (
              <div key={tier} className="p-2 bg-white dark:bg-gray-900 rounded border">
                <div className="font-medium text-sm capitalize">{tier}</div>
                <div className="text-xs text-muted-foreground">
                  {Array.isArray(actualData[tier]) ? actualData[tier].slice(0, 5).join(', ') : '-'}
                </div>
              </div>
            ))}
          </div>
        );

      case 'use_cases':
        return (
          <div className="space-y-2">
            {Array.isArray(actualData) && actualData.map((uc: any, i: number) => (
              <div key={i} className="p-2 bg-white dark:bg-gray-900 rounded border">
                <div className="font-medium text-sm">{uc.display_name?.en || uc.use_case}</div>
                {uc.description && <div className="text-xs text-muted-foreground">{uc.description}</div>}
              </div>
            ))}
          </div>
        );

      case 'premium_features':
        return (
          <div className="space-y-2">
            {Array.isArray(actualData) && actualData.map((f: any, i: number) => (
              <div key={i} className="p-2 bg-white dark:bg-gray-900 rounded border">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">#{f.priority}</Badge>
                  <span className="font-medium text-sm">{f.display_label?.en || f.feature_key || f.attribute_key}</span>
                </div>
                {f.description && (
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{f.description}</div>
                )}
                {f.anchor_phrase?.en ? (
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    <Search className="inline h-3 w-3 mr-1" />
                    Anchor: "{f.anchor_phrase.en}"
                  </div>
                ) : (
                  <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Missing anchor phrase
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      // ⛔ THIS USED TO BE NAME + DESCRIPTION ONLY, which collapsed the three states a category can be
      // in — never researched / researched but weightless / researched and complete — into one
      // appearance, on the surface an operator actually reaches from the sidebar. Same display as
      // /research/category-perceptions now, IMPORTED rather than re-implemented (task-es9vcil1gn).
      case 'personas':
        return <PersonaGrid personas={Array.isArray(actualData) ? actualData : []} />;

      case 'tier_vocabulary':
        return (
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(actualData || {}).map(([tier, vocab]: [string, any]) => (
              <div key={tier} className="p-2 bg-white dark:bg-gray-900 rounded border">
                <div className="font-medium text-sm capitalize mb-1">{tier.replace('_', ' ')}</div>
                {vocab?.use && (
                  <div className="mb-1">
                    <span className="text-xs text-green-600 dark:text-green-400">Use:</span>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {vocab.use.slice(0, 3).map((t: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs bg-green-50 dark:bg-green-950/30 text-green-800 dark:text-green-300">{t}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {vocab?.avoid && (
                  <div>
                    <span className="text-xs text-red-600 dark:text-red-400">Avoid:</span>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {vocab.avoid.slice(0, 3).map((t: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300">{t}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'price_tiers':
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {['entry', 'mid_range', 'premium', 'ultra_premium'].map((tier) => {
              const tierData = actualData?.[tier];
              if (!tierData) return null;
              return (
                <div key={tier} className="p-3 bg-white dark:bg-gray-900 rounded border">
                  <div className="font-medium text-sm capitalize mb-1">{tier.replace('_', ' ')}</div>
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    {tierData.max ? `≤ €${tierData.max}` : 'No limit'}
                  </div>
                  {tierData.personas?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {tierData.personas.slice(0, 3).map((persona: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {persona.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                      {tierData.personas.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{tierData.personas.length - 3}</Badge>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      default:
        return <pre className="text-xs overflow-auto max-h-40">{JSON.stringify(actualData, null, 2)}</pre>;
    }
  };

  return (
    <div className={cn(
      'rounded-lg border overflow-hidden',
      hasData ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800' : 'bg-gray-50 border-gray-200 dark:bg-gray-950/20 dark:border-gray-700'
    )}>
      <div
        className={cn(
          'flex items-center justify-between p-3 cursor-pointer hover:bg-opacity-80',
          hasData && 'hover:bg-green-100 dark:hover:bg-green-900/30'
        )}
        onClick={() => hasData && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Icon className={cn('h-5 w-5', hasData ? 'text-green-600' : 'text-gray-400')} />
          <div>
            <p className="font-medium text-sm">{config.label}</p>
            <p className="text-xs text-muted-foreground">{config.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasData ? (
            <>
              {/* ⛔ THE HEALTH BADGES BELONG IN THE COLLAPSED HEADER, NOT ONLY IN THE PREVIEW. This row
                  renders green with a checkmark whenever the aspect has ANY rows, so a category whose
                  personas are all weightless — which cannot route anything — reads as healthy to an
                  operator scanning the list and never expanding it. Putting the state behind a click
                  would rebuild, one level up, the exact defect this card exists to remove. */}
              {aspect === 'personas' && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <PersonaHealthBadges
                    personas={Array.isArray(actualData) ? actualData : []}
                    count={count}
                    grounding={sampleProductCount ?? null}
                    showCount={false} /* the row already prints "N items" beside these */
                  />
                </div>
              )}
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                {count} {count === 1 ? 'item' : 'items'}
              </Badge>
              <CheckCircle className="h-4 w-4 text-green-600" />
              <ChevronRight className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')} />
            </>
          ) : (
            <Badge variant="outline" className="text-gray-500">No data</Badge>
          )}
        </div>
      </div>
      {expanded && hasData && (
        <div className="p-3 pt-0 border-t border-green-200 dark:border-green-800 bg-green-25 dark:bg-green-950/10">
          {renderDataPreview()}
        </div>
      )}
    </div>
  );
}

function CategoryDetailDialog({
  category,
  lastRefreshError,
  isOpen,
  onClose,
  onRefresh,
  onRefreshAspect,
  onRegeneratePersonas,
  regenPhase = null,
  technicalResearch,
  onRegenerateAttributes,
  onResearchIntelligence,
  onResearchExtractionHints,
  onResearchPerceivedBenefits,
  onResearchAttributeMappings,
  sectorId,
  writeSectorId,
  onReload,
  onOpenCanonicalDialog,
  onOpenIntelligenceDialog,
  onOpenExtractionHintsDialog,
  onOpenChatDialog,
  dimensionNames,
}: {
  category: CategoryCharacteristics | null;
  lastRefreshError?: string | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: (categoryPath: string) => Promise<void>;
  onRefreshAspect: (categoryPath: string, aspect: string) => Promise<void>;
  /** The four-step regenerate chain (clear -> recreate -> re-weight -> verify). Separate from
   *  onRefreshAspect because a plain refresh/personas on a weighted category is REFUSED. */
  onRegeneratePersonas: (categoryPath: string) => Promise<void>;
  /** Which step of the chain is running, so the button can say so. A four-step chain of LLM
   *  calls behind a single spinner looks indistinguishable from a hang. */
  regenPhase?: null | RegenPhase;
  technicalResearch: TechnicalResearchStatus | null;
  onRegenerateAttributes: (categoryId: string, forceRegenerate: boolean) => Promise<void>;
  onResearchIntelligence: (categoryPath: string, depth?: 'quick' | 'standard' | 'deep') => Promise<void>;
  onResearchExtractionHints: () => Promise<void>;
  onResearchPerceivedBenefits: (categoryName: string, forceRegenerate: boolean) => Promise<void>;
  onResearchAttributeMappings: (forceRegenerate: boolean) => Promise<void>;
  sectorId: number | undefined;
  /**
   * ⚠️ THE SECTOR THAT WRITES ACTUALLY GO TO, WHICH IS NOT ALWAYS THE ONE BEING VIEWED.
   * Both refresh handlers already resolve through getWriteSectorId(); the plain `sectorId` prop
   * above is the VIEWED sector and is currently unused inside this component. The repair is a
   * pair-taking write (sector + category path), so it must use the same resolved sector its
   * neighbouring buttons use — otherwise Repair and Refresh, side by side, would target different
   * rows. Passed explicitly rather than reusing `sectorId`, and shown to the operator in both
   * dialogs. (The wider question of which sector these handlers SHOULD take is open —
   * task-87537g8s9a — and nothing here changes existing behaviour.)
   */
  writeSectorId: number | undefined;
  /**
   * ⛔ A RELOAD THAT WRITES NOTHING. Bound to loadData at the call site. This exists as its own prop
   * precisely because every other handler this component holds is a MUTATION with a reassuring name
   * (onRefresh, onRefreshAspect), and the repair's success path must not be able to reach one.
   */
  onReload: (categoryPath: string) => Promise<void>;
  onOpenCanonicalDialog: () => void;
  onOpenIntelligenceDialog: () => void;
  onOpenExtractionHintsDialog: () => void;
  onOpenChatDialog?: () => void;
  /**
   * ⇒ THE SEVEN RESEARCH-PIPELINE DIMENSION NAMES, index-aligned with category.researchDimensions.
   * Served by the list endpoint (utils/researchDimensions.js is the single source), so the dialog
   * NAMES the missing dimension instead of leaving the operator with a bare "6/7". Jaume, at the
   * screen: "I keep seeing 6/7, but when I click on the category, I cannot tell which is the
   * missing one." The row already had a hover title; a hover is not an answer to a click.
   * ⚠️ These are NOT the seven aspects the tabs below show. Two different seven-item lists on one
   * screen — the heading says which this is.
   */
  dimensionNames: string[];
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingAspect, setRefreshingAspect] = useState<string | null>(null);
  /**
   * ⛔ WHICH DESTRUCTIVE REBUILD IS AWAITING CONFIRMATION. Both controls below used to run on the
   * click, replacing stored research — buyer personas included — with no warning and no way back.
   */
  const [pendingRefresh, setPendingRefresh] = useState<string | null>(null);
  // Guards the persona re-research when there are curated weights to lose (task-htf7zulfpv).
  const [confirmPersonaRefresh, setConfirmPersonaRefresh] = useState(false);
  const [confirmRefreshAll, setConfirmRefreshAll] = useState(false);
  /** ⛔ What THIS dialog session knows it wrote. See shouldConfirmRefresh — the guard must not
   *  depend on the reload having landed. Cleared when the dialog moves to another category. */
  const [weightsJustWritten, setWeightsJustWritten] = useState(false);
  const [regeneratingAttrs, setRegeneratingAttrs] = useState(false);
  const [researchingIntelligence, setResearchingIntelligence] = useState(false);
  const [researchingExtractionHints, setResearchingExtractionHints] = useState(false);
  const [researchingPerceivedBenefits, setResearchingPerceivedBenefits] = useState(false);
  const [researchingAttributeMappings, setResearchingAttributeMappings] = useState(false);
  const [forceRegenerate, setForceRegenerate] = useState(false);
  const [forceRegeneratePerceivedBenefits, setForceRegeneratePerceivedBenefits] = useState(false);
  const [forceRegenerateAttributeMappings, setForceRegenerateAttributeMappings] = useState(false);

  // ⛔ MUST STAY ABOVE THE `if (!category)` EARLY RETURN. This dialog is rendered with
  // category={null} while closed, so a hook placed after the return is SKIPPED on that
  // render and CALLED once a category is selected — one more hook than the previous
  // render, which is "Rendered more hooks than during the previous render." The optional
  // chaining in the dependency is what lets it sit up here safely.
  // ⚠️ task-87537g8s9a, member 3 of 3 — this file keys the latch on the FETCHED ROW's identity,
  // while src/pages/CategoryResearch.tsx keys the same latch on free-text input state. The
  // collected note listing all three members lives at that file's PersonaRefreshConfirmDialog
  // mounts. This side is the safer of the two; do not "align" them without reading that note.
  useEffect(() => { setWeightsJustWritten(false); }, [category?.categoryPath]);

  if (!category) return null;

  /** The personas as stored on this category — the input to BOTH guards' predicate. */
  const allPersonas: PersonaLike[] = Array.isArray(category.aspects.personas?.data)
    ? (category.aspects.personas.data as PersonaLike[])
    : [];

  const handleRefreshAll = async () => {
    setRefreshing(true);
    await onRefresh(category.categoryPath);
    setRefreshing(false);
  };

  const handleRefreshAspect = async (aspect: string) => {
    setRefreshingAspect(aspect);
    await onRefreshAspect(category.categoryPath, aspect);
    setRefreshingAspect(null);
  };

  const handleRegenerateAttributes = async () => {
    setRegeneratingAttrs(true);
    try {
      await onRegenerateAttributes(category.id, forceRegenerate);
    } finally {
      setRegeneratingAttrs(false);
    }
  };

  const handleResearchIntelligence = async (depth: 'quick' | 'standard' | 'deep' = 'standard') => {
    setResearchingIntelligence(true);
    try {
      await onResearchIntelligence(category.categoryPath, depth);
    } finally {
      setResearchingIntelligence(false);
    }
  };

  const handleResearchPerceivedBenefits = async () => {
    setResearchingPerceivedBenefits(true);
    try {
      // B2: the FULL PATH, never the last segment — the page-level handler decides whether the key may be sent.
      await onResearchPerceivedBenefits(category.categoryPath, forceRegeneratePerceivedBenefits);
      setForceRegeneratePerceivedBenefits(false);
    } finally {
      setResearchingPerceivedBenefits(false);
    }
  };

  const handleResearchAttributeMappings = async () => {
    setResearchingAttributeMappings(true);
    try {
      await onResearchAttributeMappings(forceRegenerateAttributeMappings);
      setForceRegenerateAttributeMappings(false);
    } finally {
      setResearchingAttributeMappings(false);
    }
  };

  // Format days old - handle negative values and show "Today" for 0
  const formatDaysOld = (daysOld: number | null) => {
    if (daysOld === null) return '-';
    if (daysOld <= 0) return 'Today';
    return `${daysOld}d`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            {category.categoryName}
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between">
            <span className="text-xs font-mono">{category.categoryPath}</span>
            <FreshnessIndicator freshness={category.freshness} />
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-120px)] pr-4 -mr-4">
          <div className="space-y-6 py-4 pr-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{category.timesUsed}</div>
                  <div className="text-xs text-muted-foreground">Times Used</div>
                </CardContent>
              </Card>
{/* Cost tracking hidden until properly debugged
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{category.researchCostUsd == null ? 'unmeasured' : `$${category.researchCostUsd.toFixed(4)}`}</div>
                  <div className="text-xs text-muted-foreground">Research Cost</div>
                </CardContent>
              </Card>
*/}
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{category.researchModel || '-'}</div>
                  <div className="text-xs text-muted-foreground">Model</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">
                    {formatDaysOld(category.freshness.daysOld)}
                  </div>
                  <div className="text-xs text-muted-foreground">Days Old</div>
                </CardContent>
              </Card>
            </div>

            {/* Research Aspects */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Research Aspects</h3>
                <div className="flex gap-2">
                  {onOpenChatDialog && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onOpenChatDialog}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Talk to Knowledge
                    </Button>
                  )}
                  <Button
                    size="sm"
                    /* ⛔⛔ WARN ALWAYS, ESCALATE WHEN WEIGHTS ARE AT STAKE. The predicate below no
                       longer decides WHETHER to warn — only HOW hard. It used to gate the warning
                       entirely, so the most common case (a category with personas and no hand-tuned
                       weights) ran straight through with no warning at all, and the personas are
                       replaced either way.
                       🔑 AND THE PREDICATE CANNOT TELL "nothing to lose" FROM "not loaded yet":
                       both read as an empty list, which is the exact defect three rounds of #4753
                       chased from one state to its neighbour. Warning every time removes that state
                       space instead of narrowing it. (Jaume, 02 Aug: a dangerous button must carry
                       a clear warning before it goes.)
                       ⚠️ THE ESCALATION IS NOT DECORATIVE: the type-REFRESH dialog says "This will
                       destroy N curated benefit-weight sets", which reads as "0" — nothing at
                       stake — on a category that has none. It is honest only where it fires. */
                    onClick={() => {
                      if (shouldConfirmRefresh(allPersonas, weightsJustWritten)) setConfirmRefreshAll(true);
                      else setPendingRefresh('all');
                    }}
                    /* ⛔⛔ REFUSE ON UNCERTAINTY LIKE THE REPAIR BUTTON DOES. This was exactly
                       backwards: the SAFE control (repair) refused an unknown sector while the
                       DESTRUCTIVE ones guessed, because their handlers go through
                       getWriteSectorId(), which falls back to the VIEWED sector while resolution
                       is in flight. ⇒ On a multi-sector site this could fire at (viewed sector,
                       path) while the confirm dialog beside it showed the sector as unknown — a
                       SILENT FALLBACK on a destructive path, which this repo bans outright.
                       Disabling changes no write targeting, so it does not trespass on
                       task-87537g8s9a. (codex blocking.) */
                    /* ⛔⛔ ALSO DISABLED DURING THE REGENERATE CHAIN (codex-reviewer BLOCKING).
                       The chain is FOUR SEPARATE REQUESTS and the clear's row lock ends when the
                       clear commits — it does not span them. So the only thing standing between an
                       ordinary operator click and an interleaved write is this control being
                       unavailable. Disabling ONLY the personas button, as the first version did,
                       left Re-Research All live and able to reintroduce personas mid-chain while
                       step 4 still reported success. */
                    disabled={refreshing || category.status === 'researching' || writeSectorId == null
                      || regenPhase !== null}
                  >
                    {refreshing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Re-Researching...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Re-Research All
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {(Object.keys(ASPECT_CONFIG) as Array<keyof typeof ASPECT_CONFIG>).map((aspect) => {
                  // ⚠️ ONE SOURCE OF TRUTH for the predicate. Two guards deriving the persona
                  // list separately is how they come to disagree about whether to fire.
                  const personaRows: PersonaLike[] = aspect === 'personas' ? allPersonas : [];
                  return (
                  <div key={aspect} className="flex items-center gap-2">
                    <div className="flex-1">
                      <AspectStatus
                        aspect={aspect}
                        data={category.aspects[aspect]}
                        sampleProductCount={category.sampleProductCount}
                      />
                    </div>
                    {/* ⛔ THE REPAIR BELONGS ON THIS PAGE ESPECIALLY. This is the surface the sidebar
                        reaches; the perceptions page has no nav entry. A repair shipped only there
                        would sit where the default click path never lands (task-htf7zulfpv). */}
                    {aspect === 'personas' && personaRows.length > 0 && (
                      <PersonaRepairButton
                        /* ⛔ The repair writes benefit_weights — exactly what the chain is
                           clearing and then rebuilding. Racing it against step 3 is how a
                           category ends up with weights from two different persona sets. */
                        disabled={regenPhase !== null}
                        sectorId={writeSectorId}
                        categoryPath={category.categoryPath}
                        personas={personaRows}
                        /* ⛔⛔ NON-MUTATING RELOAD, AND NOTHING ELSE MAY GO HERE. This was
                           onRefreshAspect(path, 'personas') — which POSTs /refresh/personas, the
                           DESTRUCTIVE re-research this PR exists to guard. It fired automatically on
                           a successful repair, so the button REPAIRED THE WEIGHTS AND THEN THREW THEM
                           AWAY, and it bypassed the new confirmation because the gate is on the
                           button, not on the handler.
                           🔑 THE WORD IS THE TRAP: onApplied wants to mean "refresh the view", and on
                           this page the nearest thing named refresh IS A MUTATION. Reaching for a
                           reload and getting a re-research is what the surrounding vocabulary makes
                           easy. Pinned by a wiring test that fails if any success path reaches a
                           mutating handler. */
                        onApplied={async () => {
                          // ⛔ LATCH BEFORE THE AWAIT. If the reload throws or returns stale data,
                          // the guards must still know this session wrote weights.
                          setWeightsJustWritten(true);
                          await onReload(category.categoryPath);
                        }}
                      />
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      /* ⛔ EVERY ASPECT, NOT JUST PERSONAS, AND NOT ONLY WHEN WEIGHTS EXIST.
                         Nothing about a round arrow says "replaces what is stored", and that is as
                         true of price tiers and spec meanings as of personas — the guard here used
                         to cover exactly one aspect out of ten. Personas with curated weights keep
                         the stronger type-REFRESH dialog because it can name what is lost; every
                         other click gets the plain warning. Nothing runs straight through. */
                      onClick={() => {
                        if (aspect === 'personas' && shouldConfirmRefresh(personaRows, weightsJustWritten)) setConfirmPersonaRefresh(true);
                        else setPendingRefresh(aspect);
                      }}
                      /* ⛔ Same refusal as Re-Research All above — an unknown sector must not
                         be guessed on a control that replaces stored research. */
                      /* ⛔ EVERY ASPECT, not just personas — a price-tier refresh mid-chain writes
                         the same row the chain is rebuilding. */
                      /* ⛔⛔ A NON-ACTIVE CATEGORY CANNOT COMPLETE A PERSONA REGENERATION, so the
                         control that starts one is disabled rather than left to fail halfway.
                         The chain finishes by re-weighting, and that step only runs on an ACTIVE
                         row — pressing this on a pending/failed category discarded the curated
                         weights, replaced the personas, and only then failed. Measured on
                         production: 20 rows are not active, and this control used to render on
                         every one of them.
                         ⚠️ Only the PERSONAS control is gated. The other aspects are single writes
                         that do not depend on the weighting service, so disabling them here would
                         remove working functionality for an unrelated reason.
                         ⚠️ AND THIS IS A HINT, NOT THE GUARD: the clear endpoint refuses with 422
                         inside its transaction, which is what protects a caller that never loads
                         this screen. */
                      disabled={refreshingAspect === aspect || refreshing || writeSectorId == null
                        || regenPhase !== null
                        || (aspect === 'personas' && category.status !== 'active')}
                      title={aspect === 'personas' && category.status !== 'active'
                        ? `This category is "${category.status}", not "active". Regeneration finishes `
                          + `by re-weighting the personas, and that step only runs on active `
                          + `categories — starting it here would discard the curated benefit weights `
                          + `and then fail. Re-research this category first.`
                        : undefined}
                      className="shrink-0"
                    >
                      {/* ⛔⛔ PERSONAS GET A LABEL, AND THAT IS THE POINT OF THE TASK, NOT A STYLE
                          PREFERENCE. The icon-only control was undiscoverable — an operator looking
                          for "regenerate the buyer personas" could not find a round arrow, and
                          reported it as missing. A control nobody can find is not delivered.
                          ⚠️ It also says REGENERATE rather than refresh: this replaces the personas
                          and discards their curated weights on the way. "Refresh" undersells it. */}
                      {aspect === 'personas' ? (
                        regenPhase ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            {regenPhase === 'checking' && 'Checking readiness…'}
                            {regenPhase === 'recreating' && 'Regenerating personas…'}
                            {regenPhase === 'weighting' && 'Restoring benefit weights…'}
                            {regenPhase === 'verifying' && 'Verifying…'}
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Regenerate buyer personas
                          </>
                        )
                      ) : refreshingAspect === aspect ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  );
                })}
                <PersonaRefreshConfirmDialog
                  open={confirmRefreshAll}
                  onOpenChange={setConfirmRefreshAll}
                  sectorId={writeSectorId}
                  categoryPath={category.categoryPath}
                  personas={allPersonas}
                  weightsJustWritten={weightsJustWritten}
                  scope="all"
                  loading={refreshing}
                  onConfirm={async () => {
                    setConfirmRefreshAll(false);
                    await handleRefreshAll();
                  }}
                />
                <PersonaRefreshConfirmDialog
                  open={confirmPersonaRefresh}
                  onOpenChange={setConfirmPersonaRefresh}
                  sectorId={writeSectorId}
                  categoryPath={category.categoryPath}
                  personas={allPersonas}
                  weightsJustWritten={weightsJustWritten}
                  loading={refreshingAspect === 'personas'}
                  onConfirm={async () => {
                    setConfirmPersonaRefresh(false);
                    // ⛔ THE CHAIN, NOT THE PLAIN REFRESH. refresh/personas alone is REFUSED on a
                    // weighted category — which is every category this dialog appears for.
                    await onRegeneratePersonas(category.categoryPath);
                  }}
                />
              </div>
            </div>

            {/* ⇒ WHICH RESEARCH DIMENSIONS THIS CATEGORY HAS, BY NAME.
                The list row shows "6/7" and carried the names only in a hover title; clicking
                through — which is what an operator does when they want to know WHICH — lost them
                entirely. The array is already on the category object and the names already arrive
                with the list response, so this needs no new query.
                ⚠️ A null researchDimensions means the list endpoint did not score this row (it
                scores only rows it served with dimension sets). Rendered as "not scored", never as
                seven crosses — an unscored category and a fully unresearched one are different
                facts and the second is alarming. */}
                {!!category.researchReadiness?.openItems?.length && (
                  <details className="text-sm mb-2">
                    <summary>Retained evidence and open items ({category.researchReadiness.openItems.length})</summary>
                    <ul className="list-disc pl-5">
                      {category.researchReadiness.openItems.map(item => (
                        <li key={item.sourceIdentity || item.attribute}>{item.attribute?.trim() ? item.attribute : `Unnamed identity (${item.sourceIdentity || "source unavailable"})`}: {item.status.replace(/_/g, ' ')} — {item.valueCount} stored values, {item.provenanceCount} with matching provenance. {item.reason}</li>
                      ))}
                    </ul>
                  </details>
                )}
            {category.researchDimensions && dimensionNames.length > 0 && (
              <div>
                {category.researchReadiness?.ready === false && (
                  <p role="alert" className="text-sm text-destructive mb-2">{category.researchReadiness.reason}</p>
                )}

                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold flex items-center gap-2">
                    <ListChecks className="h-4 w-4" />
                    Research Dimensions
                    <span className="text-xs font-normal text-muted-foreground">
                      ({category.researchDimensions.filter(Boolean).length}/{category.researchDimensions.length}) — the research PIPELINE, not the aspects below
                    </span>
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {dimensionNames.map((name, i) => {
                    const present = category.researchDimensions?.[i] === true;
                    return (
                      <Badge
                        key={name}
                        variant={present ? 'default' : 'destructive'}
                        className={cn('font-normal', !present && 'font-medium')}
                      >
                        {present ? '✓' : '✗'} {name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Technical Research Status */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Technical Research
                </h3>
              </div>

              {technicalResearch?.readiness?.ready === false && (
                <p className="mb-4 text-sm text-amber-700 dark:text-amber-300">{technicalResearch.readiness.reason}</p>
              )}
              <div className="grid grid-cols-5 gap-4 mb-4">
                <Card
                  className={cn(
                    'border cursor-pointer transition-all hover:shadow-md',
                    technicalResearch?.research.canonicalAttributes.hasData
                      ? 'border-green-200 bg-green-50 dark:bg-green-950/20 hover:border-green-400'
                      : 'border-gray-200 bg-gray-50 dark:bg-gray-950/20 hover:border-gray-400'
                  )}
                  onClick={onOpenCanonicalDialog}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Database className={cn(
                          'h-4 w-4',
                          technicalResearch?.research.canonicalAttributes.hasData ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                        )} />
                        <span className="font-medium text-sm">Canonical Attributes</span>
                      </div>
                      <Edit3 className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="text-2xl font-bold">
                      {technicalResearch?.research.canonicalAttributes.count ?? '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Technical specs defined</div>
                  </CardContent>
                </Card>

                <Card className={cn(
                  'border',
                  technicalResearch?.research.attributeMappings.hasData
                    ? 'border-green-200 bg-green-50 dark:bg-green-950/20'
                    : 'border-gray-200 bg-gray-50 dark:bg-gray-950/20'
                )}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Link2 className={cn(
                        'h-4 w-4',
                        technicalResearch?.research.attributeMappings.hasData ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                      )} />
                      <span className="font-medium text-sm">Attribute Mappings</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {technicalResearch?.research.attributeMappings.count ?? '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Tech → benefit mappings</div>
                  </CardContent>
                </Card>

                <Card className={cn(
                  'border',
                  technicalResearch?.research.perceivedBenefits.hasData
                    ? 'border-green-200 bg-green-50 dark:bg-green-950/20'
                    : 'border-gray-200 bg-gray-50 dark:bg-gray-950/20'
                )}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className={cn(
                        'h-4 w-4',
                        technicalResearch?.research.perceivedBenefits.hasData ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                      )} />
                      <span className="font-medium text-sm">Perceived Benefits</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {technicalResearch?.research.perceivedBenefits.count ?? '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Validated benefits</div>
                  </CardContent>
                </Card>

                <Card
                  className={cn(
                    'border cursor-pointer transition-all hover:shadow-md',
                    technicalResearch?.research.attributeIntelligence?.hasData
                      ? 'border-green-200 bg-green-50 dark:bg-green-950/20 hover:border-green-400'
                      : 'border-gray-200 bg-gray-50 dark:bg-gray-950/20 hover:border-gray-400'
                  )}
                  onClick={onOpenIntelligenceDialog}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className={cn(
                        'h-4 w-4',
                        technicalResearch?.research.attributeIntelligence?.hasData ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                      )} />
                      <span className="font-medium text-sm">Attribute Intelligence</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {technicalResearch?.research.attributeIntelligence?.count ?? '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Value interpretations (click to view)</div>
                  </CardContent>
                </Card>

                <Card
                  className={cn(
                    'border cursor-pointer transition-all hover:shadow-md',
                    technicalResearch?.research.extractionHints?.hasData
                      ? 'border-green-200 bg-green-50 dark:bg-green-950/20 hover:border-green-400'
                      : 'border-gray-200 bg-gray-50 dark:bg-gray-950/20 hover:border-gray-400'
                  )}
                  onClick={onOpenExtractionHintsDialog}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Wand2 className={cn(
                        'h-4 w-4',
                        technicalResearch?.research.extractionHints?.hasData ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                      )} />
                      <span className="font-medium text-sm">Extraction Hints</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {technicalResearch?.research.extractionHints?.withHints ?? '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {technicalResearch?.research.extractionHints?.unavailableReason
                        ?? 'Field disambiguation rules for the selected site (click to view)'}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Regenerate Attributes Section */}
              <Card className="p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-sm mb-1">Regenerate Canonical Attributes</h4>
                    <p className="text-xs text-muted-foreground">
                      Generate or regenerate technical attribute definitions for this category using AI
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Checkbox
                        id="forceRegenerate"
                        checked={forceRegenerate}
                        onCheckedChange={(checked) => setForceRegenerate(checked === true)}
                      />
                      <Label htmlFor="forceRegenerate" className="text-xs cursor-pointer">
                        Force regenerate (replace existing attributes)
                      </Label>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleRegenerateAttributes}
                    disabled={regeneratingAttrs}
                    className="shrink-0"
                  >
                    {regeneratingAttrs ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        {technicalResearch?.research.canonicalAttributes.hasData ? 'Regenerate' : 'Generate'}
                      </>
                    )}
                  </Button>
                </div>
              </Card>

              {/* Research Attribute Intelligence Section */}
              <Card className={cn(
                "p-4 border",
                !technicalResearch?.research.perceivedBenefits.hasData
                  ? "bg-gray-50 dark:bg-gray-950/20 border-gray-200 opacity-75"
                  : "bg-amber-50 dark:bg-amber-950/20 border-amber-200"
              )}>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-sm mb-1 flex items-center gap-2">
                      Research Attribute Intelligence
                      {!technicalResearch?.research.perceivedBenefits.hasData && (
                        <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-300">
                          Requires: Perceived Benefits
                        </Badge>
                      )}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Generate value interpretations (what TPI=60 vs TPI=120 means to customers)
                    </p>
                  </div>
                  {!technicalResearch?.research.perceivedBenefits.hasData ? (
                    <div className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Research Perceived Benefits first - intelligence is derived from technical features
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleResearchIntelligence('standard')}
                          disabled={researchingIntelligence}
                          variant="default"
                          title="Research attribute value interpretations for this category"
                        >
                          {researchingIntelligence ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><Sparkles className="h-4 w-4 mr-1" />Research</>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>

              {/* Research Extraction Hints Section */}
              <Card className={cn(
                "p-4 border",
                !technicalResearch?.research.canonicalAttributes.hasData
                  ? "bg-gray-50 dark:bg-gray-950/20 border-gray-200 opacity-75"
                  : "bg-purple-50 dark:bg-purple-950/20 border-purple-200"
              )}>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-sm mb-1 flex items-center gap-2">
                      Research Extraction Hints
                      {!technicalResearch?.research.canonicalAttributes.hasData && (
                        <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                          Recommended: Canonical Attributes first
                        </Badge>
                      )}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Generate field disambiguation rules (which fields to prefer/avoid, qualifier preservation, semantic clarifications)
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Research only. Product enrichment is a separate action.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={async () => {
                        setResearchingExtractionHints(true);
                        try {
                          await onResearchExtractionHints();
                        } finally {
                          setResearchingExtractionHints(false);
                        }
                      }}
                      disabled={researchingExtractionHints}
                      variant="default"
                      title="Choose extraction hint research for this catalog category"
                    >
                      {researchingExtractionHints ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Researching...
                        </>
                      ) : (
                        <>
                          <Wand2 className="h-4 w-4 mr-1" />
                          Research Hints
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Research Perceived Benefits Section */}
              <Card className={cn(
                "p-4 border",
                !technicalResearch?.research.canonicalAttributes.hasData
                  ? "bg-gray-50 dark:bg-gray-950/20 border-gray-200 opacity-75"
                  : "bg-pink-50 dark:bg-pink-950/20 border-pink-200"
              )}>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-sm mb-1 flex items-center gap-2">
                      Research Perceived Benefits
                      {!technicalResearch?.research.canonicalAttributes.hasData && (
                        <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                          Recommended: Canonical Attributes first
                        </Badge>
                      )}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Generate customer-perceived benefits and purchase drivers for this category
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      id="forceRegeneratePerceivedBenefits"
                      checked={forceRegeneratePerceivedBenefits}
                      onCheckedChange={(checked) => setForceRegeneratePerceivedBenefits(checked === true)}
                    />
                    <Label htmlFor="forceRegeneratePerceivedBenefits" className="text-xs text-muted-foreground cursor-pointer">
                      Force regenerate (delete existing and research all)
                    </Label>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleResearchPerceivedBenefits}
                      disabled={researchingPerceivedBenefits}
                      variant="default"
                      title="Research perceived benefits for this category"
                    >
                      {researchingPerceivedBenefits ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Researching...
                        </>
                      ) : (
                        <>
                          <Heart className="h-4 w-4 mr-1" />
                          Research Benefits
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Research Attribute Mappings Section */}
              <Card className={cn(
                "p-4 border",
                !technicalResearch?.research.perceivedBenefits.hasData
                  ? "bg-gray-50 dark:bg-gray-950/20 border-gray-200 opacity-75"
                  : "bg-cyan-50 dark:bg-cyan-950/20 border-cyan-200"
              )}>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-sm mb-1 flex items-center gap-2">
                      Research Attribute Mappings
                      {!technicalResearch?.research.perceivedBenefits.hasData && (
                        <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-300">
                          Requires: Perceived Benefits
                        </Badge>
                      )}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Generate persona → benefit mappings (which personas care about which benefits)
                    </p>
                  </div>
                  {!technicalResearch?.research.perceivedBenefits.hasData ? (
                    <div className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Research Perceived Benefits first to enable this feature
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <Checkbox
                          id="forceRegenerateAttributeMappings"
                          checked={forceRegenerateAttributeMappings}
                          onCheckedChange={(checked) => setForceRegenerateAttributeMappings(checked === true)}
                        />
                        <Label htmlFor="forceRegenerateAttributeMappings" className="text-xs text-muted-foreground cursor-pointer">
                          Force regenerate (delete existing and research all)
                        </Label>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleResearchAttributeMappings}
                          disabled={researchingAttributeMappings}
                          variant="default"
                          title="Generate attribute mappings for this sector"
                        >
                          {researchingAttributeMappings ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Link2 className="h-4 w-4 mr-1" />
                              Generate Mappings
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            </div>

            {/* Tier Vocabulary Preview */}
            {category.aspects.tier_vocabulary.hasData && (
              <div>
                <h3 className="font-semibold mb-3">Tier Vocabulary Preview</h3>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(category.aspects.tier_vocabulary.data || {}).map(([tier, vocab]: [string, any]) => (
                    <Card key={tier} className="p-3">
                      <h4 className="font-medium text-sm capitalize mb-2">{tier.replace('_', ' ')}</h4>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-green-600 dark:text-green-400 font-medium">Use:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(vocab?.use || []).slice(0, 4).map((term: string, i: number) => (
                              <Badge key={i} variant="outline" className="text-xs bg-green-50 dark:bg-green-950/30 text-green-800 dark:text-green-300">
                                {term}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-red-600 dark:text-red-400 font-medium">Avoid:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(vocab?.avoid || []).slice(0, 4).map((term: string, i: number) => (
                              <Badge key={i} variant="outline" className="text-xs bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300">
                                {term}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Error Display — reads researchError, which the backend sends unconditionally
                (row.research_error), NOT just freshness.error, which is only populated when
                status === 'failed'. A refresh can return 200 with status still 'active' and a
                non-empty researchError (the canonical-schema write is deliberately post-commit
                and can fail independently, e.g. CANONICAL_SCHEMA_WRITE_INCOMPLETE) — that case
                has freshness.error === undefined and was previously invisible here entirely. */}
            {(category.researchError || category.freshness.error) && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{category.status === 'failed' ? 'Research Error:' : 'Research Warning:'}</strong>{' '}
                  {category.researchError || category.freshness.error}
                </AlertDescription>
              </Alert>
            )}
            {/* ⛔⛔ THE DOOR BEHIND THE REFUSAL (task-2j4ar2hvty). preserveBenefitWeights refuses a
                refresh that would strand curated benefit-weights and says "THERE IS NO CONTROL TODAY
                THAT RECONCILES PERSONA KEYS". This renders that control, and ONLY for the two
                refusals it can actually resolve — the panel returns null for anything else, because
                A CONTROL OFFERED WHERE IT CANNOT ACT TEACHES THE OPERATOR THAT THE CONTROL IS BROKEN.
                ⚠️ Placed inside the same error block so it appears exactly where the operator is
                already looking, rather than in a nav item they would have to find. */}
            {/* ⚠️ writeSectorId, NOT sectorId. The prop docs on this component say it outright: the
                plain `sectorId` is the VIEWED sector and is unused here, while writeSectorId is
                "THE SECTOR THAT WRITES ACTUALLY GO TO, WHICH IS NOT ALWAYS THE ONE BEING VIEWED".
                Reconciliation is a pair-taking write (sector + category path), so it must resolve
                the same sector its neighbouring Refresh button does — otherwise the two buttons,
                side by side, would target DIFFERENT ROWS. The wrong-tree class again, and the answer
                was written on the prop.
                ⚠️ onReload, NOT a refresh handler: it is documented as "A RELOAD THAT WRITES
                NOTHING", existing as its own prop precisely because every other handler here is a
                mutation with a reassuring name. A success path must not be able to reach one. */}
            {writeSectorId !== undefined && (
              <PersonaReconciliationPanel
                sectorId={writeSectorId}
                categoryPath={category.categoryPath}
                researchError={category.researchError || category.freshness.error || lastRefreshError}
                /* ⛔ THE REACHABILITY FIX. A preservation refusal restores the row and clears
                   research_error, so the first three terms are all null on exactly the category
                   that was just refused. The stored personas are what survive that, and their
                   weights are the real precondition. lastRefreshError is added too so the
                   in-session refusal still selects the right copy before any reload. */
                personas={category.aspects?.personas?.data}
                onApplied={() => { void onReload(category.categoryPath); }}
              />
            )}
            {/* Some refresh failures throw before the backend persists ANYTHING to
                research_error (pre-flight checks, e.g. a missing canonical_benefits
                dependency) — for those, this session's toast is the only place the message
                ever existed, and it auto-dismisses after a few seconds. lastRefreshError keeps
                it visible here until a different category is opened or a new refresh runs. Only
                shown when the backend-persisted field above didn't already cover it. */}
            {!category.researchError && !category.freshness.error && lastRefreshError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Last Refresh Failed (this session):</strong> {lastRefreshError}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </ScrollArea>
      </DialogContent>

      {/* ⛔ ONE DIALOG FOR BOTH DESTRUCTIVE CONTROLS ON THIS PANEL — Re-Research All and every
          per-aspect rebuild. Mounted once so the two cannot drift apart in what they warn about. */}
      <RefreshConfirmDialog
        open={pendingRefresh !== null}
        onOpenChange={(o) => { if (!o) setPendingRefresh(null); }}
        scope={pendingRefresh ?? 'all'}
        categoryPath={category.categoryPath}
        sectorId={writeSectorId}
        /* ⛔ NULL, NOT 0, WHEN THE LIST IS NOT THERE — "no personas" and "not loaded" are the same
           empty array, and claiming there is nothing to lose is worst when we do not know. */
        personaCount={Array.isArray(category.aspects.personas?.data)
          ? category.aspects.personas.data.length
          : null}
        loading={refreshing || refreshingAspect !== null}
        onConfirm={() => {
          const scope = pendingRefresh;
          setPendingRefresh(null);
          if (scope === 'all') handleRefreshAll();
          // ⛔ Personas take the chain on BOTH confirm paths. Routing only the weighted-personas
          // dialog would leave the unweighted path calling a refresh that cannot re-weight, and
          // the category would come back on the sector fallback with nothing saying so.
          else if (scope === 'personas') onRegeneratePersonas(category.categoryPath);
          else if (scope) handleRefreshAspect(scope);
        }}
      />
    </Dialog>
  );
}

/**
 * task-n1e23erl53 — what a Home-page request resolves to. The request is a (sector, FULL category path) pair — the
 * key the AEO hard gate and the generator read — and the AUTHORITATIVE place to answer it is the sector's research
 * list (GET /category-characteristics/:sectorId, `WHERE sector_id = $1`, sector-wide, never narrowed by site).
 * ⛔ NOT the catalogue (available-categories): that list is built from the SELECTED SITE's current feed items, drops
 * groups under three products, and keys by stored feed category, so a research row can exist with no catalogue
 * line at all (a historical block, a sector-wide preflight gap, a leaf the products still store under the parent).
 * Rounds 4–6 resolved through the catalogue and read its absence as "no research row yet" (codex-reviewer-2,
 * blocking at 043847ce1); a catalogue absence is not a research absence, so the catalogue is no longer consulted.
 * ⚠️ This is a lookup of a REQUEST by its own identity, not of a catalogue line by path — the click-through pin
 * (CategoryResearch.catalogDiagnosisClickThrough.test.ts) bans path-keyed lookups for catalogue lines because the
 * catalogue returns one line per research record and a path is ambiguous THERE. Here equality with the FULL path
 * excludes the bare-leaf convention by construction: a leaf row's `categoryPath` is the leaf name, never the path.
 *   - the FULL-PATH record is the sector's row whose `categoryPath` equals the requested path; it opens, and a leaf
 *     duplicate (a row keyed by the path's last segment) is reported on the banner — per the 2026-09-07 ruling it is
 *     deleted once the full path is researched;
 *   - a leaf record alone is 'leaf_only': the full path has no row, so the category is still blocked at the gate;
 *   - several leaf candidates with NO full-path row is 'ambiguous': shown, no dialog opened, nothing pre-filled;
 *   - no row under either key is 'none': the caller pre-fills the full path.
 * A research list that FAILED to load never reaches this helper: the caller renders 'unknown'.
 */
export type RequestedPathResolution =
  | { kind: 'full_path'; row: CategoryCharacteristics; leafDuplicate: { leafId: string; leafKey: string } | null }
  | { kind: 'leaf_only'; leafId: string; leafKey: string }
  | { kind: 'ambiguous'; candidates: Array<{ id: string; key: string }> }
  | { kind: 'none' };

export const leafOf = (path: string): string => { const parts = path.split('>').map((x) => x.trim()).filter(Boolean); return parts[parts.length - 1] || path; };

export function resolveRequestedPath(
  requestedSectorId: number | null,
  requestedPath: string,
  research: ReadonlyArray<CategoryCharacteristics>,
): RequestedPathResolution {
  const leafKey = leafOf(requestedPath);
  const fullPath: CategoryCharacteristics[] = [];
  const leaf: Array<{ id: string; key: string }> = [];
  for (const row of research) {
    if (requestedSectorId != null && row.sectorId != null && Number(row.sectorId) !== requestedSectorId) continue;
    if (row.categoryPath === requestedPath) fullPath.push(row);
    else if (leafKey !== requestedPath && row.categoryPath === leafKey) leaf.push({ id: row.id, key: row.categoryPath });
  }
  if (fullPath.length > 0) return { kind: 'full_path', row: fullPath[0], leafDuplicate: leaf.length > 0 ? { leafId: leaf[0].id, leafKey: leaf[0].key } : null };
  if (leaf.length > 1) return { kind: 'ambiguous', candidates: leaf };
  if (leaf.length === 1) return { kind: 'leaf_only', leafId: leaf[0].id, leafKey: leaf[0].key };
  return { kind: 'none' };
}

export default function CategoryResearch() {
  const { selectedSite, sites, setSelectedSite } = useSite();
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<CategoryCharacteristics[]>([]);
  const [rawAvailableCategories, setAvailableCategories] = useState<AvailableCategory[]>([]);
  const availableCategories = useMemo(() => catalogResearchRows(rawAvailableCategories), [rawAvailableCategories]);
  /**
   * ⛔ Has the AUTHORITATIVE research list been read for the sector currently shown? Starts false,
   * goes false again at the start of every load, and true only when that fetch resolves. An empty
   * `categories` array means "this sector has no research" ONLY when this is true — otherwise it
   * means "not read yet", and those are not the same answer. See researchExistsFor.
   */
  const [categoriesKnown, setCategoriesKnown] = useState(false);
  /**
   * Ruling (v): the catalogue roots of this site — the ONLY separator-free names a write may carry.
   * ⛔ Loaded from the UNFILTERED catalogue (/sites/:id/product-categories), not from /available-categories, whose
   *    HAVING COUNT(DISTINCT p.id) >= 3 drops a legitimate root with one or two products and would have made this
   *    page refuse a research the other Category Research page accepts (codex #6175 round 1, minor, both legs).
   * null = not loaded (a bare name is refused and told so); an empty Set = loaded, no roots.
   */
  const [catalogueRoots, setCatalogueRoots] = useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    const siteId = selectedSite?.id;
    setCatalogueRoots(null);
    if (!siteId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch(`${API_URL}/sites/${siteId}/product-categories`);
        const data = await response.json().catch(() => null);
        if (cancelled) return;
        if (!response.ok || !Array.isArray(data?.categories)) {
          console.error('[CategoryResearch] product categories unavailable; bare category names will be refused:', response.status, data?.error);
          return;
        }
        setCatalogueRoots(catalogueRootsFrom(data.categories.map((c: { name?: string }) => c.name)));
      } catch (err: any) {
        if (!cancelled) console.error('[CategoryResearch] product categories request failed; bare category names will be refused:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSite?.id]);
  const [summary, setSummary] = useState<SummaryStats | null>(null);
  // PR #6089 round 9 (codex-reviewer, minor at 888d74bbb): a failed summary load is SHOWN, not only logged, and the
  // previous sector's numbers never survive under the new selection — the summary is cleared at the start of every load.
  const [summaryLoadFailed, setSummaryLoadFailed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoryCharacteristics | null>(null);
  // Some refresh failures throw before the backend ever persists research_error on the row
  // (e.g. "no canonical_benefits for (...) — run the Phase 1a Step 5 backfill first" throws
  // during pre-flight, ahead of the row write _markFailed would otherwise reach). For those,
  // the toast is the ONLY place the message exists — and it auto-dismisses. This keeps the
  // last refresh's raw error visible in the dialog for this session, independent of whether
  // the backend persisted anything, so it isn't lost the moment the toast closes.
  const [lastRefreshError, setLastRefreshError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'catalog' | 'gpt-browser' | 'researched'>('catalog');
  const [triggeringResearch, setTriggeringResearch] = useState<string | null>(null);
  /**
   * ⛔ TWO MORE DESTRUCTIVE PATHS THAT BYPASSED THE WARNING (codex, both legs).
   * - The catalog table's Refresh button on an ALREADY-RESEARCHED row hits the same full rebuild
   *   endpoint as Re-Research All, overwriting stored characteristics.
   * - Batch "Start Research" includes researched categories via Select All and its default steps
   *   contain the characteristics rebuild, so it could overwrite MANY categories at once.
   * Both now confirm first. A first-time research on a never-researched row is NOT destructive and
   * still runs straight through — warning there would train the operator to click past it.
   */
  /**
   * ⛔ THE PENDING ROW CARRIES ITS OWN RESOLVED SECTOR. Holding only the path forced the dialog to
   * read the sector from somewhere else — and the only thing available was the DETAIL PANEL's
   * sector, which belongs to a different category. Resolve once, show that, write with that.
   */
  const [pendingTrigger, setPendingTrigger] = useState<{ path: string; sectorId: number } | null>(null);
  /** Set while the row's sector is being resolved, before the confirmation can honestly open. */
  const [resolvingTriggerPath, setResolvingTriggerPath] = useState<string | null>(null);
  /**
   * ⛔⛔ THE SEVENTH CALLER OF THE REBUILD ENDPOINT, AND THE ONE WHOSE LABEL LIES.
   * The "New category name" box POSTs the SAME full-rebuild /refresh endpoint as Re-Research All.
   * Type the name of a category that already has research and it is silently replaced — under a
   * Plus icon, the word "New", and an Enter key that fires it without a click.
   * 🔑 FOUND BY ENUMERATING CALLERS OF THE ENDPOINT INSTEAD OF BUTTONS ON THE SCREEN I WAS EDITING.
   * Six rounds of careful work on this exact defect never reached it, because nothing about this
   * control looks like a rebuild.
   */
  const [pendingNewCategory, setPendingNewCategory] = useState<string | null>(null);
  const [pendingBatchPaths, setPendingBatchPaths] = useState<string[] | null>(null);
  const [pendingBatchSubmission, setPendingBatchSubmission] = useState<CatalogSubmission | null>(null);
  const [preparingBatchResearch, setPreparingBatchResearch] = useState(false);
  const preparingBatchRef = useRef(false);
  const batchPreparationKeyRef = useRef('');
  const confirmedPreparationKeyRef = useRef('');
  /**
   * Operator-facing names for the batch steps that overwrite. No step ids reach the dialog.
   * ⚠️ 'intelligence' is described as replacing because its overwrite behaviour is UNVERIFIED — see
   * OVERWRITING_STEPS at the Start Research button for what was checked against the backend and
   * what was not. Step-aware copy must not become step-optimistic.
   */
  const BATCH_STEP_LABELS: Record<string, string> = {
    characteristics: 'basic characteristics — product types, brand tiers, use cases and new buyer personas with new benefit weights',
    perceived_benefits: 'perceived benefits, including any validation an operator has already done',
    extraction_hints: 'extraction hints, which also RE-ENRICHES the products in the category',
    intelligence: 'attribute intelligence',
  };

  /** Human labels for the selected steps that overwrite — what the batch dialog names. */
  const [pendingBatchStepLabels, setPendingBatchStepLabels] = useState<string[]>([]);

  // Independent sector selection (removes site/product dependency)
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
  const [newCategoryName, setNewCategoryName] = useState<string>('');

  // Resolved sector for write operations (ensures research goes to correct sector)
  const [resolvedCategorySectorId, setResolvedCategorySectorId] = useState<number | null>(null);
  /**
   * ⛔⛔ WHICH CATEGORY THE IN-FLIGHT SECTOR RESOLUTION IS FOR. This resolver left the PREVIOUS
   * category's sector in state while the new one resolved, and applied its answer unconditionally.
   * ⇒ getWriteSectorId() therefore returned the OLD category's sector while the dialog showed the
   * NEW one, so a fast click on the repair button was a paid write to (wrong sector, right path).
   * (codex leg-1 blocking at 2c93cb535.)
   */
  const sectorResolveKeyRef = useRef<string>('');
  /**
   * ⛔⛔ WHICH loadData() CALL IS ALLOWED TO COMMIT. Same class of defect as the ref above, one
   * layer up: loadData() closes over `sectorId`, so switching sector creates a NEW closure while
   * the OLD sector's fetch is still in flight, and whichever resolves LAST used to win. Sector A
   * resolving after sector B wrote A's rows into `categories` and set `categoriesKnown` — so the
   * bulk attribute-intelligence panel, whose scope key compares identifiers only, would happily
   * spend on research for A's category paths while the whole page said B.
   * ⇒ Every fetching call takes the next generation; only the call still holding the latest one
   * commits. Discard-on-resolve, not cancellation — nothing here needs the request stopped, only
   * its answer ignored. Declared at component level because it must OUTLIVE each loadData closure.
   * (codex-reviewer and codex-reviewer-2, independently, blocking at 285111725e.)
   */
  const loadGenerationRef = useRef(0);
  const [sectorMismatchWarning, setSectorMismatchWarning] = useState<string | null>(null);
  const [technicalResearch, setTechnicalResearch] = useState<TechnicalResearchStatus | null>(null);

  // Research dimension metadata (from backend)
  const [dimensionNames, setDimensionNames] = useState<string[]>([]);
  const [dimensionShortNames, setDimensionShortNames] = useState<string[]>([]);
  const [dimensionToStep, setDimensionToStep] = useState<string[]>([]);
  const [enrichmentRelevantDimensions, setEnrichmentRelevantDimensions] = useState<number[]>([]);

  // GPT Taxonomy browser state
  // GPT Browser is now shown in its own tab (removed showGptBrowser state)
  const [gptTaxonomy, setGptTaxonomy] = useState<any[]>([]);
  const [gptBreadcrumb, setGptBreadcrumb] = useState<any[]>([]);
  const [gptLoading, setGptLoading] = useState(false);

  // Canonical attributes dialog state
  const [canonicalDialogOpen, setCanonicalDialogOpen] = useState(false);
  const [canonicalAttributes, setCanonicalAttributes] = useState<CanonicalAttribute[]>([]);
  const [canonicalUnavailableReason, setCanonicalUnavailableReason] = useState<string | null>(null);
  const [canonicalLoading, setCanonicalLoading] = useState(false);

  // Attribute intelligence dialog state
  const [intelligenceDialogOpen, setIntelligenceDialogOpen] = useState(false);
  const [attributeIntelligence, setAttributeIntelligence] = useState<AttributeIntelligenceEntry[]>([]);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);

  // Extraction hints dialog state
  const [extractionHintsDialogOpen, setExtractionHintsDialogOpen] = useState(false);
  const [extractionHints, setExtractionHints] = useState<ExtractionHint[]>([]);
  const [extractionHintsLoading, setExtractionHintsLoading] = useState(false);
  const [editingIntelligence, setEditingIntelligence] = useState<AttributeIntelligenceEntry | null>(null);
  const [savingIntelligence, setSavingIntelligence] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<CanonicalAttribute | null>(null);
  const [savingAttribute, setSavingAttribute] = useState(false);
  const [deletingAttributeId, setDeletingAttributeId] = useState<string | null>(null);
  const [canonicalSearch, setCanonicalSearch] = useState('');
  const [showNewAttributeForm, setShowNewAttributeForm] = useState(false);
  const [newAttribute, setNewAttribute] = useState<Partial<CanonicalAttribute>>({
    data_type: 'string',
    enum_values: [],
  });
  const [newEnumValue, setNewEnumValue] = useState('');

  // task-po9ac21icv fix (3), admin half. The POST advisory answers a near-duplicate name with a
  // 409 CANONICAL_ATTRIBUTE_NEAR_MISS that is cleared by resending `confirm_near_miss: true`.
  // Without a UI that can resend it, the "advisory" is a HARD BLOCK in the only place it fires —
  // and the predicate is deliberately broad (~90% of the loose class are legitimate distinct
  // facets: material + material_density, size + size_unit), so blocking them is the failure mode,
  // not the feature. This state holds the near misses the server named so the operator can
  // adjudicate them; the retry is an explicit click and never automatic.
  const [nearMissConfirm, setNearMissConfirm] = useState<{
    attributeName: string;
    productCategory: string;
    nearMisses: CanonicalNearMiss[];
  } | null>(null);

  // AI suggestion state
  const [aiSuggestions, setAiSuggestions] = useState<{
    unit?: string | null;
    typical_min?: number | null;
    typical_max?: number | null;
    description?: string | null;
    enum_values?: string[] | null;
    reasoning?: string | null;
    sample_values_found?: string[];
    based_on_research?: boolean;
    research_title?: string | null;
  } | null>(null);
  const [loadingAiSuggestions, setLoadingAiSuggestions] = useState(false);

  // Batch research state
  const [batchResearchDialogOpen, setBatchResearchDialogOpen] = useState(false);
  const [batchResearchJob, setBatchResearchJob] = useState<{
    id: string;
    sectorId: number;
    siteName: string;
    siteId: string;
    targets: Record<string, string>;
    runner?: string;
    refused?: Array<{categoryPath:string;error:string}> | null;
    targetMode?: string;
    handoff?: ResearchHandoff | null;
    continuationRepair?: {policy:string;continuationId:string}|null;
    status: string;
    totalCategories: number;
    completedCategories: number;
    failedCategories: number;
    currentStep: string | null;
    progressPercent: number;
    progressDetails: Record<string, { steps: Record<string, string>; error?: string; stepResults?: Record<string, { status: string; error?: string; target?: {sectorId:number;categoryPath:string}; sectorPersonaSync?: {status:string;error?:string}; reviewItems?: Array<{attribute:string;aspect:string;reason:string}>; blockedBy?:Array<{step:string;status:string;error?:string}>; gaps?:ResearchGapReceipt[]; delivery?:{status:string;aspects:Record<string,{status:string;published?:boolean;error?:string;coverage?:{gaps?:ResearchGapReceipt[]}}>} | {contractVersion:string;requestedBenefitIds:string[];acceptedBenefitIds:string[];gaps:ResearchGapReceipt[]} }> }>;
    lastError: string | null;
  } | null>(null);
  const [knownResearchJobs, setKnownResearchJobs] = useState<Array<NonNullable<typeof batchResearchJob>>>([]);
  useEffect(()=>{if(batchResearchJob)setKnownResearchJobs(previous=>[...previous.filter(job=>job.id!==batchResearchJob.id),batchResearchJob]);},[batchResearchJob]);
  const [batchResearchPolling, setBatchResearchPolling] = useState(false);
  const [selectedBatchCategories, setSelectedBatchCategories] = useState<string[]>([]);
  const [batchResearchSteps, setBatchResearchSteps] = useState<string[]>([
    'characteristics', 'perceived_benefits', 'canonical', 'intelligence', 'extraction_hints'
  ]);
  const [startingBatchResearch, setStartingBatchResearch] = useState(false);
  const batchResearchOnly = true;
  const submissionKey = `category-research-request:${user?.id || 'anonymous'}`;
  const [unacknowledgedSubmission, setUnacknowledgedSubmission] = useState<CatalogSubmission | null>(null);
  const [researchRecoveryBlocked, setResearchRecoveryBlocked] = useState(false);
  const [researchRestoreRetry, setResearchRestoreRetry] = useState(0);
  const [researchHistoryWarning, setResearchHistoryWarning] = useState<string | null>(null);
  const [batchResearchError, setBatchResearchError] = useState<string | null>(null);
  const startingBatchRef = useRef(false);
  const batchJobIdRef = useRef<string | null>(null);
  const batchReceiptVersionRef = useRef(0);
  const batchJobRetired = researchJobRetired(batchResearchJob);
  const batchJobActive = !!batchResearchJob && !batchJobRetired && ['pending', 'running', 'paused'].includes(batchResearchJob.status);
  const researchJobs = [...knownResearchJobs.filter(job=>job.id!==batchResearchJob?.id),...(batchResearchJob?[batchResearchJob]:[])];
  // Known exact catalog owners fence their own paths. Unscoped legacy receipts
  // and an admission whose outcome is unknown retain the existing global hold.
  const batchBlocksAll = researchJobs.some(job=>job.siteId===selectedSite?.id && researchJobBlocks(job) && !researchJobHasCatalogScope(job) && !researchJobRetired(job));
  const batchSelectionLocked = batchJobActive && batchBlocksAll;
  const pathHasResearchOwner = (path:string, targetSector=sectorId) => researchJobsOverlap(researchJobs,Number(targetSector),[path]);

  // Knowledge chat state
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatParsedIntent, setChatParsedIntent] = useState<{
    operation: string;
    conceptType: string;
    conceptName: string;
    context: string;
    confirmationMessage: string;
  } | null>(null);
  const [chatExecuting, setChatExecuting] = useState(false);
  const [chatHistory, setChatHistory] = useState<Array<{
    role: 'user' | 'assistant';
    content: string;
    result?: ChatOperationOutcome;
  }>>([]);

  const isSuperAdmin = user?.role === 'super_admin';

  // Get sector ID - allow manual selection to override site's sector
  const siteSectorId = selectedSite?.sector_id || selectedSite?.sector_ids?.[0];
  const sectorId = selectedSectorId || siteSectorId;
  // A late resolver response cannot confirm a selection the operator has left.
  batchPreparationKeyRef.current = JSON.stringify([selectedSite?.id, sectorId, selectedSite?.sector_ids,
    batchResearchDialogOpen, selectedBatchCategories, batchResearchSteps]);
  useEffect(() => {
    setSelectedBatchCategories([]);
    setPendingBatchPaths(null);
    setPendingBatchSubmission(null);
  }, [selectedSite?.id, sectorId]);

  // task-n1e23erl53 (PR #6089 round 2, both legs): the admin Home page links here with ?sectorId=&categoryPath= for a
  // category that lacks research. The page SELECTS them: the sector from the URL wins over the site context, and once
  // the catalogue is loaded the requested path opens its research row if one exists or is pre-filled into the
  // "research a new category" input with a banner if it does not. Read once from the URL; never re-applied after the
  // operator changes the selection by hand.
  // Read from window.location (the app mounts under a BrowserRouter, App.tsx:830), not through a router hook: this
  // page is also rendered without a Router by its own test suites, and the request is read ONCE at mount.
  const [requested] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const raw = params.get('sectorId'); const n = raw ? parseInt(raw, 10) : NaN;
    return { sectorId: Number.isInteger(n) ? n : null, categoryPath: (params.get('categoryPath') || '').trim() || null, siteId: (params.get('siteId') || '').trim() || null };
  });
  const requestedSectorId = requested.sectorId;
  const requestedCategoryPath = requested.categoryPath;
  // PR #6089 round 5 (codex-reviewer-2, minor): the ORIGINATING site rides on the link. In "All my sites" the Home page
  // lists site B's categories while site A stays selected; every scoped read on this page goes through the global
  // site selector (CLAUDE.md: dashboards read `SiteContext`), so the destination SWITCHES the selector to the
  // originating site and the decision below waits until the selector agrees. A site that is not among the user's
  // sites cannot be switched to and the answer is 'unknown', never "no research row".
  const requestedSiteId = requested.siteId;
  const [requestedPathState, setRequestedPathState] = useState<'pending' | 'opened' | 'prefilled' | 'ambiguous' | 'leaf_only' | 'unknown' | null>(requestedCategoryPath ? 'pending' : null);
  const [requestedPathDetail, setRequestedPathDetail] = useState<{ reason?: string; leafId?: string; leafKey?: string | null; candidates?: Array<{ id: string; key: string | null }> } | null>(null);
  // PR #6089 round 2b (codex-reviewer, minor): the decision keys on LOAD STATE, never on list contents — the research
  // list (`categoriesKnown`) must have loaded OK for the current sector (empty is a valid answer); a list that FAILED to
  // load is 'unknown': the page must not claim "no research row" on a list it never received. The catalogue is not an
  // input to the decision at all (see resolveRequestedPath), so its load state does not gate it.
  const [researchListLoadFailed, setResearchListLoadFailed] = useState(false);
  useEffect(() => {
    if (requestedSectorId != null) setSelectedSectorId(requestedSectorId);
  }, [requestedSectorId]);
  // The originating site is applied ONCE (both legs, minor at 043847ce1): after it has been consumed the operator's own
  // selector changes stand — an effect that re-aligned on every mismatch made the header selector unusable here.
  const requestedSite = requestedSiteId ? (sites || []).find((st) => st && st.id === requestedSiteId) || null : null;
  const [siteSwitchDone, setSiteSwitchDone] = useState(!requestedSiteId);
  useEffect(() => {
    if (siteSwitchDone || !requestedSiteId) return;
    if (!requestedSite) return; // the sites list has not named it yet, or it is not one of the user's sites (decided below)
    if (selectedSite?.id !== requestedSiteId) setSelectedSite(requestedSite);
    setSiteSwitchDone(true);
  }, [siteSwitchDone, requestedSiteId, requestedSite, selectedSite?.id, setSelectedSite]);
  useEffect(() => {
    if (requestedPathState !== 'pending' || !requestedCategoryPath) return;
    if (!siteSwitchDone) {
      if ((sites || []).length > 0 && !requestedSite) {
        setRequestedPathDetail({ reason: `the originating site ${requestedSiteId} is not among your sites` });
        setRequestedPathState('unknown');
      }
      return; // the selector is switching to the originating site; decide once its research list has landed
    }
    if (requestedSiteId && selectedSite?.id !== requestedSiteId) return; // switched, but the site's render has not landed yet
    if (researchListLoadFailed) { setRequestedPathDetail({ reason: 'the research list did not load' }); setRequestedPathState('unknown'); return; }
    if (!categoriesKnown) return;
    // Resolve the request by its own identity — (sector, FULL path) against the sector's research list — never through
    // the catalogue and never by line order (see resolveRequestedPath). Runs from CURRENT state, gated on the
    // sector-scoped load flag, once; it is not a re-selection from a captured closure.
    const r = resolveRequestedPath(requestedSectorId ?? sectorId ?? null, requestedCategoryPath, categories);
    if (r.kind === 'full_path') { setRequestedPathDetail(r.leafDuplicate); setSelectedCategory(r.row); setRequestedPathState('opened'); return; }
    if (r.kind === 'ambiguous') { setRequestedPathDetail({ candidates: r.candidates }); setRequestedPathState('ambiguous'); return; }
    if (r.kind === 'leaf_only') { setRequestedPathDetail({ leafId: r.leafId, leafKey: r.leafKey }); setNewCategoryName(requestedCategoryPath); setRequestedPathState('leaf_only'); return; }
    setNewCategoryName(requestedCategoryPath);
    setRequestedPathState('prefilled');
  }, [requestedPathState, requestedCategoryPath, requestedSectorId, sectorId, requestedSiteId, siteSwitchDone, requestedSite, sites, selectedSite?.id, categories, categoriesKnown, researchListLoadFailed]);

  // Redirect if not super admin
  useEffect(() => {
    if (user && !isSuperAdmin) {
      toast({
        title: 'Access Denied',
        description: 'This page is only available to Super Admins',
        variant: 'destructive',
      });
    }
  }, [user, isSuperAdmin, toast]);

  // Load all sectors for the dropdown (allows selecting any sector)
  useEffect(() => {
    const loadSectors = async () => {
      try {
        const res = await authFetch(`${API_URL}/sectors`);
        if (res.ok) {
          const data = await res.json();
          setSectors(Array.isArray(data) ? data : (Array.isArray(data.sectors) ? data.sectors : []));
        }
      } catch (error) {
        console.error('Error loading sectors:', error);
      }
    };
    loadSectors();
  }, []);

  const loadData = useCallback(async () => {
    // ⛔⛔ MOVING TO A *NO-SECTOR* STATE IS A SUPERSESSION TOO — IT JUST HAS NO FETCH OF ITS OWN.
    // Both codex reviews found the same residue after the generation guard below landed: this
    // early return claimed no generation, so a sector-A load still in flight stayed "current" and
    // committed setCategories(A) / setCategoriesKnown(true) into a page that had moved off sector A
    // entirely (operator picks a site with no sector assigned). Nothing then clears those rows, so
    // the moment a sector IS picked again the page renders sector A's categories under the NEW
    // sector's label — with `categoriesKnown` asserting they belong to it.
    // ⇒ Take a generation here as well. There is no `myGeneration` to capture because this call
    // commits nothing; the bump exists solely to make every OLDER closure's isCurrent() false.
    // ⛔ AND NO `setLoading(false)` HERE — DELIBERATELY, VERIFIED, NOT OVERLOOKED. The bump does
    // strand the predecessor's gated `setLoading(false)`, which is the objection this comment used
    // to raise against bumping at all. It is not reachable as a stuck spinner: the page's own
    // `if (!sectorId)` RENDER guard replaces the whole tab UI with the sector picker, so nothing
    // reads `loading` while there is no sector — and `sectorId` cannot become truthy again without
    // loadData() re-running (it is a dependency of this useCallback and an effect re-invokes it),
    // which sets `loading` true and clears it in its own, current, finally. Clearing it here would
    // only flash the empty state where the skeletons belong. Pinned by the "does not strand the
    // spinner" test in CategoryResearch.loadDataRace.test.tsx.
    if (!sectorId) {
      loadGenerationRef.current += 1;
      // The rows on screen — if any survive from the sector we just left — are not this state's
      // rows. Unknown must be able to BE unknown; see the flag's own comment below.
      setCategoriesKnown(false);
      return;
    }

    let freshCategories: CategoryCharacteristics[] | null = null;
    // ⛔ LATEST LOAD WINS — AND THE ONLY THING THAT KNOWS WHICH ONE IS LATEST LIVES OUTSIDE THE
    // CLOSURE. loadData is a useCallback over `sectorId`/`selectedSite?.id`, so switching sector
    // BUILDS A NEW FUNCTION and the effect below re-invokes it — but the PREVIOUS sector's
    // Promise.all is still in flight in the OLD closure, which holds the OLD sectorId and has no
    // way to ask what the page is showing now. If sector A's fetch resolves after sector B's, that
    // old closure runs setCategories(A) / setCategoriesKnown(true) unconditionally, and the page —
    // including the bulk research panel's own scope-key guard — then reads "these are B's rows".
    // The panel would fire PAID research against sector A's category paths while the screen says B.
    // A ref, not state: it must survive the closure being recreated, and it must be readable
    // synchronously at commit time rather than one render later.
    // ⚠️ SUPERSEDED — this used to read "INCREMENTED AFTER the `!sectorId` early return,
    // deliberately … only a call that will actually fetch takes a generation." That was the wrong
    // rule and it left the sector→no-sector transition invalidating nothing; the early return now
    // takes a generation too, and the reason its stranded predecessor cannot leave a spinner
    // running is written at that branch, not asserted here.
    const myGeneration = ++loadGenerationRef.current;
    const isCurrent = () => loadGenerationRef.current === myGeneration;
    setLoading(true);
    // ⛔ UNKNOWN UNTIL THIS FETCH RESOLVES. While a load is in flight the rows on screen may belong
    // to another sector or predate a research that just completed, and every destructive guard
    // below reads this. Unknown must confirm, so it must be able to BE unknown.
    // ⚠️ NOT generation-gated, and that is the point: a NEW call marking the rows unknown
    // IMMEDIATELY is the safety behaviour everything else depends on. Only the COMMIT is gated.
    setCategoriesKnown(false);
    // ⛔⛔ AND THE CATALOGUE ITSELF IS CLEARED, NOT JUST MARKED UNKNOWN. `categoriesKnown` guards the
    // destructive controls, but it does NOT guard what the "Your Catalog" tab RENDERS or what its
    // bulk-selection actions operate on — so a failed or refused reload left site A's categories on
    // screen, selectable, under site B's name. Cleared here for the same reason
    // `setCategoriesKnown(false)` is: a new request must be able to say "I do not know yet".
    // ⚠️ SAME NON-GATING AS THE LINE ABOVE, deliberately: the clear is immediate, only the COMMIT is
    // generation-gated. A superseded load must not repopulate, but it must still be able to blank.
    setAvailableCategories([]);
    setSummary(null); // the previous sector's numbers must not sit under the new selection while (or after) its load fails
    setSummaryLoadFailed(false);
    setResearchListLoadFailed(false);
    try {
      // Load categories, summary, and available categories in parallel (sector-level data)
      const siteIdParam = selectedSite?.id ? `?siteId=${selectedSite.id}` : '';
      // PR #6089 round 8 (codex-reviewer-2, minor at 8a1ad0e03): EACH RESPONSE IS SETTLED ON ITS OWN. A rejected
      // catalogue or summary request (transport) or a malformed body used to throw out of the shared Promise.all,
      // discard a research list that had loaded fine, and mark it failed for the requested-path decision. `settle`
      // turns a rejection or bad JSON into THAT response being not-ok, detail kept for the log; the three still land
      // together (one generation check) and each branch still keys off its own `.ok`.
      type Settled = { ok: boolean; status: number; data: any; detail: string };
      const settle = async (p: Promise<Response>, what: string): Promise<Settled> => {
        let res: Response;
        try { res = await p; } catch (e) { return { ok: false, status: 0, data: null, detail: `${what}: ${(e as Error)?.message || String(e)}` }; }
        if (!res.ok) return { ok: false, status: res.status, data: null, detail: `${what}: ${await res.text().catch(() => '')}` };
        try { return { ok: true, status: res.status, data: await res.json(), detail: '' }; }
        catch (e) { return { ok: false, status: res.status, data: null, detail: `${what}: malformed JSON (${(e as Error)?.message || String(e)})` }; }
      };
      const [categoriesRes, summaryRes, availableRes] = await Promise.all([
        settle(authFetch(`${API_URL}/category-characteristics/${sectorId}${siteIdParam}`), 'research list'),
        settle(authFetch(`${API_URL}/category-characteristics/${sectorId}/summary`), 'summary'),
        // ⇒ SITE-SCOPED, LIKE ITS SIBLING ONE LINE ABOVE. Without siteId this endpoint answers a
        // SECTOR question and filters by the sector's taxonomy root, which hid BikeInn's
        // second-largest category (Shoes, 6,744 products) from a tab labelled "Your Catalog" and
        // showed 17 where the true figure is 19.
        settle(authFetch(`${API_URL}/category-characteristics/${sectorId}/available-categories${siteIdParam}`), 'available categories'),
      ]);

      // ⛔ PARSED (inside settle), COMMIT AS ONE UNIT. The three responses are one logical answer about one
      // sector, so they get ONE generation check — three separate checks could disagree if a newer
      // load landed between them and leave the page holding B's categories beside A's summary.
      const categoriesData = categoriesRes.ok ? categoriesRes.data : null;
      const summaryData = summaryRes.ok ? summaryRes.data : null;
      const availableData = availableRes.ok ? availableRes.data : null;

      // ⛔ ASSIGNED UNCONDITIONALLY, OUTSIDE THE GATE. The return value is this call's OWN answer
      // to its OWN caller (reloadAfterRepair finds the row it just repaired in it) and must not be
      // silently emptied because an unrelated reload raced ahead elsewhere on the page.
      if (categoriesRes.ok) freshCategories = categoriesData.data || [];

      // ⚠️ EACH BRANCH STILL KEYS OFF ITS OWN `res.ok`, NOT off the parsed body being truthy — a
      // body of literal `null` is `ok` and used to commit its `|| null` / `|| []` default. Keeping
      // `.ok` as the condition means the ONLY behavioural change here is the generation gate.
      if (isCurrent()) {
        if (!categoriesRes.ok) {
          // PR #6089 round 10 (both legs, minor at 24ec2dbfe): surfaced UNCONDITIONALLY, not only through a Home deep
          // link. The per-response settle isolated this failure from the shared catch, and the shared catch was the
          // toast; without one, an ordinary open showed "No category characteristics found" after a failed request and
          // a sector switch kept the previous sector's rows. The rows are cleared (a failed list is not an empty list,
          // and it is not the previous sector's list either), the page renders an explicit unavailable state in place
          // of the table, and the toast is raised. `categoriesKnown` stays false: nothing destructive can act.
          setResearchListLoadFailed(true); // the requested-path decision reads this, not the empty list
          setCategories([]);
          console.error('research list request failed:', categoriesRes.status, categoriesRes.detail);
          toast({ title: 'Could not load the research list', description: categoriesRes.status ? `The server returned ${categoriesRes.status}.` : categoriesRes.detail, variant: 'destructive' });
        }
        if (categoriesRes.ok) {
          setCategories(categoriesData.data || []);
          setCategoriesKnown(true);
          if (categoriesData.dimensionNames) setDimensionNames(categoriesData.dimensionNames);
          if (categoriesData.dimensionShortNames) setDimensionShortNames(categoriesData.dimensionShortNames);
          if (categoriesData.dimensionToStep) setDimensionToStep(categoriesData.dimensionToStep);
          if (categoriesData.enrichmentRelevantDimensions) setEnrichmentRelevantDimensions(categoriesData.enrichmentRelevantDimensions);
        }

        if (summaryRes.ok) {
          setSummary(summaryData.data || null);
        } else {
          // Surfaced on its own (it fails ONLY the summary): logged, an explicit unavailable state on the page, and the
          // toast the shared catch used to raise before the per-response settle isolated this failure.
          console.error('summary request failed:', summaryRes.status, summaryRes.detail);
          setSummaryLoadFailed(true);
          toast({ title: 'Could not load the summary', description: summaryRes.status ? `The server returned ${summaryRes.status}.` : summaryRes.detail, variant: 'destructive' });
        }

        if (availableRes.ok) {
          setAvailableCategories(availableData.data || []);
        } else {
          // ⛔⛔ NOT SWALLOWED, AND THE STALE LIST IS ALREADY GONE (cleared at the top of this call).
          // This branch used to do nothing at all, so a failed available-categories left the
          // PREVIOUS SITE'S CATALOGUE rendered under the newly selected site — the same cross-site
          // misrepresentation this PR exists to remove, arriving through the failure path.
          // ⚠️ AND THIS PR MADE IT REACHABLE: the endpoint now legitimately returns 400/403 for a
          // malformed or unauthorized site, so a SECURITY REFUSAL would have rendered as a healthy
          // catalogue belonging to someone else. (codex-reviewer, blocking, at 6f2b9132b.)
          // 🔑 I FIXED EXACTLY THIS IN loadResearchStatus ONE COMMIT EARLIER AND DID NOT LOOK AT ITS
          // SIBLING. A fix applied to the surface that raised the question does not reach the
          // surfaces that did not — the same lesson this branch has been reporting all night,
          // committed inside my own diff.
          console.error('available-categories request failed:', availableRes.status, availableRes.detail);
          toast({
            title: 'Could not load Your Catalog',
            description: `The server returned ${availableRes.status}. The list is cleared rather than left showing another site's catalogue.`,
            variant: 'destructive',
          });
        }
      }
    } catch (error) {
      // A thrown fetch is a research list that did NOT load, for the requested-path decision — gated like the commits.
      if (isCurrent()) setResearchListLoadFailed(true);
      console.error('Error loading category characteristics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load category characteristics',
        variant: 'destructive',
      });
    } finally {
      // ⛔ GATED TOO. A superseded call clearing the spinner would report "loaded" while the
      // CURRENT sector's fetch is still genuinely in flight. The current call always reaches its
      // own finally, so the spinner is still guaranteed to clear.
      if (isCurrent()) setLoading(false);
    }
    // ⛔ RETURNED, NOT READ BACK FROM STATE. The existing pattern elsewhere on this page does
    // `await loadData()` and then reads the `categories` closure — which React has not updated yet,
    // so it finds the PRE-refresh object. Returning what was actually fetched is the only way a
    // caller can act on it. Null on the failure path, and the caller must treat that as "unknown".
    return freshCategories;
  }, [sectorId, selectedSite?.id, toast]);

  /**
   * ⛔ THE REPAIR'S RELOAD. Refreshes the list AND replaces the open dialog's category object.
   * Without the second half the dialog keeps the object it was opened with, and its refresh guards
   * keep reading the pre-repair persona state — see shouldConfirmRefresh for what that costs.
   */
  const reloadAfterRepair = useCallback(async (categoryPath: string) => {
    const fresh = await loadData();
    const updated = (fresh || []).find((c) => c.categoryPath === categoryPath);
    if (updated) setSelectedCategory(updated);
  }, [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * ⛔⛔ THE ONLY WAY THIS SCREEN FETCHES RESEARCH STATUS. Six call sites used to build this URL
   * independently; I added `siteId` to ONE of them and left the five post-action reloads behind, so
   * generating attributes/intelligence/hints/benefits/mappings immediately replaced the correct
   * site-scoped extraction-hints count with "Select a site" while a site was plainly selected
   * (codex-reviewer-2, blocking, at 320c7d219). A shared loader makes that class unreachable rather
   * than merely fixed — the same move offering-ops used on #5552 when they made a generation token a
   * REQUIRED parameter instead of auditing its call sites.
   *
   * ⛔ AND IT IS LATEST-RESPONSE-WINS. Adding `selectedSite?.id` as an effect dependency CREATES
   * concurrent requests; it does not order them. Site A resolving after site B repopulated B's panel
   * with A's counts — the identical race I had relayed to offering-ops twice the same night before
   * introducing an instance of it here. The generation is captured BEFORE the await and re-checked
   * after EVERY await (the fetch and the json parse are two suspension points, not one).
   */
  /**
   * ⛔⛔ THE DIALOG VIEWERS NEED THE SAME TWO PROPERTIES AS THE PANEL LOADER, AND I FIXED THEM ONE
   * AT A TIME UNTIL A REVIEWER ENUMERATED FOR ME. loadResearchStatus was fixed, then loadData's
   * catalogue, then codex-reviewer-2 found loadExtractionHints — three siblings, three rounds,
   * one class. So the remaining site/sector-scoped VIEWERS are done together and share one helper
   * rather than acquiring three hand-rolled copies that agree today.
   *
   * THE TWO PROPERTIES, both of which this PR's own subject requires:
   *   1. CLEAR ON START — otherwise a failed or refused load leaves the PREVIOUS site's research
   *      text rendered under the newly selected one. This PR made that reachable: the backend now
   *      returns 403 for an unauthorized site, so a security refusal would render as healthy data.
   *   2. LATEST-REQUEST-WINS — switching site or category creates concurrent requests; ordering
   *      them is a separate problem from creating them.
   *
   * ⚠️ SCOPE, STATED SO THE BOUNDARY IS A DECISION AND NOT AN OVERSIGHT: this covers the loaders
   * that RENDER site/sector-scoped research (hints, canonical attributes, attribute intelligence).
   * The ~20 action handlers in this file can also commit stale toasts and spinners after an await —
   * a real class, and the one offering-ops is working through on #5552 — but that is a different
   * axis (a stale NOTIFICATION, not cross-site CONTENT), and widening into it inside a
   * customer-facing security fix would trade a narrow diff for a broad one. Not done here, on
   * purpose, and worth its own row.
   */
  const viewerGenRef = useRef({ hints: 0, canonical: 0, intelligence: 0 });

  /**
   * ⛔⛔ SCOPE CHANGING WHILE A VIEWER IS ALREADY OPEN — ROUND 6, AND THE FIRST ONE NOT ON THE
   * LOADER PATH AT ALL. Every previous round hardened what happens when a loader RUNS. All three
   * viewers clear on open, re-check staleness after every await, handle non-2xx, gate their failure
   * paths, and clear above their guards. None of that executes if the dialog is ALREADY OPEN and the
   * operator changes the selection underneath it: no loader runs, so nothing clears, nothing
   * invalidates, and site A's research keeps rendering under site B's selection. An in-flight A
   * response can also still commit, because its generation was never bumped.
   * (codex-reviewer, blocking at dd57e637c.)
   *
   * 🔑 THE BOUNDARY MOVED FROM "WHAT THE FUNCTION DOES" TO "WHETHER THE FUNCTION IS CALLED." Six
   * rounds: function → loader → viewer → the failure branch of a guarded viewer → the branch that
   * returns before the guard → THE PATH WHERE NO GUARDED CODE RUNS AT ALL. Each fix was correct and
   * unreachable by the next defect.
   *
   * ⇒ CLOSE, rather than reload. Reloading would swap different content into a dialog the operator
   * opened for a different scope — the same misrepresentation one step removed, and silent. Closing
   * makes the scope change visible, which is the property this whole PR is about.
   * ⚠️ Keyed on the primitive IDS, not the objects: `selectedCategory` is an object whose identity
   * can change without the selection changing, and this effect closes dialogs — an identity-keyed
   * version would shut a viewer under an operator who changed nothing.
   */
  const viewerScopeKeyRef = useRef<string | null>(null);
  useEffect(() => {
    // ⛔⛔ THE KEY CARRIES `sectorId` BECAUSE ALL THREE VIEWERS DO — round 7, codex-reviewer.
    // Every viewer request is sector-addressed:
    //     /sectors/${sectorId}/categories/…/extraction-hints
    //     /sectors/${sectorId}/canonical-attributes
    //     /category-research/${sectorId}/attribute-intelligence/${siteId}
    // and `sectorId = selectedSectorId || siteSectorId`, so a SECTOR PICKER changes it with site and
    // category both unchanged (4 `setSelectedSectorId` call sites). A key of site+category is
    // therefore blind to a scope change the requests are keyed on, and the previous sector's
    // research stays rendered under the new one.
    //
    // 🔑 AND I WROTE THE RULE THAT CATCHES THIS, ONE EFFECT BELOW, ABOUT THIS SAME `sectorId`:
    // "THE KEY MUST CARRY EVERY INPUT THE ANSWER DEPENDS ON, NOT JUST THE PATH... A GUARD THAT WAS
    // COMPLETE BECOMES PARTIAL THE MOMENT A NEW INPUT IS MADE DECISIVE." The sector-resolve guard
    // below was fixed for exactly this; the viewer guard above was written afterwards and did not
    // inherit it. ⇒ Knowing the rule protects the guard you were looking at when you learned it.
    const key = `${selectedSite?.id ?? ''}::${selectedCategory?.id ?? ''}::${sectorId ?? ''}`;
    if (viewerScopeKeyRef.current === null) { viewerScopeKeyRef.current = key; return; } // first run
    if (viewerScopeKeyRef.current === key) return;
    viewerScopeKeyRef.current = key;
    // Invalidate FIRST so an in-flight response from the previous scope cannot commit after this.
    viewerGenRef.current.hints++;
    viewerGenRef.current.canonical++;
    viewerGenRef.current.intelligence++;
    setExtractionHints([]);
    setCanonicalAttributes([]);
    setAttributeIntelligence([]);
    // The superseded requests' own `finally` is gated on staleness and will NOT clear these, so the
    // spinners have to be cleared here or a closed-then-reopened dialog shows a stuck skeleton.
    setExtractionHintsLoading(false);
    setCanonicalLoading(false);
    setIntelligenceLoading(false);
    setExtractionHintsDialogOpen(false);
    setCanonicalDialogOpen(false);
    setIntelligenceDialogOpen(false);
  }, [selectedSite?.id, selectedCategory?.id, sectorId]);

  const researchStatusGenRef = useRef(0);

  const loadResearchStatus = useCallback(async (categoryId: string | undefined) => {
    if (!sectorId || !categoryId) return;
    const generation = ++researchStatusGenRef.current;
    const isStale = () => researchStatusGenRef.current !== generation;
    // ⛔⛔ THE PREVIOUS SITE'S COUNTS ARE CLEARED THE MOMENT A NEW REQUEST STARTS — BOTH REVIEWERS,
    // BLOCKING, INDEPENDENTLY. Latest-response-wins stops an OLD SUCCESS overwriting a NEW one; it
    // does nothing about the panel simply KEEPING what it already had. So after switching site A to
    // B, A's counts stayed on screen for the whole request and INDEFINITELY IF B FAILED — labelled
    // as B's. That is the exact cross-site misrepresentation this PR exists to remove, arriving
    // through the failure path instead of the query.
    // ⚠️ DELIBERATELY NOT GENERATION-GATED, matching loadData's `setCategoriesKnown(false)` above:
    // a new request marking the panel unknown IMMEDIATELY is the safety behaviour, and only the
    // COMMIT is gated. Unknown has to be able to BE unknown.
    setTechnicalResearch(null);
    try {
      // ⇒ siteId, because the Extraction Hints tile counts a SITE-scoped table. Without it the
      // API returns a null count and a reason rather than a fleet-wide total (263 vs 15 for one
      // measured category), and the tile renders the reason.
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/categories/${categoryId}/research-status${
          selectedSite?.id ? `?siteId=${selectedSite.id}` : ''
        }`
      );
      if (isStale()) return;
      if (response.ok) {
        const data = await response.json();
        if (isStale()) return;
        setTechnicalResearch(data.data || null);
      } else {
        // ⛔ NOT SWALLOWED. A non-2xx used to be ignored entirely — no toast, no log, and (before
        // the clear above) the previous site's numbers left standing as though they were this
        // site's. The 403 the backend now returns for an unauthorized site would have been
        // INVISIBLE here, which is how a security refusal reads as "the panel is fine".
        const detail = await response.text();
        console.error('Research status request failed:', response.status, detail);
        if (isStale()) return;
        toast({
          title: 'Could not load research status',
          description: `The server returned ${response.status}. The counts below are cleared rather than left showing another site's data.`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error loading technical research status:', error);
      if (isStale()) return;
      toast({
        title: 'Could not load research status',
        description: 'The request failed. The counts below are cleared rather than left showing another site\'s data.',
        variant: 'destructive',
      });
    }
  }, [sectorId, selectedSite?.id, toast]);

  // Load technical research status when a category is selected
  useEffect(() => {
    if (!selectedCategory || !sectorId) {
      // ⛔ BUMP BEFORE CLEARING. Without this an in-flight load from the previous selection commits
      // AFTER the clear and repopulates a panel for a category that is no longer open.
      researchStatusGenRef.current++;
      setTechnicalResearch(null);
      return;
    }
    loadResearchStatus(selectedCategory.id);
    // selectedSite reaches this through loadResearchStatus's own dependency list: switching site
    // with a category open must re-fetch, or the tiles keep the previous site's counts.
  }, [selectedCategory, sectorId, loadResearchStatus]);

  // Resolve the correct sector for the selected category
  // This ensures write operations (chat, research) go to the correct sector
  useEffect(() => {
    /**
     * ⛔⛔ THE KEY MUST CARRY EVERY INPUT THE ANSWER DEPENDS ON, NOT JUST THE PATH.
     * This guard keyed on categoryPath alone, and that was CORRECT while the path was the whole
     * input. It is not any more: the viewed sector and the candidate sector ids are now decisive
     * (they are sent to resolve-sector and they change the answer), so two in-flight requests for
     * the SAME path under DIFFERENT sectors were indistinguishable here.
     * ⇒ THE CONCRETE RACE: switch 47 -> 50 on one category, the slower 47 response lands last,
     * resolvedCategorySectorId becomes 47, and the destructive controls re-arm against the sector
     * nobody is viewing. That is this PR's own defect arriving through a different door — by a
     * race instead of a key mismatch — on the screen the operator is working in.
     * 🔑 A GUARD THAT WAS COMPLETE BECOMES PARTIAL THE MOMENT A NEW INPUT IS MADE DECISIVE, without
     * the guard being touched — and a partial guard is worse than none, because it reads as handled.
     */
    const requestedPath = selectedCategory?.categoryPath ?? '';
    const requestedKey = sectorResolveKey({
      category: requestedPath,
      viewedSector: sectorId,
      candidateSectors: selectedSite?.sector_ids,
    });
    sectorResolveKeyRef.current = requestedKey;
    // ⛔ CLEAR FIRST, so getWriteSectorId() cannot hand out the previous category's sector while
    // this one resolves. It returns null in that window and every write refuses rather than guesses.
    setResolvedCategorySectorId(null);

    const resolveCategorySector = async () => {
      if (!selectedCategory || !selectedSite?.sector_ids || selectedSite.sector_ids.length <= 1) {
        // ⛔ SINGLE SECTOR IS NOT A FAILURE. No lookup is made, so the viewed sector is the only
        // answer — the one branch here that may legitimately keep it. Marking this unknown would
        // disable every rebuild on single-sector sites, which is most of them.
        setResolvedCategorySectorId(sectorId || null);
        setSectorMismatchWarning(null);
        return;
      }

      try {
        // ⛔⛔ SEND THE FULL PATH, NOT THE LEAF. This used to send categoryPath.split('>').pop(),
        // and sector_category_perceived_attributes.category_name holds a FULL PATH on 1,118 of
        // 1,124 sector-47 rows — so the leaf matched nothing and this effect set null, which is
        // what left the repair button stuck on "Working out which sector..." forever. See
        // backend-merged/tests/routes/category-characteristics.resolveSectorFullPath.test.js.
        const categoryPath = selectedCategory.categoryPath;

        // ⛔ AND SEND THE SECTOR BEING VIEWED. When the same path exists in several of the site's
        // sectors the route answers with THIS one rather than letting `sector_id ASC` pick, because
        // the answer becomes a destructive write target (PersonaRepair.tsx). Without this param the
        // route REFUSES on a multi-sector path rather than guessing — so dropping it does not
        // silently mis-target, it silently re-blocks. Both are asserted route-side.
        const response = await authFetch(
          `${API_URL}/category-characteristics/resolve-sector?sectorIds=${selectedSite.sector_ids.join(',')}&categoryName=${encodeURIComponent(categoryPath)}&viewedSectorId=${encodeURIComponent(String(sectorId ?? ''))}`
        );
        const data = await response.json();

        // ⛔ Discard a resolution the page has moved on from.
        if (sectorResolveKeyRef.current !== requestedKey) return;
        // ⛔⛔ NOT `data.success && data.sectorId`. A resolved:false response carries a FALLBACK
        // sectorId — the first candidate — under success:true. Reading it as an answer is what put
        // a guess behind resolvedWriteSectorId, the value the detail dialog's destructive controls
        // are gated on. See resolvedSectorForWrite.
        const resolvedForWrite = resolvedSectorForWrite(data);
        if (resolvedForWrite !== null) {
          setResolvedCategorySectorId(resolvedForWrite);

          // Check if there's a mismatch
          // Compared as strings: sectorId is a string on this page and resolvedForWrite is a
          // number, so a raw !== is always true and would warn on every category.
          if (String(resolvedForWrite) !== String(sectorId)) {
            // ⚠️ STRING COMPARE, AND THIS WAS A LATENT BUG. `Sector.id` is a string here while the
            // API returns a number; the original `s.id === data.sectorId` only compiled because the
            // response was untyped, so the lookup ALWAYS missed and the warning always fell back to
            // "Sector 47" instead of naming it. Typing the response surfaced it.
            const correctSector = sectors.find(s => String(s.id) === String(resolvedForWrite));
            const currentSector = sectors.find(s => s.id.toString() === sectorId?.toString());
            setSectorMismatchWarning(
              `This category belongs to "${correctSector?.name || `Sector ${resolvedForWrite}`}" but you're viewing "${currentSector?.name || `Sector ${sectorId}`}". Write operations will use the correct sector.`
            );
          } else {
            setSectorMismatchWarning(null);
          }
        } else {
          // ⛔⛔ NULL, NOT `sectorId`. This else branch used to hand the VIEWED sector back as if it
          // were the resolved one, which silently re-armed every destructive control the previous
          // round disabled — a fallback restored one layer above the refusal that removed it.
          // Null keeps resolvedWriteSectorId undefined, so those controls stay disabled and the
          // dialog says it is refusing to guess. Reads are unaffected: getWriteSectorId() still
          // falls back for them.
          setResolvedCategorySectorId(null);
          setSectorMismatchWarning(null);
        }
      } catch (error) {
        // ⛔⛔ A THROWN LOOKUP IS NOT A SINGLE-SECTOR SITE. Both used to end at "no resolved value",
        // and one of them is safe: with one sector there is nothing to resolve, so the viewed
        // sector IS the answer; a network failure on a multi-sector site means we do not know
        // which of several rows a write would land on. Null here, so destructive controls stay
        // disabled and the dialog refuses to guess. (Reads still fall back via getWriteSectorId.)
        console.error('Error resolving category sector:', error);
        if (sectorResolveKeyRef.current !== requestedKey) return;
        setResolvedCategorySectorId(null);
        setSectorMismatchWarning(null);
      }
    };

    resolveCategorySector();
  }, [selectedCategory, selectedSite?.sector_ids, sectorId, sectors]);

  /**
   * The sector a write should target: the category's OWN resolved sector when it differs from the
   * one being viewed, else the viewed one.
   *
   * ⛔⛔ IT DOES NOT COVER EVERY WRITE ON THIS PAGE, AND THE USER IS TOLD OTHERWISE. When the
   * category resolves elsewhere, `sectorMismatchWarning` renders (built at :1530, shown at :3074,
   * both as of next@a9eee2d71 — this comment shifts them, so search the strings, not the numbers)
   * saying "Write operations will use the correct sector." ⇒ THAT SENTENCE IS TRUE FOR THE CALLERS
   * OF THIS HELPER AND FALSE FOR THE FIVE HANDLERS BELOW, on the same panel, with nothing on screen
   * distinguishing them. Measured 2026-08-02 (task-87537g8s9a); recorded here because a split that
   * exists only in the author's head is indistinguishable from an oversight to everyone after them.
   *
   * USES THIS HELPER — writes follow the CATEGORY:
   *   handleRefresh, handleRefreshAspect, handleChatSend, handleChatExecute (the only four callers).
   *   The tab-row buttons reach the same behaviour by a different route: handleTriggerResearch
   *   re-resolves the sector itself, and handleResearchGptCategory delegates to it.
   *
   * DOES NOT — writes follow the sector CURRENTLY BEING VIEWED:
   *   handleRegenerateAttributes, handleResearchIntelligence, handleResearchExtractionHints,
   *   handleResearchPerceivedBenefits, handleResearchAttributeMappings.
   *
   * ⚠️ AND THEY ARE NOT ALL THE SAME CASE — do not "fix" them as a group:
   *   - /mappings/populate/:sectorId takes A SECTOR AND NO CATEGORY, so the viewed sector may be
   *     entirely correct there. That one may be by design.
   *   - The other four take a (SECTOR, CATEGORY) PAIR, so passing the viewed sector with a category
   *     belonging to another one does not produce a sector-scoped write — IT PRODUCES A MIXED PAIR.
   *   - ⛔ TWO OF THOSE FOUR ALREADY TRY TO VALIDATE THE PAIR AND CANNOT: research-attributes and
   *     research-extraction-hints look the category up `WHERE id = $1 AND sector_id = $2` against
   *     sector_category_templates, but this page sends a category_characteristics.id. Both are
   *     independently generated UUIDs — measured on the replica, 2,313 vs 1,704 rows, ID OVERLAP 0 —
   *     so the sector-filtered lookup misses every time and both fall through to a fallback that
   *     resolves by id alone with NO sector filter. The intent to validate is in the code; the check
   *     never runs.
   *
   * ⇒ WHICH REMEDY IS RIGHT IS AN OPEN PRODUCT QUESTION (task-87537g8s9a): route the pair-taking
   * handlers through this helper, or stop rendering a promise that is not uniformly kept. Nothing
   * here has been changed on that basis — this comment records what was MEASURED, not what should
   * happen. ⚠️ Reachable only when the resolved and viewed sectors differ, which needs a multi-sector
   * customer (2 of 37 sites) or the "Managing:" override; small, but not zero.
   */
  const getWriteSectorId = useCallback(() => {
    return resolvedCategorySectorId || sectorId;
  }, [resolvedCategorySectorId, sectorId]);

  /**
   * ⛔⛔ THE SECTOR TO WRITE WITH **ONLY ONCE IT IS KNOWN FOR THIS CATEGORY** — no fallback.
   * getWriteSectorId() above falls back to the VIEWED sector, which is right for a category whose
   * resolution has completed and matched, and WRONG while resolution is still in flight: for a
   * category belonging to another sector it would hand out the viewed one and the write would land
   * on the wrong row. ⇒ resolvedCategorySectorId is cleared on every category change, so `null`
   * here means NOT YET KNOWN, and undefined makes every consumer refuse instead of guess.
   * ⚠️ Deliberately NOT changing getWriteSectorId itself: its other callers each already handle a
   * falsy result, and re-pointing shared write targeting is task-87537g8s9a's decision, not a
   * ride-along on a blocking fix.
   */
  const resolvedWriteSectorId = resolvedCategorySectorId ?? undefined;

  /**
   * ⛔⛔ ONE PLACE ANSWERS "WHAT DID THAT WRITE ACTUALLY PERSIST", FOR EVERY CALLER.
   * This began as two local copies inside handleRefresh and handleTriggerResearch, and adding the
   * fourth terminal-report site was about to make a third — the exact shape this PR has now hit
   * four times (a trailing `else`, an unwritten counter, a falsified contract, and this). Hoisted
   * BEFORE writing the third copy rather than after a reviewer found it.
   *
   * Takes the sector EXPLICITLY because confirming a write and refreshing the screen are different
   * questions: loadData() loads the VIEWED sector, and a refresh can be routed elsewhere. Callers
   * pass the sector they actually wrote to.
   * Never throws: an unconfirmable write becomes classifyRefreshResult's 'unknown', never a
   * silently-assumed success.
   */
  const confirmWrittenRow = async (
    targetSectorId: number,
    path: string
  ): Promise<{ researchError?: string | null } | null> => {
    try {
      const res = await authFetch(
        `${API_URL}/category-characteristics/${targetSectorId}/category/${encodeURIComponent(path)}`
      );
      if (!res.ok) return null;
      const envelope = await res.json();
      return envelope?.data ?? null;
    } catch (confirmError) {
      console.error('[CategoryResearch] Could not confirm the written row (the original result is reported separately):', confirmError);
      return null;
    }
  };

  const handleRefresh = async (categoryPath: string) => {
    // ⛔⛔ THE RESOLVED-ONLY VALUE, NOT getWriteSectorId(). The button being disabled on
    // `writeSectorId == null` is a guard on the CONTROL, not on the WRITE — and a pending
    // confirmation can outlive the category, sector or site it was opened for. In that window the
    // dialog showed one row (or "refusing to guess") while this POSTed with the VIEWED sector.
    // ⇒ THE UI GUARD IS NOT THE SAFETY BOUNDARY. The write is. (codex BLOCKING.)
    // ⚠️ A single-sector site still works: that branch sets resolvedCategorySectorId to the viewed
    // sector because no lookup is needed, so this is defined there.
    const writeSectorId = resolvedWriteSectorId;
    if (!writeSectorId) {
      toast({
        title: 'Cannot re-research this category',
        description: `We could not work out which sector "${categoryPath}" belongs to, and this site has more than one. Nothing was changed.`,
        variant: 'destructive',
      });
      return;
    }

    // Worst-case full-refresh duration measured live on p4 today (10 parallel LLM calls +
    // a sequential persona-research step): 194,581ms. 5 minutes gives real headroom without
    // leaving the operator staring at a spinner indefinitely if the backend genuinely hangs
    // — the original bug this whole programme started from (task-dtxfk93uav).
    const REFRESH_TIMEOUT_MS = 5 * 60 * 1000;

    // Re-fetch and re-select from what loadData() RETURNS, not the `categories` closure — this
    // closure captured `categories` before the refresh, and awaiting loadData() does not update
    // a captured value, so reading it back afterward shows the PRE-refresh row. loadData()
    // returns the freshly-fetched array for exactly this reason (see its own comment). Used on
    // every outcome (success, failure, timeout) — the failure branch previously didn't refresh
    // at all, which was the direct cause of a failed refresh reverting to idle with no visible
    // trace until a full page reload.
    // ⛔ MUST NEVER THROW. Every failure branch below calls this BEFORE recording/showing the
    // ORIGINAL error (so the dialog reflects real state before the operator sees the message) —
    // but if the reload itself fails, that must not erase the original error. loadData() already
    // catches everything internally and cannot throw; this wraps the rest (the array lookup and
    // setSelectedCategory) in its own try/catch too, so a failure IN here is only ever logged,
    // never allowed to propagate past this function and skip the caller's error reporting.
    const refreshSelectedAndTable = async (): Promise<void> => {
      try {
        const fresh = await loadData();
        const updated = (fresh || []).find(c => c.categoryPath === categoryPath) || null;
        if (updated && selectedCategory?.categoryPath === categoryPath) {
          setSelectedCategory(updated);
        }
      } catch (reloadError) {
        console.error('Error re-fetching category detail after refresh (original error is reported separately):', reloadError);
      }
    };

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${writeSectorId}/category/${encodeURIComponent(categoryPath)}/refresh`,
        { method: 'POST', timeout: REFRESH_TIMEOUT_MS }
      );

      // ⛔ ONE parse: the same payload yields the error body AND the key the server stored under.
      const payload = await response.json().catch(() => null);
      const errorBody = response.ok ? null : (payload ?? ({} as any));
      // fetch the fresh row BEFORE classifying, since classification on the success path needs
      // its researchError — the canonical-schema write is deliberately post-commit and can fail
      // independently (status stays 'active', researchError gets set, e.g.
      // CANONICAL_SCHEMA_WRITE_INCOMPLETE), which is why a 200 alone is never enough to classify.
      const fresh = await confirmWrittenRow(writeSectorId, storedKeyFrom(payload, categoryPath));
      await refreshSelectedAndTable();
      const outcome = classifyRefreshResult(response.ok, response.status, fresh, errorBody);
      // Only a clean success clears the persistent session error; every other kind — including
      // 'unknown', where the POST succeeded but the confirming reload did not — leaves a message
      // on screen after the toast auto-dismisses.
      setLastRefreshError(outcome.kind === 'success' ? null : outcome.message);
      toast(toastForOutcome(outcome, 'Category characteristics have been refreshed'));
    } catch (error: any) {
      // A client-side timeout/abort does not mean the backend stopped — the request may
      // still be running server-side. Re-fetch the category detail so the operator sees
      // whatever the real current state is (failed / still researching / actually finished)
      // instead of the request just vanishing, which is what happened before this fix.
      await refreshSelectedAndTable();
      const isTimeout = error?.name === 'AbortError' || /timed out|timeout/i.test(error?.message || '');
      const message = isTimeout
        ? `No response after ${REFRESH_TIMEOUT_MS / 1000}s. The refresh may still be running in the background — check the status below or refresh the page shortly.`
        : (error.message || 'Unknown error');
      setLastRefreshError(message);
      toast({
        title: isTimeout ? 'Research Taking Longer Than Expected' : 'Research Failed',
        description: message,
        variant: 'destructive',
      });
    }
  };

  /**
   * ⛔⛔ THE FOUR-STEP CHAIN BEHIND "Regenerate buyer personas" (task-aodbhweog0, Jaume ruling
   * 2026-08-12: one press, delete-then-recreate).
   *
   * A plain refresh/personas on a weighted category CANNOT recreate in the archetype format:
   * preserveBenefitWeights matches stored→researched by persona_key, an archetype recreate invents
   * new keys, so every weighted persona strands and the write is REFUSED (HTTP 500 carrying the
   * guard's text). The chain removes the strandable state first, on purpose.
   *
   *   1. clear-persona-weights   — empties benefit_weights so nothing can strand (the guard's
   *                                :277 skip). Personas are NOT deleted.
   *   2. refresh/personas        — the fresh archetype personas, now permitted.
   *   3. research-persona-weights{dryRun:false} — re-weights them. dryRun DEFAULTS TRUE server-side,
   *                                so the explicit false is load-bearing, not decorative.
   *   4. verify                  — re-read the row and confirm the personas came back WEIGHTED.
   *
   * ⛔ NO SILENT PARTIAL. Each step names the state the category is left in when it fails, because
   * the failures are not interchangeable: failing at 2 leaves the OLD personas minus their weights;
   * failing at 3 leaves FRESH personas with NO weights, which silently demotes generation to the
   * sector fallback. An operator told only "it failed" cannot tell those apart, and they need
   * different next actions.
   */
  const [regenPhase, setRegenPhase] = useState<null | RegenPhase>(null);

  const handleRegeneratePersonas = async (categoryPath: string) => {
    const writeSectorId = resolvedWriteSectorId;
    if (!writeSectorId) {
      toast({
        title: 'Cannot regenerate personas',
        description: `We could not work out which sector "${categoryPath}" belongs to, and this site has more than one. Nothing was changed.`,
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await runRegeneratePersonasChain({
        authFetch,
        base: `${API_URL}/category-characteristics/${writeSectorId}/category/${encodeURIComponent(categoryPath)}`,
        onPhase: setRegenPhase,
        /**
         * ⛔⛔ THE OPERATOR DECIDES, NOT THE PAGE. The readiness preflight can return ready:true
         * with a `notChecked` warning — for a discovered sub-category the product sample is drawn
         * at random, so nothing can predict what the refresh will see. The next step is
         * irreversible, so the person who will lose the curated weights is the one who has to
         * accept that uncertainty.
         * ⚠️ `confirm` rather than a custom dialog: it BLOCKS, and this decision must not be
         * possible to click past. The same primitive already guards the attribute delete in this
         * file. The server's own words are shown — this page does not summarise them.
         */
        /**
         * ⛔⛔ REWRITTEN WITH THE REORDER, AND THE OLD SENTENCE WAS THE URGENT ONE. It said the run
         * "will PERMANENTLY DISCARD the curated benefit weights BEFORE it recreates the personas".
         * That was true of the four-step chain and is false now: the recreate's own write is the
         * destructive one, so nothing is discarded unless the research SUCCEEDS.
         * ⇒ Operator-facing copy that misdescribes WHEN destruction happens is worse than none —
         *   it makes someone decline a safe action, or brace for a loss that no longer occurs.
         */
        /**
         * ⛔⛔ THE FIFTH SIBLING OF THE SAME FALSE CERTAINTY (codex leg 1, minor at d7ba96287).
         * This said a weighting failure leaves "the weights the research produced". It cannot say
         * that: the weighting service takes no client and runs no transaction — its single write
         * is an autocommitting `pool.query` UPDATE — so an error can arrive AFTER the
         * purpose-researched weights are durable.
         * 🔑 I FIXED THIS EXACT CLAIM IN regeneratePersonasChain.ts AND DID NOT CENSUS THIS FILE,
         *   having written, in the commit that fixed it, that fixing the reported instance buys one
         *   review round per sibling. Predicting the pattern is not the same as running the census.
         */
        /**
         * ⭐ THE TEXT NOW LIVES IN THE TESTED MODULE (task-3b3e5t9b9g). This page composed it, and
         * this page is the half of the surface no test executes — which is where four of #5789's
         * false-claim siblings survived, including the one three comment-blocks above.
         * ⇒ The page's job is to SHOW the message and return the answer. It authors no claims about
         *   what the server did, so a sibling cannot be written here without editing the chain.
         */
        confirmUnverified: (message: string) => confirm(message),
        // ⛔ THE STAMP THE OPERATOR IS LOOKING AT — from the row currently on screen, not one the
        // chain fetches for itself. A precondition read moments before the call it guards always
        // matches; this one answers "has anything changed since the page was drawn".
        // ⛔ selectedCategory ONLY — never a lookup into the captured categories array. That array
        // is closure state which loadData() does not refresh, so a stamp read from it could be
        // OLDER than what the operator is looking at and would produce a spurious conflict on a row
        // nobody touched. The panel's selected row IS the operator's view, which is the whole
        // definition of this precondition. The writeSafety guard bans that lookup for the
        // neighbouring reason (it put a pre-refresh row back on screen) and it fired on my first
        // version of this line, correctly.
        // ⚠️ Worded WITHOUT the literal call expression on purpose: that guard is a source-text
        // scan, so spelling the banned form here — even to forbid it — trips the ban. A guard that
        // fires on the explanation is a guard that fires on the cure.
        // The status the screen last saw — the chain refuses a non-active row before dispatching
      // anything. Same narrowing as the stamp below: only when the selected row IS this row.
      status: selectedCategory?.categoryPath === categoryPath
        ? selectedCategory?.status ?? null
        : null,
      expectedUpdatedAt: selectedCategory?.categoryPath === categoryPath
          ? selectedCategory?.updatedAt ?? null
          : null,
      });
      toast({
        title: 'Buyer personas regenerated',
        description: describeChainSuccess(result),
      });
      // ⛔ Read the rows loadData RETURNS, not the captured state — the panel must not keep
      // describing the pre-chain row after a write it just made.
      const fresh = await loadData();
      if (selectedCategory?.categoryPath === categoryPath) {
        const updated = (fresh || []).find((c) => c.categoryPath === categoryPath);
        if (updated) setSelectedCategory(updated);
      }
    } catch (err: any) {
      /**
       * ⛔ NEVER SWALLOWED, NEVER GENERIC, AND WHEN WE CANNOT KNOW THE STATE IT SAYS SO. A transport
       * or parse failure after the clear was dispatched is indistinguishable from a clear that
       * committed, so it is reported as possibly-committed. Silence would leave the operator
       * assuming nothing happened, which is the expensive direction to be wrong in.
       */
      /**
       * ⛔ READS THE FLAG, NOT THE PROSE. This previously also required the message NOT to start
       * with ⛔, as a proxy for "this refusal was definitive". That proxy held only while every
       * definitive refusal happened to be worded that way — the eligibility 422 added later was
       * not, so the operator was told "Nothing was changed" and, in the same toast, that the
       * weights MAY ALREADY HAVE BEEN REMOVED. The chain now records the fact directly.
       */
      /**
       * ⚠️ "The clear step" NO LONGER EXISTS — the chain recreates before it destroys, so the
       * request that may have committed is the RECREATE. Naming a step that never ran sends an
       * operator looking for something that cannot have happened.
       * ⛔ THE TEXT NOW LIVES IN THE CHAIN MODULE, which is the half of this surface that tests
       *   actually execute. It was authored here, and this file is where four of this PR's five
       *   false-claim siblings survived — including this one, whose "re-run this button" advice
       *   would have overwritten the only copy of the replaced curated vectors.
       */
      toast({
        title: 'Regenerate buyer personas FAILED',
        description: describeChainFailure(err),
        variant: 'destructive',
      });
    } finally {
      setRegenPhase(null);
    }
  };


  const handleRefreshAspect = async (categoryPath: string, aspect: string) => {
    // ⛔⛔ THE RESOLVED-ONLY VALUE, NOT getWriteSectorId(). The button being disabled on
    // `writeSectorId == null` is a guard on the CONTROL, not on the WRITE — and a pending
    // confirmation can outlive the category, sector or site it was opened for. In that window the
    // dialog showed one row (or "refusing to guess") while this POSTed with the VIEWED sector.
    // ⇒ THE UI GUARD IS NOT THE SAFETY BOUNDARY. The write is. (codex BLOCKING.)
    // ⚠️ A single-sector site still works: that branch sets resolvedCategorySectorId to the viewed
    // sector because no lookup is needed, so this is defined there.
    const writeSectorId = resolvedWriteSectorId;
    if (!writeSectorId) {
      toast({
        title: 'Cannot re-research this category',
        description: `We could not work out which sector "${categoryPath}" belongs to, and this site has more than one. Nothing was changed.`,
        variant: 'destructive',
      });
      return;
    }

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${writeSectorId}/category/${encodeURIComponent(categoryPath)}/refresh/${aspect}`,
        { method: 'POST' }
      );

      if (response.ok) {
        toast({
          title: 'Aspect Refreshed',
          description: `${ASPECT_CONFIG[aspect as keyof typeof ASPECT_CONFIG]?.label || aspect} has been updated`,
        });
        // ⛔ Same as handleRefresh: read the rows loadData RETURNS, not the captured state.
        const fresh = await loadData();
        if (selectedCategory?.categoryPath === categoryPath) {
          const updated = (fresh || []).find((c) => c.categoryPath === categoryPath);
          if (updated) setSelectedCategory(updated);
        }
      } else {
        const error = await response.json();
        throw new Error(error.error);
      }
    } catch (error: any) {
      toast({
        title: 'Refresh Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleMarkAllStale = async () => {
    if (!sectorId) return;

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${sectorId}/mark-stale`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Categories Marked Stale',
          description: `${data.markedCategories?.length || 0} categories will be refreshed on next use`,
        });
        await loadData();
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  /**
   * Does a category with this typed name ALREADY hold research that the "new category" button
   * would overwrite?
   * ⛔⛔ EXACT MATCH ON THE STORED PATH, AND THAT IS NOT PEDANTRY — IT IS WHAT THE ENDPOINT DOES.
   * backend-merged/routes/category-characteristics.js:613 keys on `category_path = decodedPath`
   * verbatim: no normalisation, no leaf resolution, no case folding. So the row this write can
   * destroy is EXACTLY the one whose path equals the typed string.
   * ⇒ MY FIRST VERSION MATCHED THE LEAF CASE-INSENSITIVELY AND THAT WAS WRONG IN THE DANGEROUS
   * DIRECTION-OF-FIX: typing "Wetsuits" when "A > B > Wetsuits" is stored writes a NEW row keyed
   * "Wetsuits" and leaves the stored one untouched, so the dialog would have warned about a row
   * this action does not modify — and "freezing" the write onto that matched path, the obvious fix
   * for the blocking item, would have CONVERTED a non-destructive action into a destructive one.
   * ⚠️ WHAT IS THEREFORE NOT COVERED, STATED PLAINLY: typing a bare leaf silently creates a
   * duplicate row under the leaf key beside the real full-path one. That is a different defect —
   * a bare-leaf twin, not an overwrite — and it is filed rather than fixed here.
   * ⛔ AND IT MUST NOT WARN WHEN IT FINDS NOTHING: a first-time research is not destructive, and a
   * warning there trains the operator to click past the warnings that matter.
   */
  /**
   * ⛔⛔ DOES STORED RESEARCH EXIST FOR THIS PATH? true / false / NULL FOR NOT KNOWN YET.
   *
   * 🔑 THE SOURCE WE WERE ASKING DID NOT ANSWER THE QUESTION WE MEANT. Every guard on this page
   * used to read `hasResearch` off availableCategories, which comes from /available-categories —
   * a PRODUCT-FEED-DERIVED catalogue list, not the research table. It:
   *   • drops any category with FEWER THAN THREE feed products
   *     (backend-merged/routes/category-characteristics.js:1283, `HAVING COUNT(DISTINCT p.id) >= 3`)
   *   • filters to the sector's google_taxonomy_root_path when one is set (:1280)
   *   • is CACHED IN-PROCESS FOR FIVE MINUTES with no invalidation (:1241)
   * ⇒ A category that genuinely HAS research can read as not-researched, and the guard then runs
   * with NO CONFIRMATION AT ALL. Not a wrong warning — no warning, on a real destructive write.
   * ⛔ AND IT FAILED ON THE WORST COHORT: thin-catalogue categories are exactly the long tail, and
   * exactly where research is most laborious to rebuild. The guard was weakest where loss hurts most.
   *
   * ⇒ THIS READS `categories`, straight off GET /category-characteristics/:sectorId, which is
   * `SELECT * FROM category_characteristics WHERE sector_id = $1` (:336) — no product-count floor,
   * no taxonomy filter, no cache. The siteId param there touches only an unrelated dimension query,
   * so it does not narrow these rows.
   * ⛔ NOT SOLVED BY INVALIDATING THE CACHE: that would keep a feed-derived list as the source of
   * truth for a research question and leave the too-few-products hole wide open.
   * ⚠️ ANY row counts, not just status='active'. A row mid-research or stale still holds content a
   * rebuild replaces, and the conservative direction is the only honest one on a destructive path.
   */
  const researchExistsFor = (categoryPath: string): boolean | null => {
    if (!categoriesKnown) return null;
    return categories.some((c) => c.categoryPath === categoryPath);
  };

  /** True unless we KNOW there is nothing to lose. Unknown confirms — same rule as the batch steps. */
  const mayHaveResearch = (categoryPath: string) => researchExistsFor(categoryPath) !== false;

  /**
   * ⛔⛔ EXISTENCE FOR THE SECTOR THE WRITE WILL ACTUALLY TARGET. true / false / NULL FOR CANNOT KNOW.
   *
   * 🔑 THE ORDERING BUG THIS CLOSES IS A COMPOSITION OF TWO CORRECT FIXES. researchExistsFor answers
   * about the VIEWED sector — `categories` is loaded from GET /category-characteristics/:sectorId
   * for the sector on screen. The row handlers then resolve a DIFFERENT sector for the write.
   * Asking existence first and resolving second means a category unresearched in the sector you are
   * looking at, but researched in the sector the write resolves to, REBUILDS SILENTLY. Both halves
   * were right; only their order was wrong, which is why neither round that added them caught it.
   * ⇒ Resolve the target FIRST, then ask about the target.
   * ⛔ AND A CROSS-SECTOR TARGET IS UNKNOWABLE FROM LOADED STATE — we hold rows for one sector only.
   * Unknown confirms, exactly as unresolved sectors refuse and unverified batch steps count as
   * destructive. Third application of the same rule.
   */
  const researchExistsInSector = (categoryPath: string, targetSectorId: number): boolean | null => {
    if (!categoriesKnown) return null;
    if (String(targetSectorId) !== String(sectorId)) return null;
    return categories.some((c) => c.categoryPath === categoryPath);
  };

  const existingResearchForTypedName = (typed: string) => {
    const name = typed.trim();
    if (!name) return null;
    return mayHaveResearch(name) ? name : null;
  };

  /** The click/Enter entry point: confirm first when the typed name would overwrite stored research. */
  const startResearchNewCategory = () => {
    const typed = newCategoryName.trim();
    if (!typed) return;
    // ⛔⛔ FREEZE THE WRITE TARGET AT THE CLICK. The confirm handler used to re-read
    // newCategoryName, so editing the input while the dialog was open meant agreeing to one
    // category and rebuilding another — the operator can change the target AFTER reading the
    // warning. Same one-value rule that fixed the sector: the dialog shows this string, the
    // handler is handed this string, and nothing re-derives it. (codex BLOCKING.)
    if (existingResearchForTypedName(typed)) setPendingNewCategory(typed);
    else handleResearchNewCategory(typed);
  };

  // Research a new category by name (no product dependency required)
  const handleResearchNewCategory = async (frozenPath?: string) => {
    // ⛔ THE PATH IS AN ARGUMENT, NOT A RE-READ OF STATE. See startResearchNewCategory.
    const categoryPath = (frozenPath ?? newCategoryName).trim();
    if (!sectorId || !categoryPath) return;
    setTriggeringResearch(categoryPath);

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${sectorId}/category/${encodeURIComponent(categoryPath)}/refresh`,
        { method: 'POST' }
      );

      // ⛔⛔ THE FOURTH TERMINAL-REPORT SITE, and it still claimed success from `response.ok`
      // alone — so a 200 that persisted a researchError (CANONICAL_SCHEMA_WRITE_INCOMPLETE among
      // them) reported a clean success for a partial failure, exactly the defect this PR exists to
      // remove. It survived three rounds because it is reached from the "New category name" box
      // rather than from a table row, so it was outside every set anyone had enumerated.
      // ⚠️ The write targets `sectorId` DELIBERATELY here — a brand-new name has nothing to
      // resolve, so the sector the operator selected IS the target — and the confirmation below
      // reads that SAME sector, which is the invariant that matters, not which sector it is.
      // ⛔⛔ THE KEY COMES FROM THE SERVER. This site submits a BARE NAME, which the route
      // normalises to a canonical writeKey before persisting — so confirming with the submitted
      // path looked up a row that does not exist and reported "Status Unknown" for a clean write.
      const payload = await response.json().catch(() => null);
      const errorBody = response.ok ? null : (payload ?? ({} as any));
      const fresh = response.ok
        ? await confirmWrittenRow(sectorId, storedKeyFrom(payload, categoryPath))
        : null;
      const outcome = classifyRefreshResult(response.ok, response.status, fresh, errorBody);

      if (response.ok) {
        // The write happened regardless of how it is CLASSIFIED — a partial success is still a
        // write — so the input clears and the table reloads for every 2xx. Only the message differs.
        setNewCategoryName('');
        await loadData();
      }
      toast(toastForOutcome(outcome, `Category "${categoryPath}" has been researched`));
    } catch (error: any) {
      toast({
        title: 'Research Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setTriggeringResearch(null);
    }
  };

  /**
   * ⛔⛔ ONE FUNCTION RETURNS THE SECTOR A TABLE-ROW RESEARCH WILL ACTUALLY WRITE TO, and both the
   * confirmation dialog and the handler take it from here.
   *
   * 🔑 WHY THIS EXISTS AS A FUNCTION AT ALL. The dialog used to display getWriteSectorId() — the
   * sector of the category open in the DETAIL PANEL — while this handler re-resolved the sector of
   * the CLICKED ROW, which is a different category entirely. So the warning could name one row and
   * the write land on another, which is worse than naming no row: it is a warning that reads as
   * precise and is wrong. Five call sites agreeing by inspection is what has failed five times.
   *
   * ⛔ AND IT NO LONGER FALLS BACK. An earlier revision of this comment said the resolve-failed
   * fallback was pre-existing and deliberately unchanged. That was true when written and is false
   * now: showing the fallback in the dialog as a confident, precise sector is what made keeping it
   * indefensible, so a failed lookup on a multi-sector site returns null and the caller refuses.
   * See the fail-closed branch below.
   */
  const resolveTriggerSectorId = async (categoryPath: string): Promise<number | null> => {
    if (!sectorId) return null;
    // Single-sector site: there is nothing to resolve and no lookup is made. The viewed sector is
    // the only answer, not a fallback from a failure.
    if (!selectedSite?.sector_ids || selectedSite.sector_ids.length <= 1) return sectorId;

    // ⛔⛔ FULL PATH, NOT LEAF — same defect as the resolve effect above (task-2by6ldlmau defect 2).
    // The SCPA key is a full path on 1,118 of 1,124 sector-47 rows, so a leaf lookup returns
    // nothing and this helper's fail-closed branch fires on categories that are perfectly findable.
    // ⛔ viewedSectorId: `sectorId` is the sector the operator has open (guarded non-null above),
    // and it decides multi-sector paths instead of a tie-break the operator never chose.
    try {
      const resolveResponse = await authFetch(
        `${API_URL}/category-characteristics/resolve-sector?sectorIds=${selectedSite.sector_ids.join(',')}&categoryName=${encodeURIComponent(categoryPath)}&viewedSectorId=${encodeURIComponent(String(sectorId))}`
      );
      const resolveData = await resolveResponse.json();
      // ⛔⛔ NOT `success && sectorId`. The route returns success TRUE and a FALLBACK sectorId (the
      // first candidate) with resolved:false when it cannot place the category — a guess wearing a
      // success label. See resolvedSectorForWrite.
      const targetSectorId = resolvedSectorForWrite(resolveData);
      if (targetSectorId !== null) {
        if (targetSectorId !== sectorId) {
          const targetSectorName = sectors.find(s => s.id.toString() === targetSectorId.toString())?.name || `Sector ${targetSectorId}`;
          console.log(`[CategoryResearch] Resolved "${categoryPath}" to sector ${targetSectorId} (${targetSectorName})`);
        }
        return targetSectorId;
      }
      // ⛔⛔ FAIL CLOSED. The lookup ran on a MULTI-SECTOR site and did not RESOLVE. Falling back to
      // the viewed sector here is a silent fallback on a destructive path, and this PR makes it
      // WORSE than it was: the dialog now displays that fallback as a confident, precise sector,
      // so the operator reads a specific row and the write can land on a different one.
      // ⇒ Refuse, and say why. Nothing is written and nothing is guessed. (codex BLOCKING.)
      console.warn('[CategoryResearch] Sector resolution did not RESOLVE for', categoryPath, '— response was', resolveData, '(a resolved:false response still carries a fallback sectorId; it is not an answer)');
      return null;
    } catch (e: any) {
      console.warn('[CategoryResearch] Sector resolution failed for', categoryPath, e);
      return null;
    }
  };

  /**
   * @param preResolvedSectorId the sector the CONFIRMATION DIALOG showed the operator. Passed so the
   * write cannot land anywhere other than the row they were warned about — re-resolving here would
   * reopen the gap this closes.
   */
  const handleTriggerResearch = async (categoryPath: string, preResolvedSectorId?: number) => {
    if (!sectorId) return;

    setTriggeringResearch(categoryPath);
    const categoryName = categoryPath.split(' > ').pop() || categoryPath;

    try {
      // ⛔ NO `?? sectorId` TAIL. resolveTriggerSectorId now returns null to mean REFUSED, and a
      // fallback here would reinstate exactly what the refusal exists to prevent.
      const targetSectorId = preResolvedSectorId ?? (await resolveTriggerSectorId(categoryPath));
      if (!targetSectorId) {
        toast({
          title: 'Cannot research this category yet',
          description: `Could not work out which sector "${categoryPath}" belongs to, and this site has more than one. Nothing was changed.`,
          variant: 'destructive',
        });
        setTriggeringResearch(null);
        return;
      }

      // Show initial toast - research can take 1-2 minutes
      toast({
        title: 'Research Started',
        description: `Researching ${categoryName}... This may take 1-2 minutes.`,
      });

      // ⛔ Re-fetches the row and returns it, or null if the reload itself failed — MUST NEVER
      // THROW (mirrors handleRefresh's refreshSelectedAndTable, minus the dialog-selection state
      // this flow has none of). A failure here becomes classifyRefreshResult's 'unknown' outcome,
      // never a silently-assumed success.
      // Fire the research request - don't wait for completion (may timeout)
      // Use AbortController to handle timeout gracefully
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout for initial response

      try {
        const response = await authFetch(
          `${API_URL}/category-characteristics/${targetSectorId}/category/${encodeURIComponent(categoryPath)}/refresh`,
          { method: 'POST', signal: controller.signal }
        );
        clearTimeout(timeoutId);

        // ⛔⛔ CR-1 BLOCKING (both legs, #4953 @ 52328f6f9): this used to show "Research Complete"
        // unconditionally on `response.ok`, bypassing classifyRefreshResult entirely — a refresh
        // that persisted a researchError (CANONICAL_SCHEMA_WRITE_INCOMPLETE, the exact case this
        // PR exists for) but happened to answer inside the 10s window read as a false success, and
        // polling never got a chance to correct it because this `return` short-circuits it. Every
        // live-verification attempt against this fast path went down exactly this broken branch —
        // it is WHY the abort→poll branch was hard to reproduce live, not an unrelated gap.
        const payload = await response.json().catch(() => null);
        const errorBody = response.ok ? null : (payload ?? ({} as any));
        const fresh = await confirmWrittenRow(targetSectorId, storedKeyFrom(payload, categoryPath));
        await loadData();
        const outcome = classifyRefreshResult(response.ok, response.status, fresh, errorBody);

        toast(toastForOutcome(outcome, `${categoryName} research finished successfully.`));
        setTriggeringResearch(null);
        return;
      } catch (fetchError: any) {
        clearTimeout(timeoutId);

        // If aborted or network error, start polling
        if (fetchError.name === 'AbortError' || fetchError.message?.includes('fetch')) {
          console.log('[CategoryResearch] Request timeout, starting status polling...');
          // Continue to polling below
        } else {
          throw fetchError;
        }
      }

      // Poll for research completion (handles timeout/long-running research)
      const maxPolls = 24; // 2 minutes total (24 * 5s)
      const pollInterval = 5000; // 5 seconds
      // ⚠️ [leg2, #4953 @ 52328f6f9] A non-ok status on the poll ITSELF used to be treated as
      // transient unconditionally, so a persistent 401/403 (auth expired mid-poll) or a 500 that
      // never clears silently retried into the generic "taking longer than expected" after all 24
      // polls — indistinguishable from a genuinely slow research run. 401/403 cannot self-heal by
      // retrying, so those fail fast; anything else gets 3 consecutive failures before giving up,
      // and either way the operator sees the LAST status/body, not a content-free timeout message.
      let pollFailures = NO_POLL_FAILURES;

      for (let i = 0; i < maxPolls; i++) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));

        // ⛔⛔ A DETECTED TERMINAL STATUS MUST NEVER BE HANDLED INSIDE THIS TRY. The previous
        // version threw the 'failed' case from inside this same try/catch, and the catch below
        // (correctly scoped to catch TRANSPORT failures — a fetch that couldn't even complete —
        // so a network blip doesn't abort the whole poll) could not tell that throw apart from a
        // real transport error. It swallowed the detected failure as "continue polling", so a
        // backend failure detected within seconds still ended, two minutes later, with the
        // generic "taking longer than expected" message — the exact defect this file exists to
        // fix, reproduced on the one path that was never live-tested. Fetching and interpreting
        // the response are now fully separated: only the FETCH is inside a try/catch, and a
        // detected terminal status (failed/active/fresh) is handled and returned from OUTSIDE it.
        let body: any = null;
        try {
          const statusResponse = await authFetch(
            `${API_URL}/category-characteristics/${targetSectorId}/category/${encodeURIComponent(categoryPath)}`
          );
          if (statusResponse.ok) {
            body = await statusResponse.json();
          } else {
            const bodyText = await statusResponse.text().catch(() => '');
            const recorded = recordPollFailure(pollFailures, {
              kind: 'http',
              status: statusResponse.status,
              body: bodyText,
            });
            pollFailures = recorded.state;
            if (recorded.stop) break;
          }
        } catch (pollError) {
          // ⛔⛔ THIS CATCH USED TO LOG AND NOTHING ELSE — no count, no retained cause. Both review
          // legs found it independently: if EVERY attempt threw (network down, proxy corruption, or
          // a 2xx whose JSON will not parse), all 24 polls were consumed and the operator was told
          // "Research In Progress" — a claim that the run is merely slow, made by a loop that had
          // never once successfully asked. A THROWN ATTEMPT IS A FAILED ATTEMPT AND MUST COUNT AS
          // ONE; logging is for the developer, and the operator cannot read the console.
          console.warn('[CategoryResearch] Poll transport error:', pollError);
          const recorded = recordPollFailure(pollFailures, {
            kind: 'transport',
            message: pollError instanceof Error ? pollError.message : String(pollError),
          });
          pollFailures = recorded.state;
          if (recorded.stop) break;
        }
        if (!body) continue;

        // ⛔⛔ A 200 CARRYING A NEGATIVE ANSWER. This route returns { success: true, data: null }
        // when no row exists for (sector, path) — which is exactly what a refresh that died before
        // persisting the category leaves behind. It used to reach classifyPollResult as null and
        // come back 'continue', AND it reset the failure counter on the way, so 24 polls of "there
        // is nothing here" ended on "Research In Progress". The request succeeded, so no failure
        // counter fired; the value was well-formed, so no exhaustive switch fired. ⇒ Counted here
        // as its own failure kind instead. It is deliberately RETRYABLE rather than fatal on the
        // first sighting: if the row is merely created a moment later, the next poll sees it and
        // resets — so this is correct whether the row appears early or late, without having to bet
        // on which. Only a sustained absence is surfaced.
        if (!body.data) {
          const recorded = recordPollFailure(pollFailures, { kind: 'absent' });
          pollFailures = recorded.state;
          if (recorded.stop) break;
          continue;
        }
        // A real row reached us: any earlier absence or blip was transient.
        pollFailures = NO_POLL_FAILURES;
        const outcome = classifyPollResult(body.data);

        if (outcome.kind === 'continue') {
          console.log(`[CategoryResearch] Status not yet terminal, continuing to poll...`);
          continue;
        }

        // Terminal: handled and reported directly, never thrown into the transport catch above.
        // ⛔ This site previously ended in a catch-all `else` that reported "Research Complete",
        // which would have absorbed 'unknown' as a clean success once that kind was added.
        toast(toastForOutcome(outcome, `${categoryName} research finished successfully.`));
        await loadData();
        setTriggeringResearch(null);
        return;
      }

      // ⛔ "Ran out of attempts" and "never got an answer" are different outcomes and only one of
      // them is progress. A retained lastPollFailure means the most recent attempt(s) never reached
      // a terminal answer, so this must not claim the run is merely slow. Decided by a pure
      // function so the distinction is behaviourally testable — a source-string test cannot see a
      // path the mechanism never reaches, which is exactly how this survived two review rounds.
      toast(pollExhaustionToast(pollFailures.lastFailure, categoryName));

    } catch (error: any) {
      toast({
        title: 'Research Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setTriggeringResearch(null);
    }
  };

  // GPT Taxonomy browser functions
  const loadGptTaxonomy = async (parentId?: number) => {
    if (!sectorId) return;

    setGptLoading(true);
    try {
      const params = new URLSearchParams();
      if (parentId) params.append('parentId', String(parentId));

      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/taxonomy${params.toString() ? '?' + params.toString() : ''}`
      );
      if (response.ok) {
        const data = await response.json();
        setGptTaxonomy(data.taxonomy || []);
      }
    } catch (error) {
      console.error('Error loading GPT taxonomy:', error);
      toast({
        title: 'Error',
        description: 'Failed to load category taxonomy',
        variant: 'destructive',
      });
    } finally {
      setGptLoading(false);
    }
  };

  const handleGptCategoryClick = async (category: any) => {
    // Navigate into this category
    setGptBreadcrumb([...gptBreadcrumb, category]);
    await loadGptTaxonomy(category.id);
  };

  const handleGptBreadcrumbClick = async (index: number) => {
    if (index === -1) {
      // Root level
      setGptBreadcrumb([]);
      await loadGptTaxonomy();
    } else {
      const targetCategory = gptBreadcrumb[index];
      setGptBreadcrumb(gptBreadcrumb.slice(0, index + 1));
      await loadGptTaxonomy(targetCategory.id);
    }
  };

  /**
   * ⛔ THE INTERMEDIATE HOP IS GONE ON PURPOSE. This used to call handleTriggerResearch WITHOUT a
   * sector, so the no-confirm path re-resolved and the EXISTENCE CHECK AND THE WRITE ended up based
   * on two different resolver calls — the exact class of defect this PR spent five rounds closing,
   * surviving as a "minor" only because that path fires when the row reads unresearched.
   * ⇒ Callers pass the sector they already resolved. A hop that can silently drop it is not worth
   * the one line it saved.
   */
  const handleResearchGptCategory = async (category: any, resolvedSectorId?: number) => {
    await handleTriggerResearch(category.category_path, resolvedSectorId);
  };

  /**
   * ⛔ THE SAME PREDICATE THE BADGE USES. It was inline in the render, so the guard below could have
   * been written to disagree with the label the operator is reading — and the label IS the warning
   * on this control. One definition means a row that shows "Researched" always confirms.
   * ⚠️ It is a loose match (leaf name, or any path ending in it) and can therefore report a row as
   * researched that is a different category with the same leaf. That errs toward confirming, which
   * is the safe direction here; it is not accurate enough to base a WRITE on, and nothing does.
   */
  const gptCategoryIsResearched = (category: any) => categories.some(
    (c) => c.categoryName === category.category_name ||
           c.categoryPath.endsWith(category.category_name)
  );

  /**
   * ⛔⛔ THE EIGHTH CALLER OF THE REBUILD ENDPOINT, AND THE ONE THAT DOES NOT LOOK LIKE A BUTTON.
   * On an already-researched GPT taxonomy row this control renders as "Researched" behind a GREEN
   * CHECK — it reads as a STATUS BADGE, not an action. Clicking it went straight to
   * handleTriggerResearch, which POSTs the same full-rebuild /refresh as Re-Research All. So the
   * one control in this page that looks like a confirmation that everything is fine was the one
   * that destroyed it.
   * ⇒ Researched rows resolve their sector and confirm, exactly like the catalog table. A
   * never-researched row is not destructive and still runs straight through.
   */
  /**
   * ⛔ CONFIRM UNLESS WE KNOW THERE IS NOTHING TO LOSE. The badge predicate alone reads FALSE while
   * the authoritative list is still loading, which would send the most deceptive control on the
   * page straight through during exactly the window it is most likely to be clicked. Either signal
   * saying yes — the badge's loose name match or the exact stored path — confirms.
   */
  const gptRowNeedsConfirm = (category: any, targetSectorId: number) =>
    researchExistsInSector(category.category_path, targetSectorId) !== false ||
    gptCategoryIsResearched(category);

  const startResearchGptCategory = async (category: any) => {
    const fullPath = category.category_path;
    // ⛔⛔ RESOLVE FIRST, THEN ASK ABOUT THE TARGET. Same ordering bug as the catalog row: the
    // existence check answered about the VIEWED sector while the write resolved elsewhere, so a
    // row unresearched here but researched in the target rebuilt silently — on the control that
    // renders as a green-check status badge, where it is least likely to be noticed.
    setResolvingTriggerPath(fullPath);
    try {
      const resolved = await resolveTriggerSectorId(fullPath);
      if (resolved) {
        if (!gptRowNeedsConfirm(category, resolved)) {
          // ⛔ THE SECTOR WE JUST CHECKED, HANDED STRAIGHT THROUGH. Re-resolving here would let the
          // existence check and the write disagree.
          handleResearchGptCategory(category, resolved);
          return;
        }
        setPendingTrigger({ path: fullPath, sectorId: resolved });
      } else {
        toast({
          title: 'Cannot research this category yet',
          description: `Could not work out which sector "${fullPath}" belongs to, and this site has more than one. Nothing was changed.`,
          variant: 'destructive',
        });
      }
    } finally {
      setResolvingTriggerPath(null);
    }
  };

  const handleRegenerateAttributes = async (categoryId: string, forceRegenerate: boolean) => {
    if (!sectorId) return;

    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/categories/${categoryId}/research-attributes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ forceRegenerate }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Attributes Generated',
          description: data.message || 'Canonical attributes generated successfully',
        });
        // Reload technical research status
        await loadResearchStatus(categoryId);
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate attributes');
      }
    } catch (error: any) {
      toast({
        title: 'Generation Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleResearchIntelligence = async (categoryPath: string) => {
    openCatalogResearch(categoryPath, ['intelligence']);
  };

  // Load extraction hints for the dialog
  const loadExtractionHints = async () => {
    // ⛔⛔ INVALIDATE AND CLEAR ABOVE THE GUARDS, NOT BELOW THEM. An early return is still an open
    // dialog: the tile opens the dialog and THEN calls this, so a guard that returns before
    // clearing leaves the previous site's hint TEXT rendered under a "Select a site" toast — and
    // leaves any in-flight request's generation current, so it can still commit afterwards.
    // (codex-reviewer, round 5 of this class on this PR. The boundary has moved inward every
    // round: function → loader → viewer → the failure branch of a guarded viewer → the branch
    // that returns BEFORE the guard. Each round the fix was correct and the next path was the one
    // the fix could not reach.)
    const generation = ++viewerGenRef.current.hints;
    const isStale = () => viewerGenRef.current.hints !== generation;
    // ⛔ CLEARED BEFORE THE REQUEST, not after it resolves — a 403 for site B must not leave site
    // A's hint TEXT on screen. This dialog renders content, not a count.
    setExtractionHints([]);

    if (!sectorId || !selectedCategory?.id) return;
    // ⛔ THE SITE IS PART OF THE QUESTION, NOT A REFINEMENT OF IT. category_spec_cache is keyed by
    // site_id; without one this dialog used to render every site's hints for the category — 263
    // rows from 19 sites for one measured path. The API now refuses rather than guessing, so say
    // why instead of showing an empty list the operator would read as "no hints researched".
    if (!selectedSite?.id) {
      toast({
        title: 'Select a site first',
        description: 'Extraction hints are researched and stored per site, so there is nothing to show until one is selected.',
      });
      return;
    }

    setExtractionHintsLoading(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/categories/${selectedCategory.id}/extraction-hints?siteId=${selectedSite.id}`
      );
      if (isStale()) return;
      if (response.ok) {
        const data = await response.json();
        if (isStale()) return;
        setExtractionHints(data.data?.hints || []);
      } else {
        // ⛔ NOT SWALLOWED. A non-ok response used to leave the previous category's hints on
        // screen with no indication anything had failed.
        // ⛔ `.catch(() => '')` AND THE RECHECK — the other three non-2xx branches in this file both
        // do this and the hints one did neither (codex-reviewer-2, minor at 2a5a6cc6f; the `.catch`
        // is unreported and is the same asymmetry one step over).
        // · Without the catch, a `response.text()` rejection falls to the OUTER catch, which logs a
        //   generic "Error loading extraction hints" and LOSES the status code — the diagnostic the
        //   branch exists to produce.
        // · Without the recheck, `await response.text()` is a suspension point like any other: if
        //   the scope changes while the body is being read, a superseded request toasts an error
        //   about a selection nobody is looking at.
        const detail = await response.text().catch(() => '');
        console.error('Extraction hints request failed:', response.status, detail);
        if (isStale()) return;
        toast({
          title: 'Failed to load extraction hints',
          description: `The server returned ${response.status}.`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error loading extraction hints:', error);
      // ⛔ THE CATCH IS GATED TOO. A superseded request rejecting after a newer one started would
      // otherwise toast a failure about a selection nobody is looking at. (codex-reviewer, minor —
      // and a minor is not a minor finding: this is the same axis offering-ops worked through on
      // #5552, arriving in the paths I had just guarded on the success side only.)
      if (isStale()) return;
      toast({
        title: 'Error',
        description: 'Failed to load extraction hints',
        variant: 'destructive',
      });
    } finally {
      // ⛔ AND THE SPINNER. An older request's finally would clear the NEWER request's spinner,
      // reporting "loaded" while the current fetch is still genuinely in flight. The current call
      // always reaches its own finally, so the spinner is still guaranteed to clear.
      if (!isStale()) setExtractionHintsLoading(false);
    }
  };

  // Open extraction hints dialog
  const handleOpenExtractionHintsDialog = async () => {
    setExtractionHintsDialogOpen(true);
    await loadExtractionHints();
  };

  // Reuse the catalog submission identity, sector resolution and job polling.
  const handleResearchExtractionHints = async () => {
    if (selectedCategory) openCatalogResearch(selectedCategory.categoryPath, ['extraction_hints']);
  };

  const handleResearchPerceivedBenefits = async (categoryKey: string, forceRegenerate: boolean = false) => {
    if (!sectorId) return;
    // B2 (kexnycgswp): the key this write carries is decided in ONE place, and a bare leaf is refused here,
    // before any request — the UI does not lean on the backend to stop it.
    const writeKey = categoryWriteKey(categoryKey, catalogueRoots);
    if (!writeKey.ok) {
      toast({ title: 'Not researched: the category key is not a full path', description: writeKey.reason, variant: 'destructive' });
      return;
    }

    try {
      const response = await authFetch(
        `${API_URL}/category-research/${sectorId}/research`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // categoryName carries the full path, and options.categoryPath lets the service resolve BY PATH
          // (CategoryPerceptionResearchService.resolveCanonicalCategoryPath) instead of by name.
          body: JSON.stringify({ categoryName: writeKey.key, options: { categoryPath: writeKey.key, forceRegenerate }, forceRegenerate }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Perceived Benefits Researched',
          description: data.message || `Research completed for ${writeKey.key}`,
        });
        // Reload technical research status
        if (selectedCategory) {
          await loadResearchStatus(selectedCategory.id);
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to research perceived benefits');
      }
    } catch (error: any) {
      toast({
        title: 'Research Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleResearchAttributeMappings = async (forceRegenerate: boolean = false) => {
    if (!sectorId) return;

    try {
      const response = await authFetch(
        `${API_URL}/mappings/populate/${sectorId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            skipBenefitAttribute: false,
            skipValueRanges: true,
            skipPersonaBenefit: false,
            skipAttributeCategory: true,
            forceRegenerate
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        // ⛔ THE THIRD CALLER OF THIS ENDPOINT. It rendered "Attribute Mappings Generated" on any
        // HTTP 200, ignoring the body — so a run that wrote nothing still showed as generated.
        // `success` is derived server-side from the real created/error counts; read it.
        toast({
          title: data.success ? 'Attribute Mappings Generated' : 'No mappings were written',
          description: data.message || `Generated persona-benefit mappings for sector ${sectorId}`,
          ...(data.success ? {} : { variant: 'destructive' as const }),
        });
        // Reload technical research status
        if (selectedCategory) {
          await loadResearchStatus(selectedCategory.id);
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate attribute mappings');
      }
    } catch (error: any) {
      toast({
        title: 'Generation Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  // Load canonical attributes for the dialog
  const loadCanonicalAttributes = async () => {
    // ⛔ SAME SHAPE AS loadExtractionHints, AND NOBODY REPORTED THIS ONE. `handleOpenCanonicalDialog`
    // opens the dialog before awaiting this, so returning on a missing sector/category leaves the
    // PREVIOUS category's canonical attributes on screen inside an open dialog.
    const generation = ++viewerGenRef.current.canonical;
    const isStale = () => viewerGenRef.current.canonical !== generation;
    setCanonicalAttributes([]);
    setCanonicalUnavailableReason(null);

    if (!sectorId || !technicalResearch?.categoryPath) return;

    setCanonicalLoading(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${technicalResearch.sectorId || sectorId}/canonical-attributes?category=${encodeURIComponent(technicalResearch.categoryPath)}${selectedSite ? `&siteId=${encodeURIComponent(selectedSite.id)}` : ''}`
      );
      if (isStale()) return;
      if (response.ok) {
        const data = await response.json();
        if (isStale()) return;
        setCanonicalAttributes(data.data?.attributes || []);
      } else {
        // ⛔ NOT SWALLOWED — this branch did not exist, so a failure left the previous category's
        // attributes rendered with no indication anything had gone wrong.
        const detail = await response.text();
        if (!isStale()) {
          let message = detail;
          try { const parsed = JSON.parse(detail); message = parsed.details || parsed.error || detail; } catch { /* Non-JSON server error is shown verbatim. */ }
          setCanonicalUnavailableReason(message);
        }
        console.error('Canonical attributes request failed:', response.status, detail);
        if (isStale()) return;
        toast({
          title: 'Could not load canonical attributes',
          description: `The server returned ${response.status}. The list is cleared rather than left showing another category's attributes.`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error loading canonical attributes:', error);
      // ⛔ THE CATCH IS GATED TOO. A superseded request rejecting after a newer one started would
      // otherwise toast a failure about a selection nobody is looking at. (codex-reviewer, minor —
      // and a minor is not a minor finding: this is the same axis offering-ops worked through on
      // #5552, arriving in the paths I had just guarded on the success side only.)
      if (isStale()) return;
      toast({
        title: 'Error',
        description: 'Failed to load canonical attributes',
        variant: 'destructive',
      });
    } finally {
      // ⛔ AND THE SPINNER. An older request's finally would clear the NEWER request's spinner,
      // reporting "loaded" while the current fetch is still genuinely in flight. The current call
      // always reaches its own finally, so the spinner is still guaranteed to clear.
      if (!isStale()) setCanonicalLoading(false);
    }
  };

  // Open canonical attributes dialog
  const handleOpenCanonicalDialog = async () => {
    setCanonicalDialogOpen(true);
    await loadCanonicalAttributes();
  };

  // Load attribute intelligence for the dialog
  const loadAttributeIntelligence = async () => {
    // ⛔⛔ THE WORST OF THE THREE, AND ALSO UNREPORTED: this guard tests `selectedSite?.id`, so the
    // path that returns without clearing is exactly the DESELECTED-SITE path — site A's attribute
    // intelligence stays rendered in an open dialog after the site it belongs to is gone. Clearing
    // above the guard makes the no-site case show nothing rather than someone else's research.
    const generation = ++viewerGenRef.current.intelligence;
    const isStale = () => viewerGenRef.current.intelligence !== generation;
    // ⛔ This dialog is SITE-scoped in its URL, so a stale commit here is cross-site by definition.
    setAttributeIntelligence([]);

    if (!selectedSite?.id || !technicalResearch?.categoryName) return;

    setIntelligenceLoading(true);
    try {
      const response = await authFetch(
        // B2: query by the selected category's FULL PATH. The read direction is tolerant (the stored
        // scope_category_path must be a SUBSTRING of the queried one), so leaf-keyed rows written before
        // this change still match; the status row's categoryName is the last segment and is only a fallback.
        `${API_URL}/category-research/${sectorId}/attribute-intelligence/${selectedSite.id}?categoryName=${encodeURIComponent(selectedCategory?.categoryPath || technicalResearch.categoryName)}`
      );
      if (isStale()) return;
      if (response.ok) {
        const data = await response.json();
        if (isStale()) return;
        setAttributeIntelligence(data.intelligence || []);
      } else {
        const detail = await response.text().catch(() => '');
        console.error('Attribute intelligence request failed:', response.status, detail);
        if (isStale()) return;
        toast({
          title: 'Could not load attribute intelligence',
          description: `The server returned ${response.status}. The list is cleared rather than left showing another site's research.`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error loading attribute intelligence:', error);
      // ⛔ THE CATCH IS GATED TOO. A superseded request rejecting after a newer one started would
      // otherwise toast a failure about a selection nobody is looking at. (codex-reviewer, minor —
      // and a minor is not a minor finding: this is the same axis offering-ops worked through on
      // #5552, arriving in the paths I had just guarded on the success side only.)
      if (isStale()) return;
      toast({
        title: 'Error',
        description: 'Failed to load attribute intelligence',
        variant: 'destructive',
      });
    } finally {
      // ⛔ AND THE SPINNER. An older request's finally would clear the NEWER request's spinner,
      // reporting "loaded" while the current fetch is still genuinely in flight. The current call
      // always reaches its own finally, so the spinner is still guaranteed to clear.
      if (!isStale()) setIntelligenceLoading(false);
    }
  };

  // Open attribute intelligence dialog
  const handleOpenIntelligenceDialog = async () => {
    setIntelligenceDialogOpen(true);
    await loadAttributeIntelligence();
  };

  // Save edited attribute intelligence
  const handleSaveIntelligence = async () => {
    if (!editingIntelligence) return;

    setSavingIntelligence(true);
    try {
      const response = await authFetch(
        `/api/domain-knowledge/${editingIntelligence.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            research_findings: editingIntelligence.research_findings,
            knowledge_text: editingIntelligence.knowledge_text,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save');
      }

      // Update local state
      setAttributeIntelligence(prev =>
        prev.map(intel =>
          intel.id === editingIntelligence.id ? editingIntelligence : intel
        )
      );

      setEditingIntelligence(null);
      toast({
        title: 'Saved',
        description: 'Attribute intelligence updated successfully',
      });
    } catch (error) {
      console.error('Error saving intelligence:', error);
      toast({
        title: 'Error',
        description: 'Failed to save attribute intelligence',
        variant: 'destructive',
      });
    } finally {
      setSavingIntelligence(false);
    }
  };

  // Save edited attribute
  const handleSaveAttribute = async () => {
    if (!sectorId || !editingAttribute) return;

    setSavingAttribute(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/canonical-attributes/${editingAttribute.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attribute_name: editingAttribute.attribute_name,
            canonical_name: editingAttribute.canonical_name,
            data_type: editingAttribute.data_type === 'number' ? 'numeric' : editingAttribute.data_type,
            unit: editingAttribute.unit,
            typical_min: editingAttribute.typical_min,
            typical_max: editingAttribute.typical_max,
            enum_values: editingAttribute.enum_values,
            description: editingAttribute.description,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (canonicalAttributes.some(attr => attr.id === editingAttribute.id && attr.shared_definition)) {
          setCanonicalAttributes([]);
          setCanonicalUnavailableReason('Shared definition saved. This category research needs refresh before its definitions can be used.');
        } else {
          setCanonicalAttributes(prev => prev.map(attr => attr.id === editingAttribute.id ? data.data : attr));
        }
        setEditingAttribute(null);
        toast({
          title: 'Attribute Updated',
          description: 'Canonical attribute saved successfully',
        });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save attribute');
      }
    } catch (error: any) {
      toast({
        title: 'Save Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSavingAttribute(false);
    }
  };

  // Create new attribute.
  //
  // `confirmNearMiss` is the operator's explicit answer to the server's near-miss advisory
  // (task-po9ac21icv fix (3)). It is NEVER defaulted to true and never set programmatically from
  // the error itself — that would auto-clear the advisory and make it decorative. It is true only
  // on the retry launched by the confirm dialog's button.
  const submitCreateAttribute = async (confirmNearMiss: boolean) => {
    if (!sectorId || !technicalResearch?.categoryPath) return;

    if (!newAttribute.attribute_name || !newAttribute.canonical_name) {
      toast({
        title: 'Validation Error',
        description: 'Attribute name and canonical name are required',
        variant: 'destructive',
      });
      return;
    }

    setSavingAttribute(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/canonical-attributes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_category: technicalResearch.categoryPath,
            attribute_name: newAttribute.attribute_name,
            canonical_name: newAttribute.canonical_name,
            data_type: newAttribute.data_type || 'string',
            unit: newAttribute.unit,
            typical_min: newAttribute.typical_min,
            typical_max: newAttribute.typical_max,
            enum_values: newAttribute.enum_values,
            description: newAttribute.description,
            ...(confirmNearMiss ? { confirm_near_miss: true } : {}),
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCanonicalAttributes(prev => [...prev, data.data]);
        setShowNewAttributeForm(false);
        setNewAttribute({ data_type: 'string', enum_values: [] });
        setNearMissConfirm(null);
        toast({
          title: 'Attribute Created',
          description: 'New canonical attribute created successfully',
        });
      } else {
        const error = await response.json();
        const decision = decideNearMissResponse(response.status, error, confirmNearMiss);

        if (decision.kind === 'confirm') {
          setNearMissConfirm({
            attributeName: String(newAttribute.attribute_name),
            productCategory: technicalResearch.categoryPath,
            nearMisses: decision.nearMisses,
          });
          return;
        }

        throw new Error(decision.message);
      }
    } catch (error: any) {
      toast({
        title: 'Create Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSavingAttribute(false);
    }
  };

  const handleCreateAttribute = () => submitCreateAttribute(false);

  // Delete attribute
  const handleDeleteAttribute = async (id: string) => {
    if (!sectorId) return;

    setDeletingAttributeId(id);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/canonical-attributes/${id}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        setCanonicalAttributes(prev => prev.filter(attr => attr.id !== id));
        toast({
          title: 'Attribute Deleted',
          description: 'Canonical attribute deleted successfully',
        });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete attribute');
      }
    } catch (error: any) {
      toast({
        title: 'Delete Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setDeletingAttributeId(null);
    }
  };

  // Add enum value to editing attribute
  const handleAddEnumValue = (value: string) => {
    if (!value.trim()) return;

    if (editingAttribute) {
      const currentValues = editingAttribute.enum_values || [];
      if (!currentValues.includes(value.trim())) {
        setEditingAttribute({
          ...editingAttribute,
          enum_values: [...currentValues, value.trim()],
        });
      }
    } else if (showNewAttributeForm) {
      const currentValues = newAttribute.enum_values || [];
      if (!currentValues.includes(value.trim())) {
        setNewAttribute({
          ...newAttribute,
          enum_values: [...currentValues, value.trim()],
        });
      }
    }
    setNewEnumValue('');
  };

  // Remove enum value from editing attribute
  const handleRemoveEnumValue = (value: string) => {
    if (editingAttribute) {
      setEditingAttribute({
        ...editingAttribute,
        enum_values: (editingAttribute.enum_values || []).filter(v => v !== value),
      });
    } else if (showNewAttributeForm) {
      setNewAttribute({
        ...newAttribute,
        enum_values: (newAttribute.enum_values || []).filter(v => v !== value),
      });
    }
  };

  // Fetch AI suggestions for attribute values
  const handleAskAI = async () => {
    if (!editingAttribute?.id || !sectorId) return;

    setLoadingAiSuggestions(true);
    setAiSuggestions(null);

    try {
      const response = await authFetch(
        `/api/sectors/${sectorId}/canonical-attributes/${editingAttribute.id}/suggest`,
        { method: 'POST' }
      );

      if (!response.ok) {
        throw new Error('Failed to get AI suggestions');
      }

      const data = await response.json();
      setAiSuggestions({
        unit: data.suggestions?.unit,
        typical_min: data.suggestions?.typical_min,
        typical_max: data.suggestions?.typical_max,
        description: data.suggestions?.description,
        enum_values: data.suggestions?.enum_values,
        reasoning: data.reasoning,
        sample_values_found: data.sample_values_found,
        based_on_research: data.based_on_research,
        research_title: data.research_title
      });

      toast({
        title: 'AI Suggestions Ready',
        description: 'Review the suggested values below. Click "Apply" to use them.',
      });
    } catch (error) {
      console.error('Error fetching AI suggestions:', error);
      toast({
        title: 'Failed to get suggestions',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setLoadingAiSuggestions(false);
    }
  };

  // Apply a single AI suggestion to the editing attribute
  const applyAiSuggestion = (field: string, value: unknown) => {
    if (!editingAttribute) return;
    setEditingAttribute({
      ...editingAttribute,
      [field]: value,
    });
    // Remove from suggestions after applying
    setAiSuggestions(prev => prev ? { ...prev, [field]: undefined } : null);
    toast({
      title: 'Suggestion Applied',
      description: `${field.replace('_', ' ')} has been updated`,
    });
  };

  // Apply all AI suggestions at once
  const applyAllAiSuggestions = () => {
    if (!editingAttribute || !aiSuggestions) return;

    const updates: Partial<CanonicalAttribute> = {};
    if (aiSuggestions.unit !== undefined && aiSuggestions.unit !== null) updates.unit = aiSuggestions.unit;
    if (aiSuggestions.typical_min !== undefined && aiSuggestions.typical_min !== null) updates.typical_min = aiSuggestions.typical_min;
    if (aiSuggestions.typical_max !== undefined && aiSuggestions.typical_max !== null) updates.typical_max = aiSuggestions.typical_max;
    if (aiSuggestions.description !== undefined && aiSuggestions.description !== null) updates.description = aiSuggestions.description;
    if (aiSuggestions.enum_values !== undefined && aiSuggestions.enum_values !== null) updates.enum_values = aiSuggestions.enum_values;

    setEditingAttribute({ ...editingAttribute, ...updates });
    setAiSuggestions(null);
    toast({
      title: 'All Suggestions Applied',
      description: 'Review the values and click Save to confirm',
    });
  };

  // Dismiss AI suggestions
  const dismissAiSuggestions = () => {
    setAiSuggestions(null);
  };

  // Batch research functions
  // The one resolved sector held by the frozen confirmation request. Mixed-sector
  // selections require separate submissions; the viewed/detail sector is not a substitute.
  const batchWriteSectorId = pendingBatchSubmission?.sectorId;
  // Display, confirmation and request name the same complete catalog path.
  const batchTargetPath = (cat: AvailableCategory) => cat.categoryPath;
  const selectedBatchTargets = [...new Set(availableCategories
    .filter(c => selectedBatchCategories.includes(rowKey(c))).map(batchTargetPath))];

  const selectedBatchOverlap = researchJobsOverlap(researchJobs,Number(sectorId),selectedBatchTargets);

  const validateResearchReceipt = (job: any) => {
    if (!job || typeof job.id !== 'string' || !job.id || !['pending','running','paused','cancelled','completed','failed'].includes(job.status)
        || !Number.isInteger(Number(job.sectorId)) || Number(job.sectorId)<=0) {
      throw new Error('The server did not return a valid research job receipt. Check request status before trying again.');
    }
    if(job.handoff!=null && (job.handoff.legacyRetired!==true || !Array.isArray(job.handoff.targets) || !job.handoff.targets.length
      || job.handoff.targets.some((target:any)=>!target || !Number.isInteger(target.sectorId) || target.sectorId<=0
        || typeof target.categoryPath!=='string' || !target.categoryPath.trim() || target.categoryPath.trim()!==target.categoryPath)))
      throw new Error('The research handoff receipt is invalid. Check request status before starting research.');
    if((job.runner==='v2' || job.targetMode==='catalog_full_path') && (!Array.isArray(job.categories) || !job.categories.length
      || job.categories.some((category:any)=>{const path=typeof category==='string'?category:category?.categoryPath;return typeof path!=='string'||!path.trim()||path.trim()!==path;})))
      throw new Error('The catalog research receipt has no valid category ownership. Check its saved request before starting research.');
    if(job.runner==='v2' && (!Number.isInteger(job.totalCategories) || job.totalCategories!==job.categories.length
      || new Set(job.categories.map((category:any)=>typeof category==='string'?category:category.categoryPath)).size!==job.totalCategories
      || (job.refused!==null && (!Array.isArray(job.refused) || job.refused.some((target:any)=>!target || typeof target.categoryPath!=='string' || !target.categoryPath.trim()
        || typeof target.error!=='string' || !target.error.trim())))))
      throw new Error('The research admission receipt has invalid admitted categories or refusals. Check its saved request before starting research.');
  };

  const adoptResearchReceipt = (job: any, submission?: CatalogSubmission) => {
    validateResearchReceipt(job);
    // Displaying another receipt cannot resolve a saved admission. Read its
    // durable identity at adoption time, including after an asynchronous fetch.
    const pendingIdentity=readCatalogSubmission(submissionKey);
    const preservesOtherAdmission=!!pendingIdentity && pendingIdentity.requestId!==submission?.requestId;
    batchReceiptVersionRef.current++;
    batchJobIdRef.current = job.id;
    const unresolvedTerminal=!researchJobRetired(job) && ['completed','failed','cancelled'].includes(job.status)
      && Object.values(job.progressDetails || {}).some((entry:any)=>Object.values(entry.steps || {}).some(status=>status==='running'||status==='outcome_unknown'));
    const scoped=job.targetMode==='catalog_full_path' && Array.isArray(job.categories) && job.categories.length>0;
    if(!preservesOtherAdmission) {
      setUnacknowledgedSubmission(unresolvedTerminal && !scoped && submission ? submission : null);
      setResearchRecoveryBlocked(unresolvedTerminal && !scoped);
    }
    if(unresolvedTerminal && !preservesOtherAdmission) setBatchResearchError(scoped ? 'This saved job has an unknown outcome. Its categories remain blocked; other categories can be researched.' : 'The receipt still has work with an unknown outcome. Check request status before starting new research.');
    const receipt = {
      id:job.id, sectorId:Number(job.sectorId),runner:job.runner,refused:job.refused,
      targetMode:job.targetMode || submission?.targetMode,
      handoff:job.handoff || null,
      continuationRepair:job.continuationRepair || null,
      siteId:submission?.siteId || job.siteId,
      siteName:submission?.siteName || (job.siteId === selectedSite?.id ? selectedSite.name : `Site ${job.siteId}`),
      targets:Object.fromEntries((job.categories || submission?.categories || []).map((c: any) => [c.categoryPath || c,c.categoryPath || c])),
      status:job.status, totalCategories:job.totalCategories,
      completedCategories:job.completedCategories || 0, failedCategories:job.failedCategories || 0,
      currentStep:job.currentStep || null, progressPercent:job.progressPercent || 0,
      progressDetails:job.progressDetails || {}, lastError:job.lastError || null,
    };
    setKnownResearchJobs(previous=>[...previous.filter(item=>item.id!==receipt.id),receipt]);
    setBatchResearchJob(receipt);
    setBatchResearchPolling(!researchJobRetired(job) && ['pending','running'].includes(job.status));
  };

  const inspectSavedResearch = async (saved: NonNullable<typeof batchResearchJob>) => {
    const preparationKey=batchPreparationKeyRef.current;
    const receiptVersion=batchReceiptVersionRef.current;
    try {
      const response=await authFetch(`${API_URL}/category-characteristics/${saved.sectorId}/batch-research/${encodeURIComponent(saved.id)}`);
      const data=await response.json();
      if(!response.ok)throw new Error(data.error || `Saved research unavailable: HTTP ${response.status}`);
      validateResearchReceipt(data.job);
      if(data.job.id!==saved.id || data.job.siteId!==saved.siteId || Number(data.job.sectorId)!==saved.sectorId)
        throw new Error('Saved research returned a different job, site or sector.');
      if(preparationKey!==batchPreparationKeyRef.current || receiptVersion!==batchReceiptVersionRef.current || startingBatchRef.current)return;
      adoptResearchReceipt(data.job);
    } catch(error:any) {setBatchResearchError(`Saved research could not be inspected: ${error.message}`);}
  };

  const recoverSubmission = async (submission: CatalogSubmission) => {
    const response = await authFetch(`${API_URL}/category-characteristics/${submission.sectorId}/batch-research?requestId=${encodeURIComponent(submission.requestId)}&siteId=${encodeURIComponent(submission.siteId)}&limit=1`);
    if (!response.ok) {
      const error=await response.json();
      throw new Error(error.error || `Research receipt unavailable: HTTP ${response.status}`);
    }
    const data=await response.json();
    if (!Array.isArray(data.jobs)) throw new Error('Research receipt response did not contain a job list.');
    return data.jobs[0] || null;
  };

  const submitResearch = async (submission: CatalogSubmission) => {
    const retryingUncertainRequest=unacknowledgedSubmission?.requestId===submission.requestId;
    if (startingBatchRef.current || (!retryingUncertainRequest && (batchBlocksAll || researchJobsOverlap(researchJobs,submission.sectorId,submission.categories.map(c=>c.categoryPath))))) return;
    startingBatchRef.current=true;setStartingBatchResearch(true);setBatchResearchError(null);
    let identitySaved=false,definitivelyRejected=false,reconcileReceipt=false;
    try {
      // Save before sending: a timeout is not proof that admission failed.
      localStorage.setItem(submissionKey,JSON.stringify(submission));
      identitySaved=true;
      setUnacknowledgedSubmission(submission);
      const response=await authFetch(`${API_URL}/category-characteristics/${submission.sectorId}/batch-research`,{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({categories:submission.categories,siteId:submission.siteId,researchSteps:submission.researchSteps,
          reenrich:false,targetMode:'catalog_full_path',requestId:submission.requestId}),
      });
      const data=await response.json();
      if (!response.ok) {
        const message=data.error || data.message || `Research request failed: HTTP ${response.status}`;
        // An explicit first-attempt refusal is safe to correct. A later refusal
        // cannot establish the outcome of an earlier lost response for this ID.
        if (data.admission==='rejected' && !retryingUncertainRequest) {
          try {localStorage.removeItem(submissionKey);} catch(error: any) {
            throw new Error(`${message} The server refused this request, but its saved identity could not be cleared: ${error.message}`);
          }
          setUnacknowledgedSubmission(null);
          definitivelyRejected=true;
        }
        throw new Error(message + (Array.isArray(data.refused) ? data.refused.map((target:any)=>` ${target.categoryPath}: ${target.error}`).join(';') : '') + (Array.isArray(data.jobIds) ? ` Existing jobs: ${data.jobIds.join(', ')}.` : ''));
      }
      if (data.success!==true || typeof data.jobId!=='string' || !data.jobId || typeof data.status!=='string') {
        throw new Error('The server did not return a research job receipt. Check request status before trying again.');
      }
      adoptResearchReceipt({id:data.jobId,sectorId:submission.sectorId,siteId:submission.siteId,
        runner:data.runner,status:data.status,refused:data.refused,targetMode:data.targetMode,
        totalCategories:data.runner==='v2' ? data.totalCategories : data.totalCategories ?? submission.categories.length,
        categories:data.runner==='v2' ? data.categories : data.categories || submission.categories},submission);
      reconcileReceipt=['completed','failed','cancelled'].includes(data.status);
      if(reconcileReceipt) setResearchRecoveryBlocked(true);
      if (!['pending','running'].includes(data.status)) await pollBatchResearchStatus(data.jobId,submission.sectorId);
      toast({title:data.coalesced ? 'Existing research job found' : 'Research job accepted',description:`Job ${data.jobId}`});
    } catch(error: any) {
      setBatchResearchError(definitivelyRejected
        ? `${error.message} This research was not admitted. Correct the selection or request and submit again.`
        : identitySaved
        ? `${error.message} The request identity is saved; check its status or retry this same request.`
        : `${error.message} The request identity could not be saved; research was not submitted.`);
    } finally {
      startingBatchRef.current=false;setStartingBatchResearch(false);
      // A same-identity retry may return a terminal receipt without changing
      // batchJobActive. Reconcile only after the submission fence is released.
      if(reconcileReceipt) setResearchRestoreRetry(value=>value+1);
    }
  };

  const handleStartBatchResearch = async (destructive: boolean) => {
    if (!sectorId || !selectedSite?.id || !selectedBatchCategories.length || unacknowledgedSubmission
        || researchRecoveryBlocked || batchBlocksAll || selectedBatchOverlap || startingBatchRef.current || preparingBatchRef.current) return;
    const chosen=availableCategories.filter(c=>selectedBatchCategories.includes(rowKey(c)));
    if (!chosen.length) {setBatchResearchError('The selected categories are no longer available. Select them again.');return;}
    const preparationKey=batchPreparationKeyRef.current;
    preparingBatchRef.current=true;setPreparingBatchResearch(true);setBatchResearchError(null);
    try {
      const resolved=await Promise.all(chosen.map(async category=>({category,
        sectorId:await resolveTriggerSectorId(category.categoryPath)})));
      if (batchPreparationKeyRef.current!==preparationKey) return;
      const unresolved=resolved.filter(target=>target.sectorId===null);
      if (unresolved.length) throw new Error(`Could not resolve a sector for: ${unresolved.map(t=>t.category.categoryPath).join(', ')}. Research was not submitted.`);
      const targetSectors=[...new Set(resolved.map(target=>target.sectorId!))];
      if (targetSectors.length!==1) throw new Error(`The selected categories belong to different sectors (${targetSectors.join(', ')}). Select categories from one sector and submit each sector separately. Research was not submitted.`);
      const submission: CatalogSubmission={sectorId:targetSectors[0],siteId:selectedSite.id,siteName:selectedSite.name,requestId:crypto.randomUUID(),
        categories:chosen.map(c=>({categoryPath:c.categoryPath,categoryName:c.categoryName})),
        researchSteps:[...batchResearchSteps],reenrich:false,targetMode:'catalog_full_path'};
      if(researchJobsOverlap(researchJobs,submission.sectorId,submission.categories.map(c=>c.categoryPath))) throw new Error('Research is already active or unresolved for a selected category. Inspect its saved job before starting overlapping work.');
      // The confirmation and eventual POST share this resolved, frozen request.
      const researched=submission.categories.map(c=>c.categoryPath)
        .filter(path=>researchExistsInSector(path,submission.sectorId)!==false);
      if (destructive && researched.length>0) {
        confirmedPreparationKeyRef.current=preparationKey;
        setPendingBatchSubmission(submission);setPendingBatchPaths(researched);
      } else await submitResearch(submission);
    } catch(error: any) {
      if (batchPreparationKeyRef.current===preparationKey) setBatchResearchError(error.message);
    } finally {preparingBatchRef.current=false;setPreparingBatchResearch(false);}
  };

  const openCatalogResearch = (path: string, steps = ['characteristics','perceived_benefits','canonical','intelligence','extraction_hints']) => {
    const row=availableCategories.find(c=>c.categoryPath===path);
    if (!row) {
      const message='This path is not in the current catalog. Reload the catalog before starting research.';
      setBatchResearchError(message);
      toast({title:'Research was not submitted',description:message,variant:'destructive'});
      return;
    }
    setSelectedBatchCategories([rowKey(row)]);
    setBatchResearchSteps(steps);
    setSelectedCategory(null);
    setBatchResearchDialogOpen(true);
  };

  const pollBatchResearchStatus = useCallback(async (jobId: string, jobSectorId: number) => {
    const version = batchReceiptVersionRef.current;
    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${jobSectorId}/batch-research/${jobId}`
      );

      if (response.ok) {
        const data = await response.json();
        if (batchJobIdRef.current !== jobId || batchReceiptVersionRef.current !== version) return;
        if (data.job?.id !== jobId) throw new Error('Research status returned a different job.');
        validateResearchReceipt({...data.job,sectorId:jobSectorId});
        setBatchResearchError(null);
        setBatchResearchJob(previous => previous?.id === jobId ? {
          ...previous,
          id: data.job.id,
          status: data.job.status,
          ...(data.job.runner==='v2' ? {runner:data.job.runner,refused:data.job.refused,targets:Object.fromEntries(data.job.categories.map((c:any)=>[c.categoryPath||c,c.categoryPath||c]))} : {}),
          continuationRepair:data.job.continuationRepair || null,
          handoff:data.job.handoff || null,
          totalCategories: data.job.totalCategories,
          completedCategories: data.job.completedCategories,
          failedCategories: data.job.failedCategories,
          currentStep: data.job.currentStep,
          progressPercent: data.job.progressPercent,
          progressDetails: data.job.progressDetails || {},
          lastError: data.job.lastError,
        } : previous);

        // Stop polling if job is complete or failed
        if (researchJobRetired(data.job) || ['completed', 'failed', 'cancelled', 'paused'].includes(data.job.status)) {
          setBatchResearchPolling(false);
          if (data.job.status === 'completed') {
            const gapCount=batchResearchGapCount(data.job);
            const partial=gapCount>0;
            toast({title:partial ? 'Research finished with partial results' : 'Research job finished',
              description:`${data.job.completedCategories} categories completed; ${gapCount} have gaps. Inspect each recorded step and target.`});
            loadData(); // Refresh the main data
          }
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || error.message || `Research status failed: HTTP ${response.status}`);
      }
    } catch (error: any) {
      console.error('Error polling batch research status:', error);
      if (batchJobIdRef.current === jobId && batchReceiptVersionRef.current === version) setBatchResearchError(error.message);
    }
  }, [loadData, toast]);

  // Poll for batch research status
  useEffect(() => {
    if (!batchResearchPolling || !batchResearchJob?.id) return;

    const interval = setInterval(() => {
      pollBatchResearchStatus(batchResearchJob.id, batchResearchJob.sectorId);
    }, 3000);

    return () => clearInterval(interval);
  }, [batchResearchPolling, batchResearchJob?.id, batchResearchJob?.sectorId, pollBatchResearchStatus]);

  const handleCancelBatchResearch = async () => {
    if (!batchResearchJob?.id || researchJobRetired(batchResearchJob)) return;

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${batchResearchJob.sectorId}/batch-research/${batchResearchJob.id}/cancel`,
        { method: 'POST' }
      );

      if (response.ok) {
        setBatchResearchPolling(false);
        batchReceiptVersionRef.current++;
        batchJobIdRef.current = null;
        setBatchResearchJob(previous => previous?.id === batchResearchJob.id ? { ...previous, status: 'cancelled' } : previous);
        toast({
          title: 'Job Cancelled',
          description: 'Batch research job has been cancelled',
        });
      } else {
        const error = await response.json();
        throw new Error(error.error || error.message || `Cancel research failed: HTTP ${response.status}`);
      }
    } catch (error: any) {
      setBatchResearchError(error.message);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const refreshCancelledReceipt = async () => {
    if (!batchResearchJob || batchResearchJob.status !== 'cancelled') return;
    const {id,sectorId:jobSectorId} = batchResearchJob;
    try {
      const response = await authFetch(`${API_URL}/category-characteristics/${jobSectorId}/batch-research/${id}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Research receipt unavailable: HTTP ${response.status}`);
      if (data.job?.id !== id || data.job.status !== 'cancelled') throw new Error('The server did not return the cancelled research receipt.');
      // Do not re-enable the old polling identity: a pre-cancel running read
      // must stay invalid while this explicit read reveals eventual outcomes.
      setBatchResearchJob(previous => previous?.id === id && previous.status === 'cancelled' ? {
        ...previous,progressDetails:data.job.progressDetails || {},lastError:data.job.lastError || null,
        completedCategories:data.job.completedCategories,failedCategories:data.job.failedCategories,
        currentStep:data.job.currentStep,progressPercent:data.job.progressPercent,
      } : previous);
      setBatchResearchError(null);
      // A terminal receipt can resolve uncertain work without changing job
      // status. Reconcile its saved identity and check for active work before
      // enabling another request; displaying the refreshed result is not enough.
      setResearchRecoveryBlocked(true);
      setResearchRestoreRetry(previous => previous + 1);
    } catch (error: any) {setBatchResearchError(error.message);}
  };

  const handleSelectAllCategories = () => {
    if (selectedBatchCategories.length === availableCategories.length) {
      setSelectedBatchCategories([]);
    } else {
      // ⛔ ROW KEYS, NOT PATHS. Two lines can share a path and mean different research rows.
      setSelectedBatchCategories(availableCategories.map(rowKey));
    }
  };

  const handleSelectUnresearchedCategories = () => {
    const unresearched = availableCategories
      .filter(c => !c.hasResearch)
      .map(rowKey);
    setSelectedBatchCategories(unresearched);
  };

  const toggleBatchCategory = (categoryPath: string) => {
    setSelectedBatchCategories(prev =>
      prev.includes(categoryPath)
        ? prev.filter(p => p !== categoryPath)
        : [...prev, categoryPath]
    );
  };

  const toggleBatchStep = (step: string) => {
    setBatchResearchSteps(prev =>
      prev.includes(step)
        ? prev.filter(s => s !== step)
        : [...prev, step]
    );
  };

  // Knowledge Chat Handlers
  const handleChatSend = async () => {
    const writeSectorId = getWriteSectorId();
    if (!chatMessage.trim() || !writeSectorId || !selectedCategory) return;

    setChatLoading(true);
    setChatParsedIntent(null);

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${writeSectorId}/category/${encodeURIComponent(selectedCategory.categoryPath)}/chat`,
        {
          method: 'POST',
          body: JSON.stringify({ message: chatMessage, execute: false }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to parse request');
      }

      if (!data.understood) {
        // Add to history as clarification needed
        setChatHistory(prev => [
          ...prev,
          { role: 'user', content: chatMessage },
          { role: 'assistant', content: data.clarificationNeeded || 'Could you please clarify your request?' },
        ]);
        setChatMessage('');
      } else {
        // Show parsed intent for confirmation
        setChatParsedIntent(data.parsed);
        setChatHistory(prev => [
          ...prev,
          { role: 'user', content: chatMessage },
        ]);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatExecute = async () => {
    const writeSectorId = getWriteSectorId();
    if (!chatParsedIntent || !writeSectorId || !selectedCategory) return;

    setChatExecuting(true);

    try {
      const response = await authFetch(
        `${API_URL}/category-characteristics/${writeSectorId}/category/${encodeURIComponent(selectedCategory.categoryPath)}/chat`,
        {
          method: 'POST',
          body: JSON.stringify({ message: chatMessage, execute: true }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to execute operation');
      }

      // ⛔ The outer `data.success` checked above means REQUEST HANDLED, not OPERATION WORKED — it
      // is returned unconditionally on the executed path. The operation's real verdict is in
      // `data.result`, and recovering it needs more than `result.success` (see the classifier).
      const outcome = classifyChatOperationOutcome(data.result);

      // ⚠️ The history entry and the toast are written separately, so they are both derived from
      // the SAME outcome here. Fixing only the toast would leave the history lying.
      setChatHistory(prev => [
        ...prev,
        {
          role: 'assistant',
          content: chatParsedIntent.confirmationMessage,
          result: outcome,
        },
      ]);

      toast({
        title: outcome.severity === 'success'
          ? 'Success'
          : outcome.severity === 'no_change' ? 'No change' : 'Operation failed',
        description: outcome.message,
        variant: outcome.severity === 'failed' ? 'destructive' : 'default',
      });

      // Refresh category data and update selected category
      const categoryPath = selectedCategory.categoryPath;
      await loadData();

      // Fetch fresh category data to update the dialog (from the correct sector)
      const categoryResponse = await authFetch(
        `${API_URL}/category-characteristics/${writeSectorId}/category/${encodeURIComponent(categoryPath)}`
      );
      if (categoryResponse.ok) {
        const categoryData = await categoryResponse.json();
        if (categoryData.data) {
          setSelectedCategory(categoryData.data);
        }
      }

      // Clear state
      setChatMessage('');
      setChatParsedIntent(null);

    } catch (error: any) {
      setChatHistory(prev => [
        ...prev,
        {
          role: 'assistant',
          content: chatParsedIntent.confirmationMessage,
          result: { success: false, severity: 'failed', message: error.message },
        },
      ]);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setChatExecuting(false);
    }
  };

  // Keep unresolved identities pinned; terminal receipts are history, so they
  // must not suppress discovery of the current site's active research controls.
  useEffect(() => {
    let cancelled=false;
    const observedJob=batchJobIdRef.current;
    const current=()=>!cancelled && !startingBatchRef.current && batchJobIdRef.current===observedJob;
    const historyKey=submissionKey+':history';
    const historyWarning=(error: unknown)=>{
      const detail=error instanceof Error ? error.message : String(error);
      console.warn('Research history is unavailable; server receipts are retained:', error);
      setResearchHistoryWarning(`Recent research history could not be saved or read: ${detail}. Complete receipts remain on the server. Pending request recovery is checked separately.`);
    };
    const saveHistory=(entries: ReturnType<typeof compactResearchHistory>)=>{
      try {localStorage.setItem(historyKey,JSON.stringify(entries));}
      catch(error) {
        historyWarning(error);
        if(error instanceof DOMException && error.name==='QuotaExceededError') {
          // Reclaim only this optional archive; never delete other storage or
          // an active/uncertain identity. Server receipts remain authoritative.
          try {localStorage.removeItem(historyKey);}
          catch(removalError) {historyWarning(removalError);}
        }
      }
    };
    const history=()=>{
      try {
        const raw=localStorage.getItem(historyKey);
        const entries=raw ? JSON.parse(raw) : [];
        if(!Array.isArray(entries)) throw new Error('Saved research history is unreadable.');
        const compact=compactResearchHistory(entries);
        if(raw && raw!==JSON.stringify(compact)) saveHistory(compact);
        return compact;
      } catch(error) {historyWarning(error);return [];}
    };
    const restore=async()=>{
      if (!sectorId || (batchJobActive && (batchResearchJob?.status!=='paused' || !researchJobHasCatalogScope(batchResearchJob))) || startingBatchRef.current) return;
      try {
        const saved=readCatalogSubmission(submissionKey);
        setResearchRecoveryBlocked(true);
        let job,submission: CatalogSubmission | undefined=saved || undefined;
        if (saved) {
          setUnacknowledgedSubmission(saved);
          job=await recoverSubmission(saved);
          if(!current()) return;
          if(!job) {
            setUnacknowledgedSubmission(saved);
            setBatchResearchError('The saved request has no visible receipt yet. Check its status or retry the same request identity.');
            return;
          }
          validateResearchReceipt(job);
          if(Number(job.sectorId)!==saved.sectorId || job.siteId!==saved.siteId) throw new Error('The saved request returned a receipt for a different site or sector.');
          const unresolved=Object.values(job.progressDetails || {}).some((entry:any)=>Object.values(entry.steps || {}).some(status=>status==='running'||status==='outcome_unknown'));
          if ((!['completed','failed','cancelled'].includes(job.status) || unresolved)
              && !(job.targetMode==='catalog_full_path' && Array.isArray(job.categories) && job.categories.length) && !researchJobRetired(job)) {
            adoptResearchReceipt(job,saved);
            return;
          }
          // A terminal receipt or an exact catalog owner resolves admission.
          // Its category ownership stays visible and fenced independently.
          // Archive only bounded pointers, never provider output or full requests.
          // Archival failure cannot turn a known terminal request into uncertainty.
          const entries=compactResearchHistory([...history(),{job}]);
          saveHistory(entries);
          localStorage.removeItem(submissionKey);
          setUnacknowledgedSubmission(null);
        }
        if(selectedSite?.id) {
          const response=await authFetch(`${API_URL}/category-characteristics/${sectorId}/batch-research?status=active&siteId=${encodeURIComponent(selectedSite.id)}&limit=100`);
          const data=await response.json();
          if(!current()) return;
          if(!response.ok) throw new Error(data.error || `Active research jobs unavailable: HTTP ${response.status}`);
          if(!Array.isArray(data.jobs)) throw new Error('Active research response did not contain a job list.');
          if(data.jobs.length===100) throw new Error('The active research list reached its limit. Research ownership must be fully checked before another request.');
          for(const active of data.jobs) {
            validateResearchReceipt(active);
            if(Number(active.sectorId)!==sectorId || active.siteId!==selectedSite.id) throw new Error('Active research returned a different site or sector.');
          }
          const ownedReceipts=job ? [job,...data.jobs.filter((active:any)=>active.id!==job.id)] : data.jobs;
          setKnownResearchJobs(previous=>[...previous.filter(item=>item.siteId!==selectedSite.id),...ownedReceipts.map((active:any)=>({
            ...active,siteName:selectedSite.name,sectorId:Number(active.sectorId),
            targets:Object.fromEntries((active.categories||[]).map((category:any)=>[category.categoryPath||category,category.categoryPath||category])),
            progressDetails:active.progressDetails||{},lastError:active.lastError||null,
          }))]);
          if(data.jobs[0]) {
            validateResearchReceipt(data.jobs[0]);
            if(Number(data.jobs[0].sectorId)!==sectorId || data.jobs[0].siteId!==selectedSite.id) throw new Error('Active research returned a different site or sector.');
            job=data.jobs[0];submission=undefined;
          }
          else if(!job) {
            const prior=history().slice().reverse().find((entry:any)=>entry?.job?.siteId===selectedSite.id && Number(entry.job.sectorId)===sectorId);
            if(prior) {
              // A history pointer is not a receipt. Rehydrate from the server
              // and validate scope before displaying it or adopting controls.
              try {
                const receiptResponse=await authFetch(`${API_URL}/category-characteristics/${prior.job.sectorId}/batch-research/${encodeURIComponent(prior.job.id)}`);
                if(!current()) return;
                const receipt=await receiptResponse.json();
                if(!current()) return;
                if(!receiptResponse.ok) throw new Error(receipt.error || `Archived receipt unavailable: HTTP ${receiptResponse.status}`);
                validateResearchReceipt(receipt.job);
                if(receipt.job.id!==prior.job.id || receipt.job.siteId!==selectedSite.id || Number(receipt.job.sectorId)!==sectorId)
                  throw new Error('Archived research returned a different receipt, site or sector.');
                job=receipt.job;
              } catch(error) {if(current())historyWarning(error);}
            }
          }
        }
        if(current()) {
          setResearchRecoveryBlocked(false);
          setBatchResearchError(null);
          if(job) adoptResearchReceipt(job,submission);
        }
      } catch(error: any) {if(current()) {setResearchRecoveryBlocked(true);setBatchResearchError(`Research recovery could not be checked: ${error.message}`);}}
    };
    restore();
    return ()=>{cancelled=true;};
  }, [sectorId,selectedSite?.id,batchJobActive,submissionKey,researchRestoreRetry]);

  // Filter canonical attributes by search
  const filteredCanonicalAttributes = canonicalAttributes.filter(attr => {
    if (!canonicalSearch.trim()) return true;
    const search = canonicalSearch.toLowerCase();
    return (
      attr.attribute_name.toLowerCase().includes(search) ||
      attr.canonical_name.toLowerCase().includes(search) ||
      (attr.description?.toLowerCase().includes(search))
    );
  });

  const filteredCategories = categories.filter(cat => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'stale') return cat.freshness.status === 'stale' || cat.freshness.status === 'aging';
    return cat.status === statusFilter;
  });

  const filteredAvailableCategories = availableCategories.filter(cat => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'researched') return cat.hasResearch;
    if (statusFilter === 'not_researched') return !cat.hasResearch;
    return true;
  });

  /**
   * ⛔⛔ THE RESEARCH ROW FOR A "Your Catalog" LINE, BY id — AND WHY IT IS A MAP BUILT HERE RATHER
   * THAN A per-row scan of the captured `categories` array.
   *
   * ⚠️ WORDING NOTE, AND IT IS THE SAME TRAP AS routes/category-characteristics.js:2505. This
   * comment may not spell the banned expression — the words "categories" and "find(" adjacent —
   * because CategoryResearch.writeSafety.test.ts scans the WHOLE FILE TEXT, comments included, and
   * my prose explaining why I do not use that form tripped the guard twice. The guard is
   * deliberately NOT relaxed to permit a comment: it is what keeps a destructive-refresh staleness
   * bug from returning, and "strip comments before scanning" is a change to a safety predicate that
   * deserves its own review rather than a drive-by inside an unrelated feature. Filed as a papercut.
   *
   * The ban is CORRECT: `await loadData()` does not update a value captured in an async closure, so
   * re-selecting from `categories` after a destructive rebuild put the PRE-REFRESH row back on
   * screen. The sanctioned form there is `const fresh = await loadData()` and then a lookup in
   * `fresh`.
   * ⇒ THIS IS NOT THAT, AND I DID NOT WEAKEN THE GUARD TO SAY SO. This lookup is RENDER-TIME
   * derived state: it is rebuilt from `categories` on every render, so a state update after a
   * reload re-renders and re-derives it. There is no captured closure to go stale. Narrowing the
   * assertion to "async handlers only" would have made the guard reasonable-sounding and weaker,
   * and reasoning around it is exactly what someone would do while reintroducing the bug — so the
   * guard keeps its blanket form and this uses a shape that genuinely is not the banned one.
   *
   * ⛔ AND THE KEY MUST BE THE id, NOT THE PATH. A catalog line carries the FEED path (full-path in
   * practice) while its backing research row may be stored under the BARE LEAF — the two storage
   * conventions in category_path. So a path lookup from this tab either misses the leaf-keyed row
   * or matches a different full-path row that is not the one the line is backed by.
   * ⚠️ Note this is the OPPOSITE conclusion to reloadAfterRepair, which legitimately matches on
   * categoryPath: there both sides are `category_characteristics` rows whose paths are exact and
   * distinct, so the match is unambiguous. The ambiguity is introduced by the available-categories
   * JOIN, so it exists on THIS tab and not on that path.
   */
  /**
   * ⛔⛔ EMPTY WHILE `categoriesKnown` IS FALSE — AND THAT IS THE WHOLE FIX, NOT A PRECAUTION.
   * (codex MINOR, BOTH LEGS INDEPENDENTLY, at 817aae69c.)
   *
   * loadData clears `availableCategories` on entry but has NO else-branch for a failed research
   * list: `setCategories` runs only `if (categoriesRes.ok)`, so on a non-2xx the PREVIOUS
   * `categories` array survives while a SUCCESSFUL available-categories repopulates the catalog.
   * ⇒ Ids then match against RETAINED research and this tab renders a stale diagnosis — and opens
   * stale detail — with nothing on screen saying the research request failed. After a destructive
   * rebuild that is the PRE-REFRESH row, which is exactly the hazard writeSafety.test.ts exists to
   * prevent. I avoided its banned PATTERN and reintroduced its HAZARD by another route.
   *
   * 🔑 AND IT IS THE THIRD SIBLING OF A DEFECT THIS FILE HAS ALREADY FIXED TWICE. The note at the
   * available-categories failure branch says it outright: "I FIXED EXACTLY THIS IN
   * loadResearchStatus ONE COMMIT EARLIER AND DID NOT LOOK AT ITS SIBLING." loadResearchStatus was
   * first, availableCategories second, and `categories` was still open — reachable only once
   * something resolved rows out of it, which is what this feature does.
   *
   * ⇒ Gating the MAP rather than each reader closes it for the diagnosis cell and the click-through
   * in one place, and both already have a defined "not loaded" branch to fall into. `categoriesKnown`
   * is false while a load is in flight AND after one fails, so this degrades to an honest "unknown"
   * in both cases rather than to a confident wrong answer.
   */
  const researchRowById = categoriesKnown
    ? new Map(categories.map((c) => [c.id, c]))
    : new Map<string, CategoryCharacteristics>();

  if (!isSuperAdmin) {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Access denied. This page is only available to Super Admins.
          </AlertDescription>
        </Alert>
        {requestedCategoryPath && (
          <Alert data-testid="ask-admin-to-research">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Ask an admin to research <span className="font-mono">{requestedCategoryPath}</span>
              {requestedSectorId != null ? <> (sector {requestedSectorId})</> : null}. Research runs from this page and needs a Super Admin; until the row exists, every product in that category is blocked at generation.
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  if (!sectorId) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-6 w-6" />
              Category Knowledge
            </CardTitle>
            <CardDescription>
              Sector-level AI-researched category characteristics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No sector assigned to the selected site. Select a sector below to manage category research.
              </AlertDescription>
            </Alert>
            <div className="flex items-center gap-4">
              <Label className="font-medium">Select Sector:</Label>
              <Select
                value={selectedSectorId?.toString() || ''}
                onValueChange={(v) => setSelectedSectorId(v ? parseInt(v, 10) : null)}
              >
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Choose a sector to research..." />
                </SelectTrigger>
                <SelectContent>
                  {sectors.map(s => (
                    <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Find current sector name for display
  const currentSectorName = sectors.find(s => s.id.toString() === sectorId?.toString())?.name ||
    (siteSectorId ? `Sector ${siteSectorId}` : 'Unknown');

  return (
    <div className="container mx-auto p-6 space-y-6">
      {requestedCategoryPath && requestedPathState === 'prefilled' && (
        <Alert data-testid="requested-path-banner">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Requested from the Home page: <span className="font-mono">{requestedCategoryPath}</span>
            {requestedSectorId != null ? <> (sector {requestedSectorId})</> : null} has no research row yet — it is pre-filled below; press Research to create it.
          </AlertDescription>
        </Alert>
      )}
      {requestedCategoryPath && requestedPathState === 'unknown' && (
        <Alert variant="destructive" data-testid="requested-path-banner">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Could not load research for sector {requestedSectorId ?? sectorId ?? '?'} — {requestedPathDetail?.reason || 'the lookup did not complete'}, so whether <span className="font-mono">{requestedCategoryPath}</span> has a research row is unknown. Reload the page to retry.</AlertDescription>
        </Alert>
      )}
      {requestedCategoryPath && requestedPathState === 'ambiguous' && (
        <Alert variant="destructive" data-testid="requested-path-banner">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Requested from the Home page: the full path <span className="font-mono">{requestedCategoryPath}</span> is NOT researched. It matches {requestedPathDetail?.candidates?.length ?? 0} research rows and NONE is keyed by the full path ({(requestedPathDetail?.candidates || []).map((c) => `row ${c.id} keyed by ${c.key ?? '?'}`).join('; ')}): bare-leaf rows are a retired convention, scheduled for deletion (2026-09-07 ruling); the generator and the hard gate read the full path only. Nothing was opened and nothing was pre-filled.
          </AlertDescription>
        </Alert>
      )}
      {requestedCategoryPath && requestedPathState === 'leaf_only' && (
        <Alert data-testid="requested-path-banner">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Requested from the Home page: <span className="font-mono">{requestedCategoryPath}</span>
            {requestedSectorId != null ? <> (sector {requestedSectorId})</> : null}: the full path is NOT researched, so this category is still blocked at the gate. Leaf row {requestedPathDetail?.leafId} (keyed by <span className="font-mono">{requestedPathDetail?.leafKey ?? '?'}</span>) is a retired convention, scheduled for deletion (2026-09-07 ruling); it is not opened and nothing acts on it. The FULL path is pre-filled below — press Research to create its row.
          </AlertDescription>
        </Alert>
      )}
      {requestedCategoryPath && requestedPathState === 'opened' && (
        <Alert data-testid="requested-path-banner">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Requested from the Home page: <span className="font-mono">{requestedCategoryPath}</span> — its research row {selectedCategory?.id} (keyed by the full path) is open below.
            {requestedPathDetail?.leafId ? <> Leaf row {requestedPathDetail.leafId} (keyed by <span className="font-mono">{requestedPathDetail.leafKey ?? '?'}</span>) also exists: a retired convention, scheduled for deletion (2026-09-07 ruling); the generator does not read it and nothing here acts on it.</> : null}
          </AlertDescription>
        </Alert>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers className="h-6 w-6" />
            Category Knowledge
          </h1>
          <p className="text-muted-foreground">
            Sector-level AI-researched category characteristics (shared by all sites in this sector)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="default"
            onClick={() => setBatchResearchDialogOpen(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <ListChecks className="h-4 w-4 mr-2" />
            Complete All Research
          </Button>
          <Button variant="destructive" onClick={handleMarkAllStale}>
            <AlertTriangle className="h-4 w-4 mr-2" />
            Mark All Stale
          </Button>
        </div>
      </div>

      {batchResearchJob && batchJobRetired && <Alert>
        <AlertDescription>
          Legacy research retired after handoff · Job {batchResearchJob.id}. Recorded results and unresolved costs are preserved.
          Targets without a handoff remain blocked.
        </AlertDescription>
      </Alert>}
      {/* Active Batch Job Progress */}
      {batchResearchJob && batchJobActive && (
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="font-medium">Batch Research in Progress</span>
                <Badge variant="secondary">
                  {batchResearchJob.completedCategories}/{batchResearchJob.totalCategories} categories
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {Object.entries(batchResearchJob.progressDetails || {}).flatMap(([categoryPath,progress])=>
                    Object.entries(progress.steps || {}).filter(([,status])=>status==='running')
                      .map(([step])=>`${categoryPath}: ${step.replace(/_/g,' ')}`)).join(' · ')
                    || (batchResearchJob.currentStep && `Current: ${batchResearchJob.currentStep.replace('_', ' ')}`)}
                </span>
                <Button size="sm" variant="outline" onClick={handleCancelBatchResearch}>
                  <Square className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
            <Progress value={batchResearchJob.progressPercent} className="h-2" />
            <div className="flex justify-between mt-1 text-xs text-muted-foreground">
              <span>{batchResearchJob.progressPercent}% complete</span>
              {batchResearchJob.failedCategories > 0 && (
                <span className="text-red-600">{batchResearchJob.failedCategories} failed</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sector Selection - allows managing any sector */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col gap-4">
            {/* Site's sectors quick access */}
            {selectedSite?.sector_ids && selectedSite.sector_ids.length > 1 && (
              <div className="flex items-center gap-2">
                <Label className="font-medium whitespace-nowrap text-muted-foreground">Site sectors:</Label>
                <div className="flex gap-2">
                  {selectedSite.sector_ids.map(sId => {
                    const sectorIdNum = typeof sId === 'string' ? parseInt(sId, 10) : sId;
                    const sector = sectors.find(s => s.id.toString() === sectorIdNum.toString());
                    const isActive = sectorId === sectorIdNum;
                    return (
                      <Button
                        key={sectorIdNum}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedSectorId(sectorIdNum)}
                        className={cn(
                          "transition-all",
                          isActive && "ring-2 ring-offset-2 ring-primary"
                        )}
                      >
                        {sector?.name || `Sector ${sectorIdNum}`}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Full sector selector and actions */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Label className="font-medium whitespace-nowrap">Sector:</Label>
                <Select
                  value={selectedSectorId?.toString() || '__current__'}
                  onValueChange={(v) => setSelectedSectorId(v === '__current__' ? null : parseInt(v, 10))}
                >
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder={siteSectorId ? `Current site: ${currentSectorName}` : 'Select sector...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {siteSectorId && (
                      <SelectItem value="__current__">Current site's sector ({currentSectorName})</SelectItem>
                    )}
                    {sectors.map(s => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedSectorId && selectedSectorId !== siteSectorId && (
                  <Badge variant="outline" className="bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300">
                    Managing: {currentSectorName}
                  </Badge>
                )}
              </div>

              {/* Manual category research */}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="New category name (e.g., DC Motors)"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="w-[250px]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCategoryName.trim()) {
                      startResearchNewCategory();
                    }
                  }}
                />
                <Button
                  onClick={startResearchNewCategory}
                  disabled={!newCategoryName.trim() || triggeringResearch === newCategoryName.trim()}
                >
                  {triggeringResearch === newCategoryName.trim() ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Research Category
                </Button>
              </div>
            </div>

            {/* Sector mismatch warning */}
            {sectorMismatchWarning && selectedCategory && (
              <Alert variant="default" className="bg-amber-50 dark:bg-amber-950/30 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 dark:text-amber-200">
                  {sectorMismatchWarning}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {researchListLoadFailed && (
        // Page-level, above the tabs: a failed research list is visible whichever tab is open (both legs, minor at
        // 24ec2dbfe). The Researched tab also replaces its table with the same state instead of "No category
        // characteristics found".
        <Alert variant="destructive" data-testid="research-list-unavailable">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>The research list for sector {sectorId ?? '?'} could not be loaded. Nothing is shown rather than an empty list or another sector's rows. Reload the page to retry.</AlertDescription>
        </Alert>
      )}
      {summaryLoadFailed && (
        <Alert variant="destructive" data-testid="summary-unavailable">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>The research summary for this sector could not be loaded, so no counts are shown. Reload the page to retry.</AlertDescription>
        </Alert>
      )}
      {summary && (() => {
        const fullyResearched = categories.filter(c =>
          c.researchDimensions && c.researchDimensions.every(Boolean)
        ).length;
        const totalWithDims = categories.filter(c => c.researchDimensions).length;

        return (
          <div className="grid grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-3xl font-bold">{summary.total}</div>
                <div className="text-sm text-muted-foreground">Total Categories</div>
              </CardContent>
            </Card>
            <Card className={cn(
              fullyResearched === totalWithDims && totalWithDims > 0
                ? 'border-green-200 bg-green-50 dark:bg-green-950/20'
                : 'border-blue-200 bg-blue-50 dark:bg-blue-950/20'
            )}>
              {/* ⛔ THE SEVEN COUNTED HERE ARE NOT THE SEVEN IN THE CATEGORY DETAIL DIALOG.
                  This card counts RESEARCH PIPELINE dimensions (characteristics, canonical attrs,
                  perceived benefits, attr-perception maps, persona-benefit maps, extraction hints,
                  attr intelligence). The detail dialog shows seven CONTENT aspects (product types,
                  brand tiers, use cases, premium features, personas, tier vocabulary, price tiers).
                  Two different seven-item lists on one screen: an operator reading "5/7" could not
                  tell which, and the orchestrator conflated them while deciding a 7,400-page
                  regeneration. So the names are rendered, not just tooltipped. */}
              <CardContent className="pt-4">
                <div className={cn(
                  'text-3xl font-bold',
                  fullyResearched === totalWithDims && totalWithDims > 0 ? 'text-green-700' : 'text-blue-700'
                )}>
                  {fullyResearched}/{totalWithDims}
                </div>
                <div className="text-sm text-muted-foreground">Fully Researched (7/7 pipeline dimensions)</div>
                {dimensionNames.length > 0 && (
                  <div
                    className="mt-1 text-[10px] leading-tight text-muted-foreground"
                    title={`Research pipeline dimensions: ${dimensionNames.join(', ')}. These are NOT the seven aspects shown in the category detail dialog.`}
                  >
                    {dimensionNames.join(' · ')}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-green-700">{summary.byStatus.active}</div>
                <div className="text-sm text-green-600">Active</div>
              </CardContent>
            </Card>
            <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20">
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-yellow-700">{summary.byStatus.stale}</div>
                <div className="text-sm text-yellow-600">Stale</div>
              </CardContent>
            </Card>
            <Card className="border-gray-200 bg-gray-50 dark:bg-gray-950/20">
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-gray-700">{summary.byStatus.pending}</div>
                <div className="text-sm text-gray-600">Pending</div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-red-700">{summary.byStatus.failed}</div>
                <div className="text-sm text-red-600">Failed</div>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Tabs for Catalog, GPT Browser, and Researched */}
      <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'catalog' | 'gpt-browser' | 'researched')}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="catalog">
              Your Catalog ({filteredAvailableCategories.length})
            </TabsTrigger>
            <TabsTrigger value="gpt-browser">
              Browse GPT Taxonomy
            </TabsTrigger>
            <TabsTrigger value="researched">
              Research records ({categories.length})
            </TabsTrigger>
          </TabsList>

          {viewMode === 'researched' && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="stale">Stale / Aging</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Catalog Categories Tab - Categories where site has products */}
        <TabsContent value="catalog" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Your Catalog Categories</CardTitle>
              {/* ⇒ THE LABEL NOW STATES THE SCOPE IT ACTUALLY HAS. It used to say "belong to the
                  selected sector's taxonomy" while being titled "Your Catalog", and the gap between
                  those two was the defect: every category outside the viewed sector's taxonomy root
                  was silently dropped, Shoes included. */}
              <CardDescription>
                {selectedSite
                  ? <>Every category in {selectedSite.name}'s product feed with 3 or more products — across all of this site's sectors, not only the one selected. Research is stored under the selected sector.</>
                  : <>No site selected, so this lists categories across every site in the sector, limited to the sector's taxonomy root. Select a site to see that site's own catalogue.</>}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : filteredAvailableCategories.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  {selectedSite ? (
                    <>
                      <p>No product categories found for {selectedSite.name}</p>
                      <p className="text-sm mt-2">
                        This can happen if:
                        <br />• This site has no imported product feed yet
                        <br />• Every category has fewer than 3 products
                        <br />• This site is not assigned to the selected sector
                      </p>
                    </>
                  ) : (
                    <>
                      <p>No product categories found in this sector's taxonomy</p>
                      <p className="text-sm mt-2">
                        This can happen if:
                        <br />• Your products use a different Google taxonomy path
                        <br />• The sector's taxonomy root doesn't match your product categories
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Products</TableHead>
                      <TableHead>Brands</TableHead>
                      <TableHead>Price Range</TableHead>
                      <TableHead>Research Status</TableHead>
                      {/* ⇒ THE DIAGNOSIS, as the Researched tab shows it. Same fields, same
                          components, resolved from the same CategoryCharacteristics row. */}
                      <TableHead>Diagnosis</TableHead>
                      <TableHead className="w-32">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAvailableCategories.map((cat) => {
                      /* ⛔⛔ THE RESEARCH ROW BEHIND THIS LINE, RESOLVED BY id AND NOTHING ELSE.
                         Jaume asked this tab to show "the diagnosys of the status of the category
                         as in the tab Researched" — and that diagnosis (dimension completeness,
                         freshness, research error) lives on the CategoryCharacteristics row, not on
                         the feed-derived row this table is built from.
                         ⇒ Resolving it here rather than widening the endpoint means the tab shows
                         THE SAME OBJECT the Researched tab renders, so the two views cannot drift.
                         ⚠️ `?? null` deliberately: `find` returns undefined and the distinction
                         between "no research row exists" and "the row exists but is not loaded" is
                         one the cells below have to be able to make. */
                      const researchRow = cat.characteristicsId
                        ? (researchRowById.get(cat.characteristicsId) ?? null)
                        : null;
                      const dims = researchRow?.researchDimensions || [];
                      const completedDims = dims.filter(Boolean).length;
                      const totalDims = dims.length || 7;
                      const missingDims = dimensionShortNames.filter((_, i) => dims[i] === false);
                      const dimTooltip = dimensionStateTooltip(researchRow?.researchDimensionStates,dimensionShortNames) || researchRow?.researchReadiness?.reason || (missingDims.length > 0
                        ? `Missing: ${missingDims.join(', ')}`
                        : researchRow?.researchReadiness?.ready ? 'Research complete; empty researched results are included'
                          : 'All stored sections present; research completion is recorded per job step');

                      return (
                      /* ⛔ THE KEY MUST INCLUDE THE RESEARCH ROW. Two lines share a categoryPath
                         when both a leaf-keyed and a full-path-keyed research row exist, so keying
                         on the path alone gave React DUPLICATE KEYS for visibly different rows. */
                      <TableRow key={rowKey(cat)}>
                        <TableCell>
                          <div>
                            {/* ⛔⛔ CLICK-THROUGH KEYS ON THE RESEARCH ROW'S id, NEVER ON THE PATH.
                                Jaume asked that a category here open "the details of the research as
                                in the Research view" — the same CategoryDetailDialog the Researched
                                tab opens, which takes a CategoryCharacteristics row.
                                ⇒ Matching by categoryPath would be ambiguous BY CONSTRUCTION: the
                                column holds two conventions, this query deliberately returns BOTH
                                lines when both shapes exist, and a path lookup would open one of
                                them arbitrarily — showing the operator research that is not the
                                research their row is backed by. Same class as task-fx7gfghirm,
                                arriving through the click instead of the table. A uuid names one row.
                                ⚠️ NOT a silent no-op when the row cannot be found. `categories` is
                                the viewed sector's list and the join above is `cc.sector_id = $1`,
                                so a hit is expected — but "expected" is not "guaranteed" (a status
                                the list filters, a load still in flight), and a dead click that
                                explains nothing is how an operator concludes the page is broken. */}
                            {cat.characteristicsId ? (
                              <button
                                type="button"
                                className="font-medium text-left hover:underline text-primary"
                                onClick={() => {
                                  if (researchRow) {
                                    // ⚠️ setLastRefreshError(null) TOO — the Researched tab's row
                                    // click does both, and lastRefreshError is dialog-scoped state
                                    // left over from a PREVIOUS category's failed refresh. Opening
                                    // from here without clearing it would show one category's error
                                    // inside another category's detail.
                                    setSelectedCategory(researchRow);
                                    setLastRefreshError(null);
                                    return;
                                  }
                                  toast({
                                    title: 'Research row not loaded',
                                    description: `"${cat.categoryName}" is backed by research row ${cat.characteristicsId}, but the research list is not loaded — either it is still loading or its request failed. Showing you the previous research here could be out of date, so nothing is opened. Reload the page.`,
                                    variant: 'destructive',
                                  });
                                }}
                              >
                                {cat.categoryName}
                              </button>
                            ) : (
                              <div className="font-medium">{cat.categoryName}</div>
                            )}
                            <div className="text-xs text-muted-foreground font-mono whitespace-normal break-words">
                              {cat.categoryPath}
                            </div>
                            {/* ⛔⛔ WHICH RESEARCH ROW THIS LINE IS BACKED BY. Until this existed the
                                two copies of a category were IDENTICAL on screen — same heading,
                                same path, same counts, same status, same Refresh — while each
                                Refresh destroyed a DIFFERENT research row. An operator choosing
                                between them was choosing blind. (task-fx7gfghirm.) */}
                            {cat.researchCategoryPath && cat.researchCategoryPath !== cat.categoryPath && (
                              <div className="text-xs mt-1 flex items-center gap-1 flex-wrap">
                                <Badge variant="outline" className="text-[10px] font-normal">
                                  {cat.researchKeyIsLeaf ? 'research stored under LEAF' : 'research stored under FULL PATH'}
                                </Badge>
                                <span className="font-mono text-muted-foreground break-all">
                                  {cat.researchCategoryPath}
                                </span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{cat.productCount} products</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{cat.brandCount} brands</span>
                        </TableCell>
                        <TableCell>
                          {cat.priceRange ? (
                            <span className="text-sm">
                              €{cat.priceRange.min.toFixed(0)} - €{cat.priceRange.max.toFixed(0)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {cat.hasResearch ? (
                            <div className="space-y-1">
                              <Badge variant="default" className="bg-green-600">
                                {cat.researchStatus}
                              </Badge>
                              {/* ⛔⛔ PERSONA HEALTH, BECAUSE THE STATUS BADGE IS THE SAME ON BOTH
                                  COPIES AND THEIR HEALTH IS NOT. Measured on DiveInn: for Wetsuits,
                                  Diving Regulators and Dive Computers the LEAF row holds all five
                                  weighted personas and the FULL-PATH row holds none — but for
                                  Diving & Snorkeling Masks it is the OTHER WAY ROUND, and for Fins
                                  both are healthy.
                                  ⇒ NEITHER SHAPE IS RELIABLY THE GOOD ONE. That is exactly why this
                                  has to be shown per row rather than summarised as a rule, and why
                                  no bulk "keep the leaf" or "keep the full path" repair is safe. */}
                              {cat.personaCount !== null && cat.personaCount > 0 && (
                                <div className={cn(
                                  'text-xs',
                                  cat.weightedPersonaCount === 0 ? 'text-red-600 font-medium' : 'text-muted-foreground'
                                )}>
                                  {cat.weightedPersonaCount} of {cat.personaCount} personas weighted
                                </div>
                              )}
                              {cat.personaCount === 0 && (
                                <div className="text-xs text-muted-foreground">no personas</div>
                              )}
                            </div>
                          ) : (
                            <Badge variant="outline">No exact-path research</Badge>
                          )}
                        </TableCell>
                        {/* ⛔⛔ THE DIAGNOSIS CELL — THREE DISTINCT STATES, NEVER COLLAPSED TO TWO.
                            (a) no research row at all      -> "Never researched", the state 2 DiveInn
                                                               categories are in today and the one an
                                                               operator most needs to see;
                            (b) a research row exists but is NOT in the loaded list -> say exactly
                                that. Rendering an empty cell here would be indistinguishable from
                                (a) and would quietly assert "never researched" about a category
                                that HAS research — a false negative on the one screen built to
                                answer this question;
                            (c) resolved -> the same dimension progress, freshness and error the
                                Researched tab renders.
                            ⚠️ (b) is expected to be unreachable — the join is `cc.sector_id = $1`,
                            so the row belongs to the viewed sector — but "expected unreachable" and
                            "silently rendered as absent" must not be the same pixel. */}
                        <TableCell>
                          {!cat.characteristicsId ? (
                            <span className="text-xs text-muted-foreground">{cat.researchStatus === 'ambiguous' ? 'Multiple exact-path records; inspect research records' : 'No research record at this catalog path'}</span>
                          ) : !researchRow ? (
                            <span className="text-xs text-amber-600" title={`This category HAS research (row ${cat.characteristicsId}) but the research list is not loaded — still loading, or its request failed. The previous diagnosis is deliberately not shown, because after a rebuild it would be the pre-refresh one.`}>
                              research not loaded
                            </span>
                          ) : (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2" title={dimTooltip}>
                                <Progress value={(completedDims / totalDims) * 100} className="h-2 w-16" />
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {completedDims}/{totalDims}
                                </span>
                              </div>
                              <ResearchDimensionDetails states={researchRow?.researchDimensionStates} names={dimensionShortNames} />
                              <div className="text-xs text-muted-foreground">Stored sections: {completedDims}/{totalDims}. Full research completion is shown by the job's step receipts.</div>
                              <FreshnessIndicator freshness={researchRow.freshness} />
                              {(researchRow.researchError || researchRow.freshness.error) && (
                                <div className="text-xs text-red-600 truncate max-w-[16rem]"
                                     title={researchRow.researchError || researchRow.freshness.error}>
                                  {researchRow.researchError || researchRow.freshness.error}
                                </div>
                              )}
                            </div>
                          )}
                          {batchResearchJob?.siteId===selectedSite?.id && batchResearchJob.progressDetails[cat.categoryPath] &&
                            <div className="text-xs mt-2">
                              {Object.entries(batchResearchJob.progressDetails[cat.categoryPath].steps || {}).map(([step,status])=>
                                <div key={step}>{step.replace(/_/g,' ')}: {status.replace(/_/g,' ')}</div>)}
                            </div>}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={cat.hasResearch ? 'outline' : 'default'}
                            onClick={() => openCatalogResearch(cat.categoryPath)}
                            disabled={startingBatchResearch || batchBlocksAll || pathHasResearchOwner(cat.categoryPath) || !!unacknowledgedSubmission || researchRecoveryBlocked || cat.researchStatus==='ambiguous'}
                          >
                            {triggeringResearch === refreshTargetPath(cat) ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Researching...
                              </>
                            ) : cat.hasResearch ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Refresh
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 mr-2" />
                                Research
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GPT Taxonomy Browser Tab */}
        <TabsContent value="gpt-browser" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Browse Google Product Taxonomy
                  </CardTitle>
                  <CardDescription>
                    Research any category from Google's taxonomy. Select a sector below to determine where research is stored.
                  </CardDescription>
                </div>
              </div>
              {/* Sector selector for GPT browser - restricted to site's sectors */}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                <Label className="font-medium whitespace-nowrap">Research for Sector:</Label>
                {selectedSite?.sector_ids && selectedSite.sector_ids.length > 0 ? (
                  <div className="flex gap-2">
                    {selectedSite.sector_ids.map(sId => {
                      const sectorIdNum = typeof sId === 'string' ? parseInt(sId, 10) : sId;
                      const sector = sectors.find(s => s.id.toString() === sectorIdNum.toString());
                      const isActive = sectorId === sectorIdNum;
                      return (
                        <Button
                          key={sectorIdNum}
                          variant={isActive ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setSelectedSectorId(sectorIdNum);
                            // Reset GPT browser state when sector changes
                            setGptBreadcrumb([]);
                            setGptTaxonomy([]);
                          }}
                          className={cn(
                            "transition-all",
                            isActive && "ring-2 ring-offset-2 ring-primary"
                          )}
                        >
                          {sector?.name || `Sector ${sectorIdNum}`}
                        </Button>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">No sectors assigned to this site</span>
                )}
              </div>
            </CardHeader>

            <CardContent>
              {/* Breadcrumb navigation */}
              {gptBreadcrumb.length > 0 && (
                <div className="flex items-center gap-1 mb-4 text-sm flex-wrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleGptBreadcrumbClick(-1)}
                    className="h-7 px-2"
                  >
                    Root
                  </Button>
                  {gptBreadcrumb.map((crumb, index) => (
                    <React.Fragment key={crumb.id}>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleGptBreadcrumbClick(index)}
                        className={cn(
                          "h-7 px-2",
                          index === gptBreadcrumb.length - 1 && "font-semibold"
                        )}
                      >
                        {crumb.category_name}
                      </Button>
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Load taxonomy button if not loaded */}
              {gptTaxonomy.length === 0 && !gptLoading && (
                <div className="text-center py-8">
                  <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground mb-4">Click to load the taxonomy tree for this sector</p>
                  <Button onClick={() => loadGptTaxonomy()}>
                    <Eye className="h-4 w-4 mr-2" />
                    Load Taxonomy
                  </Button>
                </div>
              )}

              {gptLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : gptTaxonomy.length > 0 && (
                <div className="space-y-1">
                  {gptTaxonomy.map((cat) => {
                    // Check if this category is already researched. Shared with the click guard —
                    // see gptCategoryIsResearched.
                    const isResearched = gptCategoryIsResearched(cat);
                    return (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 group"
                      >
                        <button
                          onClick={() => handleGptCategoryClick(cat)}
                          className="flex items-center gap-2 text-left flex-1"
                        >
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                          <span className="font-medium">{cat.category_name}</span>
                          {cat.avg_monthly_searches && (
                            <Badge variant="outline" className="text-xs">
                              {parseInt(cat.avg_monthly_searches).toLocaleString()} searches/mo
                            </Badge>
                          )}
                        </button>
                        <Button
                          size="sm"
                          variant={isResearched ? "outline" : "default"}
                          onClick={(e) => {
                            e.stopPropagation();
                            startResearchGptCategory(cat);
                          }}
                          disabled={triggeringResearch !== null || resolvingTriggerPath === cat.category_path}
                        >
                          {isResearched ? (
                            <>
                              <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                              Researched
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-1" />
                              Research
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Researched Categories Tab */}
        <TabsContent value="researched" className="mt-4">
          {/* Bulk attribute-intelligence research. Mounted here, above the per-category table,
              because it targets every category in the sector at once rather than one row.
              ⛔ categoriesKnown IS LOAD-BEARING, NOT INFORMATIONAL (codex-reviewer-2): sectorId
              changes the instant the operator switches sector, while `categories` still holds
              the previous sector's rows until loadData() resolves. The panel's scope key
              compares identifiers only, so it cannot detect that mismatch on its own — it is
              handed the same flag researchExistsFor / researchExistsInSector already gate on,
              and renders no preview/write controls while it is false. */}
          <AttributeIntelligenceBulkResearch sectorId={sectorId} categories={categories} categoriesKnown={categoriesKnown} catalogueRoots={catalogueRoots} />
          {/* The persona prose rewrite. Mounted HERE because this tab is where an operator sees
              the category personas themselves — the action belongs beside the thing it changes.
              Renders nothing for non-super-admins; see the component. */}
          <PersonaRewriteEnqueue />
          {/* ⛔⛔ THE MECHANISM FOR THE CEO RULING OF 2026-09-07 — leaf rows must be DELETED, not
              preserved. Mounted HERE, with the other sector-wide panels, because a leaf duplicate is
              a property of the sector's row set rather than of any one category: you cannot tell
              that `Backpacks` is a truncation by looking at `Backpacks`, only by seeing that
              `Luggage & Bags > Backpacks` also exists. Until this shipped there was no delete
              control anywhere in the admin and no DELETE route behind it, so the ruling was
              unexecutable by the person who made it. */}
          <LeafRetirementPanel sectorId={sectorId} onRetired={() => { void loadData(); }} />
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : researchListLoadFailed ? (
                <div className="p-12 text-center text-destructive" data-testid="research-list-unavailable-table">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-70" />
                  <p>The research list for this sector could not be loaded.</p>
                  <p className="text-sm">Nothing is shown rather than an empty list or another sector's rows. Reload the page to retry.</p>
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No category characteristics found</p>
                  <p className="text-sm">Research will be triggered automatically during AEO generation</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Stored sections</TableHead>
                      <TableHead>Freshness</TableHead>
                      <TableHead>Usage</TableHead>
                      {/* <TableHead>Cost</TableHead> */}
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCategories.map((cat) => {
                      const dims = cat.researchDimensions || [];
                      const completedDims = dims.filter(Boolean).length;
                      const totalDims = dims.length || 7;
                      const missingDims = dimensionShortNames.filter((_, i) => dims[i] === false);
                      const dimTooltip = dimensionStateTooltip(cat.researchDimensionStates,dimensionShortNames) || cat.researchReadiness?.reason || (missingDims.length > 0
                        ? `Missing: ${missingDims.join(', ')}`
                        : cat.researchReadiness?.ready ? 'Research complete; empty researched results are included'
                          : 'All stored sections present; research completion is recorded per job step');

                      return (
                        <TableRow
                          key={cat.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => { setSelectedCategory(cat); setLastRefreshError(null); }}
                        >
                          <TableCell>
                            <div>
                              <div className="font-medium">{cat.categoryName}</div>
                              <div className="text-xs text-muted-foreground font-mono whitespace-normal break-words">
                                {cat.categoryPath}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {/* researchError is sent unconditionally by the backend
                                (formatCharacteristicsResponse), so a category can carry one
                                even while status stays 'active' (a post-commit canonical-
                                schema write failing independently of the research itself).
                                Previously the badge showed only the bare status word with no
                                way to see WHY — a click-through to the full detail dialog's
                                Alert was the only path, and that dialog isn't open from this
                                table row. A hover tooltip surfaces the message right here. */}
                            {cat.researchError ? (
                              <TooltipProvider>
                                <Tooltip>
                                  {/* Badge is a plain function component (no forwardRef), so
                                  `asChild` on TooltipTrigger can't attach its positioning ref
                                  directly to it (Radix logs "Function components cannot be given
                                  refs"). A span gives Radix a real DOM node to attach to. */}
                                  <TooltipTrigger asChild>
                                    <span>
                                      <Badge
                                        variant={cat.status === 'failed' ? 'destructive' : 'secondary'}
                                        className="cursor-help"
                                      >
                                        {cat.status === 'failed' ? cat.status : `${cat.status} ⚠`}
                                      </Badge>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-sm whitespace-pre-wrap text-left">
                                    {cat.researchError}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <Badge
                                variant={
                                  cat.status === 'active' ? 'default' :
                                  cat.status === 'failed' ? 'destructive' :
                                  cat.status === 'researching' ? 'secondary' :
                                  'outline'
                                }
                              >
                                {cat.status}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2" title={dimTooltip}>
                              <Progress
                                value={(completedDims / totalDims) * 100}
                                className={cn(
                                  'w-20 h-2',
                                  completedDims === totalDims ? '[&>div]:bg-green-500' :
                                  completedDims <= 2 ? '[&>div]:bg-red-500' :
                                  '[&>div]:bg-yellow-500'
                                )}
                              />
                              <span className={cn(
                                'text-sm font-medium',
                                completedDims === totalDims ? 'text-green-700 dark:text-green-400' :
                                completedDims <= 2 ? 'text-red-700 dark:text-red-400' :
                                'text-yellow-700 dark:text-yellow-400'
                              )}>
                                {completedDims}/{totalDims}
                              </span>
                            </div>
                            <ResearchDimensionDetails states={cat?.researchDimensionStates} names={dimensionShortNames} />
                          </TableCell>
                          <TableCell>
                            <FreshnessIndicator freshness={cat.freshness} />
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{cat.timesUsed}x</span>
                          </TableCell>
                          {/* Cost column hidden until properly debugged
                          <TableCell>
                            <span className="text-sm">{cat.researchCostUsd == null ? 'unmeasured' : `$${cat.researchCostUsd.toFixed(4)}`}</span>
                          </TableCell>
                          */}
                          <TableCell>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Category Detail Dialog */}
      {selectedCategory && !selectedCategory.categoryPath.includes('>') && !catalogueRoots?.has(selectedCategory.categoryPath) ? (
        <Dialog open onOpenChange={()=>setSelectedCategory(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>Legacy research record · Read only</DialogTitle>
              <DialogDescription>{selectedCategory.categoryPath} · {selectedCategory.id}</DialogDescription></DialogHeader>
            <p>{availableCategories.some(c=>c.categoryPath.split('>').pop()?.trim()===selectedCategory.categoryPath)
              ? 'A catalog path has a matching leaf name. Research actions are available on the catalog path.'
              : 'No matching catalog path is listed. This legacy record is retained for inspection.'}</p>
            <p>Genuine catalog roots are identified from the site's full catalog. Legacy records are not research targets.</p>
            {Object.keys(ASPECT_CONFIG).map(key=><AspectStatus key={key} aspect={key as keyof typeof ASPECT_CONFIG}
              data={selectedCategory.aspects[key as keyof typeof ASPECT_CONFIG]} sampleProductCount={selectedCategory.sampleProductCount} />)}
            <Button variant="outline" onClick={()=>setSelectedCategory(null)}>Close</Button>
          </DialogContent>
        </Dialog>
      ) : <CategoryDetailDialog
        /* ⛔ resolvedWriteSectorId, NOT getWriteSectorId(). A duplicate of this prop reached here in
           the merge; the helper falls back to the VIEWED sector while resolution is in flight,
           which is exactly what #4753 removed from this dialog's destructive controls. */
        category={selectedCategory}
        lastRefreshError={lastRefreshError}
        isOpen={!!selectedCategory}
        onClose={() => setSelectedCategory(null)}
        onRefresh={async path=>{setSelectedCategory(null);openCatalogResearch(path);}}
        onRefreshAspect={handleRefreshAspect}
        onRegeneratePersonas={handleRegeneratePersonas}
        regenPhase={regenPhase}
        technicalResearch={technicalResearch}
        onRegenerateAttributes={handleRegenerateAttributes}
        onResearchIntelligence={handleResearchIntelligence}
        onResearchExtractionHints={handleResearchExtractionHints}
        onResearchPerceivedBenefits={handleResearchPerceivedBenefits}
        onResearchAttributeMappings={handleResearchAttributeMappings}
        sectorId={sectorId}
        writeSectorId={resolvedWriteSectorId}
        onReload={reloadAfterRepair}
        onOpenCanonicalDialog={handleOpenCanonicalDialog}
        onOpenIntelligenceDialog={handleOpenIntelligenceDialog}
        onOpenExtractionHintsDialog={handleOpenExtractionHintsDialog}
        onOpenChatDialog={() => setChatDialogOpen(true)}
        dimensionNames={dimensionNames}
      />}

      {/* Attribute Intelligence Viewer Dialog */}
      <Dialog open={intelligenceDialogOpen} onOpenChange={setIntelligenceDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5" />
              Attribute Intelligence
            </DialogTitle>
            <DialogDescription>
              Value interpretations for "{technicalResearch?.categoryName || 'category'}" - explains what technical spec values mean to customers
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[500px] pr-4">
            {intelligenceLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))}
              </div>
            ) : attributeIntelligence.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No attribute intelligence found</p>
                <p className="text-sm mt-1">
                  Use the Category Perception Research to generate value interpretations for technical attributes.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {attributeIntelligence.map((intel) => (
                  <Card key={intel.id} className={cn(
                    "border",
                    editingIntelligence?.id === intel.id
                      ? "border-blue-400 bg-blue-50/50 dark:bg-blue-950/20"
                      : "border-amber-200 bg-amber-50/50 dark:bg-amber-950/20"
                  )}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Lightbulb className="h-4 w-4 text-amber-600" />
                          {intel.title}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {intel.scope_attribute_name}
                          </Badge>
                          {editingIntelligence?.id !== intel.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingIntelligence({ ...intel })}
                              className="h-7 px-2"
                            >
                              <Edit3 className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                      {editingIntelligence?.id === intel.id ? (
                        <div className="mt-2">
                          <Label className="text-xs">Summary (shown in UI)</Label>
                          <Input
                            value={editingIntelligence.knowledge_text}
                            onChange={(e) => setEditingIntelligence({
                              ...editingIntelligence,
                              knowledge_text: e.target.value
                            })}
                            className="mt-1"
                          />
                        </div>
                      ) : (
                        <CardDescription>{intel.knowledge_text}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border">
                        <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Value Interpretations
                        </Label>
                        {editingIntelligence?.id === intel.id ? (
                          <Textarea
                            value={editingIntelligence.research_findings}
                            onChange={(e) => setEditingIntelligence({
                              ...editingIntelligence,
                              research_findings: e.target.value
                            })}
                            className="mt-2 font-mono text-sm min-h-[200px]"
                            placeholder="Enter value interpretations...

Example format:
- 60 TPI: Standard durability. Good for training and commuting.
- 120 TPI: Performance-oriented. Lighter, more supple feel.
- 660 TPI: Downhill/Enduro reinforced casing. Ultra-durable."
                          />
                        ) : (
                          <pre className="mt-2 text-sm whitespace-pre-wrap font-mono text-gray-700 dark:text-gray-300">
                            {intel.research_findings}
                          </pre>
                        )}
                      </div>
                      {editingIntelligence?.id === intel.id && (
                        <div className="flex justify-end gap-2 mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingIntelligence(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveIntelligence}
                            disabled={savingIntelligence}
                          >
                            {savingIntelligence ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4 mr-2" />
                            )}
                            Save Changes
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIntelligenceDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extraction Hints Dialog */}
      <Dialog open={extractionHintsDialogOpen} onOpenChange={setExtractionHintsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5" />
              Extraction Hints
            </DialogTitle>
            <DialogDescription>
              Field disambiguation and qualifier rules for "{technicalResearch?.categoryName || 'category'}" - teaches AI how to correctly extract values from product feeds
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[500px] pr-4">
            {extractionHintsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))}
              </div>
            ) : extractionHints.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <Wand2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No extraction hints found</p>
                <p className="text-sm mt-1">
                  Use the "Research Extraction Hints" button to generate field disambiguation rules.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {extractionHints.map((hint) => (
                  <Card key={hint.specName} className={cn(
                    "border",
                    hint.hasDisambiguation || hint.hasQualifierRules || hint.hasSemanticClarifications
                      ? "border-purple-200 bg-purple-50/50 dark:bg-purple-950/20"
                      : "border-gray-200 bg-gray-50/50 dark:bg-gray-950/20"
                  )}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Wand2 className="h-4 w-4 text-purple-600" />
                          {hint.displayName}
                          <Badge variant="outline" className="text-xs ml-2 font-mono">
                            {hint.specName}
                          </Badge>
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          {hint.isKeyAttribute && (
                            <Badge className="bg-amber-500 text-xs">Key Attribute</Badge>
                          )}
                          {hint.isMultiSelect && (
                            <Badge variant="secondary" className="text-xs">Multi-Select</Badge>
                          )}
                          {hint.unit && (
                            <Badge variant="outline" className="text-xs">{hint.unit}</Badge>
                          )}
                        </div>
                      </div>
                      {hint.guidance && (
                        <CardDescription className="mt-1">{hint.guidance}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Field Disambiguation */}
                      {hint.hasDisambiguation && hint.fieldDisambiguation && (
                        <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border">
                          <Label className="text-xs font-semibold text-purple-600 uppercase tracking-wide flex items-center gap-1">
                            <ArrowRight className="h-3 w-3" />
                            Field Disambiguation
                          </Label>
                          <div className="mt-2 space-y-2">
                            {hint.fieldDisambiguation.prefer_fields && hint.fieldDisambiguation.prefer_fields.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                <span className="text-xs text-green-600 font-medium">PREFER:</span>
                                {hint.fieldDisambiguation.prefer_fields.map((field, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-green-50 text-green-800">
                                    {field}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {hint.fieldDisambiguation.avoid_fields && hint.fieldDisambiguation.avoid_fields.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                <span className="text-xs text-red-600 font-medium">AVOID:</span>
                                {hint.fieldDisambiguation.avoid_fields.map((field, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-800">
                                    {field}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {hint.fieldDisambiguation.notes && (
                              <p className="text-xs text-muted-foreground mt-1">{hint.fieldDisambiguation.notes}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Qualifier Rules */}
                      {hint.hasQualifierRules && hint.qualifierRules && (
                        <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border">
                          <Label className="text-xs font-semibold text-blue-600 uppercase tracking-wide flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            Qualifier Preservation
                          </Label>
                          <div className="mt-2 space-y-2">
                            {hint.qualifierRules.preserve && hint.qualifierRules.preserve.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                <span className="text-xs text-blue-600 font-medium">PRESERVE:</span>
                                {hint.qualifierRules.preserve.map((q, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-blue-50 text-blue-800">
                                    {q}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {hint.qualifierRules.examples && hint.qualifierRules.examples.length > 0 && (
                              <div className="text-xs text-muted-foreground">
                                Examples: {hint.qualifierRules.examples.join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Semantic Clarifications */}
                      {hint.hasSemanticClarifications && hint.semanticClarifications && (
                        <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border">
                          <Label className="text-xs font-semibold text-amber-600 uppercase tracking-wide flex items-center gap-1">
                            <WarningIcon className="h-3 w-3" />
                            Semantic Clarifications
                          </Label>
                          <div className="mt-2 space-y-2">
                            {hint.semanticClarifications.mislabels && Object.keys(hint.semanticClarifications.mislabels).length > 0 && (
                              <div className="space-y-1">
                                <span className="text-xs text-amber-600 font-medium">MISLABELS:</span>
                                {Object.entries(hint.semanticClarifications.mislabels).map(([key, value], i) => (
                                  <div key={i} className="flex items-center gap-2 text-xs">
                                    <Badge variant="outline" className="bg-amber-50 text-amber-800">{key}</Badge>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-muted-foreground">actually means:</span>
                                    <Badge variant="secondary">{value}</Badge>
                                  </div>
                                ))}
                              </div>
                            )}
                            {hint.semanticClarifications.notes && (
                              <p className="text-xs text-muted-foreground mt-1">{hint.semanticClarifications.notes}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* No hints message */}
                      {!hint.hasDisambiguation && !hint.hasQualifierRules && !hint.hasSemanticClarifications && (
                        <div className="text-xs text-muted-foreground italic">
                          No extraction hints generated yet. Research to populate.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExtractionHintsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Canonical Attributes Editor Dialog */}
      <Dialog open={canonicalDialogOpen} onOpenChange={setCanonicalDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Canonical Attributes Editor
            </DialogTitle>
            <DialogDescription>
              View researched definitions for "{technicalResearch?.categoryName || 'category'}". Editing opens the shared definition and affects every category that uses it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search and Add New */}
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search attributes..."
                value={canonicalSearch}
                onChange={(e) => setCanonicalSearch(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={() => {
                  setShowNewAttributeForm(true);
                  setEditingAttribute(null);
                }}
                disabled={showNewAttributeForm}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Attribute
              </Button>
            </div>

            {/* New Attribute Form */}
            {showNewAttributeForm && (
              <Card className="border-2 border-blue-300 bg-blue-50 dark:bg-blue-950/20">
                <CardContent className="pt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">New Attribute</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowNewAttributeForm(false);
                        setNewAttribute({ data_type: 'string', enum_values: [] });
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Attribute Name *</Label>
                      <Input
                        value={newAttribute.attribute_name || ''}
                        onChange={(e) => setNewAttribute({ ...newAttribute, attribute_name: e.target.value })}
                        placeholder="e.g., bead_type"
                      />
                    </div>
                    <div>
                      <Label>Canonical Name *</Label>
                      <Input
                        value={newAttribute.canonical_name || ''}
                        onChange={(e) => setNewAttribute({ ...newAttribute, canonical_name: e.target.value })}
                        placeholder="e.g., Bead Type"
                      />
                    </div>
                    <div>
                      <Label>Data Type</Label>
                      <Select
                        value={newAttribute.data_type || 'string'}
                        onValueChange={(value) => setNewAttribute({ ...newAttribute, data_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string">String</SelectItem>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="boolean">Boolean</SelectItem>
                          <SelectItem value="enum">Enum</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Unit</Label>
                      <Input
                        value={newAttribute.unit || ''}
                        onChange={(e) => setNewAttribute({ ...newAttribute, unit: e.target.value })}
                        placeholder="e.g., mm, kg, etc."
                      />
                    </div>
                    {(newAttribute.data_type === 'number' || newAttribute.data_type === 'numeric') && (
                      <>
                        <div>
                          <Label>Typical Min</Label>
                          <Input
                            type="number"
                            value={newAttribute.typical_min ?? ''}
                            onChange={(e) => setNewAttribute({ ...newAttribute, typical_min: e.target.value ? Number(e.target.value) : null })}
                            placeholder="e.g., 60"
                          />
                        </div>
                        <div>
                          <Label>Typical Max</Label>
                          <Input
                            type="number"
                            value={newAttribute.typical_max ?? ''}
                            onChange={(e) => setNewAttribute({ ...newAttribute, typical_max: e.target.value ? Number(e.target.value) : null })}
                            placeholder="e.g., 700"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Enum Values - always show */}
                  <div>
                      <Label>Allowed Values</Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {(newAttribute.enum_values || []).map((value, i) => (
                          <Badge key={i} variant="secondary" className="flex items-center gap-1">
                            {value}
                            <button
                              onClick={() => handleRemoveEnumValue(value)}
                              className="ml-1 hover:text-red-500"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={newEnumValue}
                          onChange={(e) => setNewEnumValue(e.target.value)}
                          placeholder="Add allowed value..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddEnumValue(newEnumValue);
                            }
                          }}
                        />
                        <Button
                          variant="outline"
                          onClick={() => handleAddEnumValue(newEnumValue)}
                        >
                          Add
                        </Button>
                      </div>
                    </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={newAttribute.description || ''}
                      onChange={(e) => setNewAttribute({ ...newAttribute, description: e.target.value })}
                      placeholder="Description of this attribute..."
                      rows={2}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowNewAttributeForm(false);
                        setNewAttribute({ data_type: 'string', enum_values: [] });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleCreateAttribute} disabled={savingAttribute}>
                      {savingAttribute ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Create Attribute
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attributes List */}
            <ScrollArea className="h-[400px] pr-4">
              {canonicalLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : filteredCanonicalAttributes.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {canonicalUnavailableReason || (canonicalSearch ? 'No attributes match your search' : 'No canonical attributes defined for this category')}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredCanonicalAttributes.map((attr) => (
                    <Card
                      key={attr.id}
                      className={cn(
                        'transition-all',
                        editingAttribute?.id === attr.id
                          ? 'border-2 border-blue-400'
                          : 'hover:border-gray-400 cursor-pointer'
                      )}
                      onClick={() => {
                        if (editingAttribute?.id !== attr.id) {
                          // Normalize data_type from DB format ('numeric') to UI format ('number')
                          const shared = attr.shared_definition || attr;
                          const normalizedDataType = shared.data_type === 'numeric' ? 'number' : shared.data_type;
                          setEditingAttribute({ ...shared, write_scope: 'shared', data_type: normalizedDataType });
                          setShowNewAttributeForm(false);
                          setAiSuggestions(null); // Clear previous suggestions
                        }
                      }}
                    >
                      <CardContent className="pt-4">
                        {editingAttribute?.id === attr.id ? (
                          <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">Editing the shared definition. Saving a correction marks affected category research as needing refresh.</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Attribute Name</Label>
                                <Input
                                  value={editingAttribute.attribute_name}
                                  onChange={(e) => setEditingAttribute({ ...editingAttribute, attribute_name: e.target.value })}
                                />
                              </div>
                              <div>
                                <Label>Canonical Name</Label>
                                <Input
                                  value={editingAttribute.canonical_name}
                                  onChange={(e) => setEditingAttribute({ ...editingAttribute, canonical_name: e.target.value })}
                                />
                              </div>
                              <div>
                                <Label>Data Type</Label>
                                <Select
                                  value={editingAttribute.data_type}
                                  onValueChange={(value) => setEditingAttribute({ ...editingAttribute, data_type: value })}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="string">String</SelectItem>
                                    <SelectItem value="text">Text</SelectItem>
                                    <SelectItem value="number">Number</SelectItem>
                                    <SelectItem value="boolean">Boolean</SelectItem>
                                    <SelectItem value="enum">Enum</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label>Unit</Label>
                                <Input
                                  value={editingAttribute.unit || ''}
                                  onChange={(e) => setEditingAttribute({ ...editingAttribute, unit: e.target.value || null })}
                                />
                              </div>
                              {(editingAttribute.data_type === 'number' || editingAttribute.data_type === 'numeric') && (
                                <>
                                  <div>
                                    <Label>Typical Min</Label>
                                    <Input
                                      type="number"
                                      value={editingAttribute.typical_min ?? ''}
                                      onChange={(e) => setEditingAttribute({ ...editingAttribute, typical_min: e.target.value ? Number(e.target.value) : null })}
                                      placeholder="e.g., 60"
                                    />
                                  </div>
                                  <div>
                                    <Label>Typical Max</Label>
                                    <Input
                                      type="number"
                                      value={editingAttribute.typical_max ?? ''}
                                      onChange={(e) => setEditingAttribute({ ...editingAttribute, typical_max: e.target.value ? Number(e.target.value) : null })}
                                      placeholder="e.g., 700"
                                    />
                                  </div>
                                </>
                              )}
                            </div>

                            {/* Enum Values Editor - show for all types that can have allowed values */}
                            <div>
                              <Label>Allowed Values (enum_values)</Label>
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {(editingAttribute.enum_values || []).map((value, i) => (
                                    <Badge key={i} variant="secondary" className="flex items-center gap-1">
                                      {value}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveEnumValue(value);
                                        }}
                                        className="ml-1 hover:text-red-500"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </Badge>
                                  ))}
                                </div>
                                <div className="flex gap-2">
                                  <Input
                                    value={newEnumValue}
                                    onChange={(e) => setNewEnumValue(e.target.value)}
                                    placeholder="Add allowed value..."
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddEnumValue(newEnumValue);
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddEnumValue(newEnumValue);
                                    }}
                                  >
                                    Add
                                  </Button>
                                </div>
                            </div>

                            <div>
                              <Label>Description</Label>
                              <Textarea
                                value={editingAttribute.description || ''}
                                onChange={(e) => setEditingAttribute({ ...editingAttribute, description: e.target.value })}
                                rows={2}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>

                            {/* AI Suggestions Section */}
                            <div className="border-t border-dashed pt-4 mt-2">
                              <div className="flex items-center justify-between mb-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAskAI();
                                  }}
                                  disabled={loadingAiSuggestions}
                                  className="gap-2"
                                >
                                  {loadingAiSuggestions ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Sparkles className="h-4 w-4" />
                                  )}
                                  {loadingAiSuggestions ? 'Analyzing...' : 'Ask AI for suggestions'}
                                </Button>
                                {aiSuggestions && (
                                  <div className="flex gap-2">
                                    <Button
                                      variant="default"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        applyAllAiSuggestions();
                                      }}
                                      className="gap-1"
                                    >
                                      <Check className="h-3 w-3" />
                                      Apply All
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dismissAiSuggestions();
                                      }}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                )}
                              </div>

                              {/* Show AI suggestions if available */}
                              {aiSuggestions && (
                                <div className={cn(
                                  "rounded-lg p-3 space-y-3 border",
                                  aiSuggestions.based_on_research
                                    ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800"
                                    : "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800"
                                )}>
                                  {aiSuggestions.based_on_research && (
                                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300 text-sm font-medium">
                                      <Lightbulb className="h-4 w-4" />
                                      Based on Attribute Intelligence: {aiSuggestions.research_title}
                                    </div>
                                  )}
                                  {aiSuggestions.reasoning && (
                                    <p className={cn(
                                      "text-sm italic",
                                      aiSuggestions.based_on_research
                                        ? "text-green-700 dark:text-green-300"
                                        : "text-blue-700 dark:text-blue-300"
                                    )}>
                                      💡 {aiSuggestions.reasoning}
                                    </p>
                                  )}
                                  {aiSuggestions.sample_values_found && aiSuggestions.sample_values_found.length > 0 && (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      <span className="font-medium">Values found in products:</span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {aiSuggestions.sample_values_found.slice(0, 12).map((val, i) => (
                                          <Badge key={i} variant="outline" className="text-xs font-mono">
                                            {String(val)}
                                          </Badge>
                                        ))}
                                        {aiSuggestions.sample_values_found.length > 12 && (
                                          <Badge variant="outline" className="text-xs text-gray-400">
                                            +{aiSuggestions.sample_values_found.length - 12} more
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    {aiSuggestions.unit !== undefined && aiSuggestions.unit !== null && (
                                      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded px-2 py-1">
                                        <span><strong>Unit:</strong> {aiSuggestions.unit}</span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            applyAiSuggestion('unit', aiSuggestions.unit);
                                          }}
                                        >
                                          <Check className="h-3 w-3 text-green-600" />
                                        </Button>
                                      </div>
                                    )}
                                    {aiSuggestions.typical_min !== undefined && aiSuggestions.typical_min !== null && (
                                      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded px-2 py-1">
                                        <span><strong>Min:</strong> {aiSuggestions.typical_min}</span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            applyAiSuggestion('typical_min', aiSuggestions.typical_min);
                                          }}
                                        >
                                          <Check className="h-3 w-3 text-green-600" />
                                        </Button>
                                      </div>
                                    )}
                                    {aiSuggestions.typical_max !== undefined && aiSuggestions.typical_max !== null && (
                                      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded px-2 py-1">
                                        <span><strong>Max:</strong> {aiSuggestions.typical_max}</span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            applyAiSuggestion('typical_max', aiSuggestions.typical_max);
                                          }}
                                        >
                                          <Check className="h-3 w-3 text-green-600" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  {aiSuggestions.description !== undefined && aiSuggestions.description !== null && (
                                    <div className="flex items-start justify-between bg-white dark:bg-gray-800 rounded px-2 py-1">
                                      <span className="text-sm"><strong>Description:</strong> {aiSuggestions.description}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 flex-shrink-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          applyAiSuggestion('description', aiSuggestions.description);
                                        }}
                                      >
                                        <Check className="h-3 w-3 text-green-600" />
                                      </Button>
                                    </div>
                                  )}
                                  {aiSuggestions.enum_values && aiSuggestions.enum_values.length > 0 && (
                                    <div className="flex items-start justify-between bg-white dark:bg-gray-800 rounded px-2 py-1">
                                      <span className="text-sm"><strong>Allowed Values:</strong> {aiSuggestions.enum_values.join(', ')}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 flex-shrink-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          applyAiSuggestion('enum_values', aiSuggestions.enum_values);
                                        }}
                                      >
                                        <Check className="h-3 w-3 text-green-600" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex justify-between">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm('Are you sure you want to delete this attribute?')) {
                                    handleDeleteAttribute(attr.id);
                                  }
                                }}
                                disabled={deletingAttributeId === attr.id}
                              >
                                {deletingAttributeId === attr.id ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4 mr-2" />
                                )}
                                Delete
                              </Button>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingAttribute(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveAttribute();
                                  }}
                                  disabled={savingAttribute}
                                >
                                  {savingAttribute ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Save className="h-4 w-4 mr-2" />
                                  )}
                                  Save
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{attr.canonical_name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {attr.data_type}
                                </Badge>
                                {attr.unit && (
                                  <Badge variant="secondary" className="text-xs">
                                    {attr.unit}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono mb-1">
                                {attr.attribute_name}
                              </div>
                              {attr.description && (
                                <div className="text-sm text-muted-foreground">
                                  {attr.description}
                                </div>
                              )}
                              {attr.enum_values && attr.enum_values.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {attr.enum_values.slice(0, 5).map((val, i) => (
                                    <Badge key={i} variant="secondary" className="text-xs">
                                      {val}
                                    </Badge>
                                  ))}
                                  {attr.enum_values.length > 5 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{attr.enum_values.length - 5} more
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                            <Edit3 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCanonicalDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Research Dialog */}
      <Dialog open={batchResearchDialogOpen} onOpenChange={setBatchResearchDialogOpen}>
        <DialogContent className="flex max-w-4xl max-h-[90dvh] flex-col overflow-hidden">
          <DialogHeader className="shrink-0 pr-6">
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="h-5 w-5" />
              Complete Category Research
            </DialogTitle>
            <DialogDescription>
              Select categories to research and choose which research steps to run.
              Required research runs first. Basic Characteristics replaces buyer personas and their benefit weights after the new results pass validation.
              You can close this dialog while research runs.
            </DialogDescription>
          </DialogHeader>

          <div role="region" aria-label="Research options and progress" tabIndex={0}
            className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain space-y-4 py-4 pr-2">
            {!batchBlocksAll && <div className="rounded-lg border p-3 space-y-2">
              <label className="flex items-center gap-2 font-medium">
                <Checkbox checked disabled />
                Research only
              </label>
              <p className="text-sm text-muted-foreground">
                Update research for the catalog paths shown below. Product enrichment is a separate action.
              </p>
              <p className="text-sm">Site: {selectedSite?.name || 'Select a site'} · Sector: {batchWriteSectorId}</p>
            </div>}
            {selectedBatchOverlap && <p role="status" className="text-sm text-amber-700">A selected category has active or unresolved research. Inspect its saved job below, or select another category.</p>}
            {researchJobs.filter(job=>job.id!==batchResearchJob?.id && job.siteId===selectedSite?.id && researchJobBlocks(job)).map(job=><div key={job.id} className="rounded border p-3 text-sm">
              <p>Saved research: {job.status} · {Object.keys(job.targets).join(', ')}</p>
              <p className="break-all">Research job: {job.id}</p>
              {job.lastError&&<p>{job.lastError}</p>}
              <Button variant="outline" disabled={startingBatchResearch || preparingBatchResearch} onClick={()=>inspectSavedResearch(job)}>Inspect saved research {job.id}</Button>
            </div>)}
            {/* Research Steps Selection */}
            <div>
              {researchHistoryWarning && <div role="status" className="mb-3 text-sm text-amber-700">{researchHistoryWarning}</div>}
              <Label className="text-sm font-medium mb-2 block">Research Steps to Run</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'characteristics', label: 'Basic Characteristics', description: 'Product types, brand tiers, use cases, personas' },
                  { id: 'canonical', label: 'Canonical Attributes', description: 'Technical spec definitions' },
                  { id: 'perceived_benefits', label: 'Perceived Benefits', description: 'Customer-facing benefit descriptions' },
                  { id: 'intelligence', label: 'Attribute Intelligence', description: 'Value interpretation guidance' },
                  { id: 'extraction_hints', label: 'Extraction Hints', description: 'Field disambiguation rules' },
                ].map(step => (
                  <div
                    key={step.id}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all',
                      batchResearchSteps.includes(step.id)
                        ? 'bg-green-50 border-green-300 dark:bg-green-950/30 dark:border-green-700'
                        : 'bg-gray-50 border-gray-200 dark:bg-gray-900 dark:border-gray-700 opacity-60'
                    )}
                    onClick={() => { if (!preparingBatchResearch && !startingBatchResearch && !batchSelectionLocked) toggleBatchStep(step.id); }}
                    title={step.description}
                  >
                    {batchResearchSteps.includes(step.id) ? (
                      <CheckSquare className="h-4 w-4 text-green-600" />
                    ) : (
                      <Square className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="text-sm">{step.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Selection */}
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <Label className="text-sm font-medium">Categories to Research</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleSelectUnresearchedCategories} disabled={preparingBatchResearch || startingBatchResearch || batchSelectionLocked}>
                    Select Unresearched
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleSelectAllCategories} disabled={preparingBatchResearch || startingBatchResearch || batchSelectionLocked}>
                    {selectedBatchCategories.length === availableCategories.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </div>
              <ScrollArea className="h-[300px] border rounded-lg p-2">
                {availableCategories.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    No categories available. Import products first.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {availableCategories.map(cat => {
                      // ⛔ ROW IDENTITY, NOT PATH — see rowKey. Keying selection or React on the
                      // path made the two duplicates one entry: picking either picked both, and
                      // they were indistinguishable while each meant a different research row.
                      const key = rowKey(cat);
                      const isSelected = selectedBatchCategories.includes(key);
                      // Progress comes back keyed by what we POSTED, which is the target path.
                      const progressTarget = batchResearchJob?.targets[cat.categoryPath];
                      const progress = progressTarget ? batchResearchJob?.progressDetails?.[progressTarget] : undefined;

                      return (
                        <div
                          key={key}
                          className={cn(
                            'flex items-center justify-between p-2 rounded cursor-pointer transition-all',
                            isSelected
                              ? 'bg-blue-50 dark:bg-blue-950/30'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-900'
                          )}
                          onClick={() => { if (!preparingBatchResearch && !startingBatchResearch && !batchSelectionLocked) toggleBatchCategory(key); }}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <Checkbox
                              checked={isSelected}
                              aria-label={`Select ${batchTargetPath(cat)}`}
                              disabled={preparingBatchResearch || startingBatchResearch || batchSelectionLocked}
                              onClick={event => event.stopPropagation()}
                              onCheckedChange={() => toggleBatchCategory(key)}
                            />
                            <div className="min-w-0">
                              <div className="font-medium text-sm">{cat.categoryName}</div>
                              <div className="text-xs break-all">Research target: {batchTargetPath(cat)}</div>
                              <div className="text-xs text-muted-foreground">{cat.productCount} products</div>
                              {progress?.error && <details className="text-xs text-red-600 break-words" onClick={event => event.stopPropagation()}>
                                <summary className="cursor-pointer">{batchJobActive ? 'Research error' : 'Previous attempt error'}</summary>
                                <p>{progress.error}</p>
                              </details>}
                              {/* ⛔ THE SAME BACKING-ROW LABEL AND HEALTH AS THE CATALOG TABLE. Two
                                  entries that are the same category and different research rows must
                                  be distinguishable HERE TOO — this is the tab that acts on many at
                                  once, so an indistinguishable pair costs more here, not less. */}
                              {cat.researchCategoryPath && cat.researchCategoryPath !== cat.categoryPath && (
                                <div className="text-[10px] font-mono text-muted-foreground break-all">
                                  {cat.researchKeyIsLeaf ? 'stored under LEAF: ' : 'stored under FULL PATH: '}
                                  {cat.researchCategoryPath}
                                </div>
                              )}
                              {cat.hasResearch && cat.personaCount !== null && cat.personaCount > 0 && (
                                <div className={cn(
                                  'text-[10px]',
                                  cat.weightedPersonaCount === 0 ? 'text-red-600 font-medium' : 'text-muted-foreground'
                                )}>
                                  {cat.weightedPersonaCount} of {cat.personaCount} personas weighted
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            {cat.hasResearch ? (
                              <Badge variant="default" className="bg-green-600 text-xs">
                                Stored research
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">
                                No exact-path record
                              </Badge>
                            )}
                            {progress && (
                              <div className="flex items-center gap-1">
                                {Object.entries(progress.steps || {}).map(([step, status]) => (
                                  <div
                                    key={step}
                                    className={cn('w-2 h-2 rounded-full', batchStepDotClass(status))}
                                    title={batchStepTitle(step, status)}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
              <div className="mt-2 text-sm text-muted-foreground">
                {selectedBatchCategories.length} of {availableCategories.length} categories selected
              </div>
            </div>

            {/* Job Progress */}
            {batchResearchError && <div role="alert" className="text-sm text-red-600">{batchResearchError}</div>}
            {researchRecoveryBlocked && !unacknowledgedSubmission && <Button variant="outline" onClick={()=>setResearchRestoreRetry(value=>value+1)}>Check research recovery</Button>}
            {unacknowledgedSubmission && <div className="rounded border p-3 space-y-2">
              <p>Saved request: {unacknowledgedSubmission.requestId} · {unacknowledgedSubmission.siteName}</p>
              <Button variant="outline" disabled={startingBatchResearch} onClick={()=>{
                // Use the same recovery path as restore/refresh so a resolved
                // receipt cannot bypass archival and active-job discovery.
                setResearchRecoveryBlocked(true);
                setResearchRestoreRetry(value=>value+1);
              }}>Check request status</Button>
              <Button disabled={startingBatchResearch} onClick={()=>submitResearch(unacknowledgedSubmission)}>Retry same request</Button>
            </div>}
            {batchResearchJob && (
              <Card className={cn(
                'p-4 min-w-0 break-words',
                batchResearchJob.status === 'running' ? 'border-blue-200 bg-blue-50 dark:bg-blue-950/20' :
                batchResearchJob.status === 'completed' ? 'border-green-200 bg-green-50 dark:bg-green-950/20' :
                batchResearchJob.status === 'failed' ? 'border-red-200 bg-red-50 dark:bg-red-950/20' :
                ''
              )}>
                {!batchJobActive && <p className="mb-2 text-sm font-medium">{batchJobRetired ? 'Retired legacy research · Read only' : 'Previous attempt'}</p>}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    {batchResearchJob.status === 'running' && <Loader2 className="h-4 w-4 animate-spin" />}
                    {batchResearchJob.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-600" />}
                    {batchResearchJob.status === 'failed' && <XCircle className="h-4 w-4 text-red-600" />}
                    <span className="font-medium capitalize">{batchResearchJob.status === 'completed' && batchResearchGapCount(batchResearchJob)>0 ? 'Finished with partial results' : batchResearchJob.status}</span>
                  </div>
                  <span className="text-sm">
                    {batchResearchJob.completedCategories}/{batchResearchJob.totalCategories} complete
                    {batchResearchGapCount(batchResearchJob) > 0 && ` · ${batchResearchGapCount(batchResearchJob)} have gaps`}
                  </span>
                </div>
                <Progress value={batchResearchJob.progressPercent} className="h-2" />
                <div className="mt-2 text-xs break-all">Research job: {batchResearchJob.id} · {batchResearchJob.siteName}</div>
                {batchResearchJob.runner==='v2' && <div className="mt-2 text-sm">
                  <p>Admitted categories:</p>
                  <ul>{Object.keys(batchResearchJob.targets).map(path=><li key={path}>{path}</li>)}</ul>
                </div>}
                {batchResearchJob.runner==='v2' && batchResearchJob.refused===null && <Alert className="mt-2">
                  <AlertDescription>This older job has no complete saved refusal list. Only its admitted categories are known.</AlertDescription>
                </Alert>}
                {!!batchResearchJob.refused?.length && <Alert className="mt-2">
                  <AlertDescription>
                    Categories not admitted:
                    <ul>{batchResearchJob.refused.map((target,index)=><li key={`${target.categoryPath}:${index}`}>{target.categoryPath}: {target.error}</li>)}</ul>
                  </AlertDescription>
                </Alert>}
                {batchResearchJob.status === 'cancelled' && <div className="mt-2 space-y-1">
                  <p className="text-sm">Work already in flight may still finish. Refresh the receipt to see its recorded outcome.</p>
                  <Button size="sm" variant="outline" onClick={refreshCancelledReceipt}>Refresh receipt</Button>
                </div>}
                {!batchJobRetired && <ResearchContinuationPanel key={batchResearchJob.id} job={batchResearchJob}
                  actorId={user.id} apiUrl={API_URL} authFetch={authFetch} onReceipt={adoptResearchReceipt}/>}
                {batchJobRetired && <p className="mt-2 text-sm">This legacy job cannot be resumed or cancelled. Handed-off targets use new research; other targets remain blocked.</p>}
                <details key={`${batchResearchJob.id}:${batchJobActive}`} open={batchJobActive} className="mt-3">
                  <summary className="cursor-pointer text-sm font-medium">{batchJobActive ? 'Current research details' : 'Previous attempt details'}</summary>
                {Object.entries(batchResearchJob.progressDetails).map(([path,details])=><div key={path} className="mt-3 border-t pt-2">
                  <p className="font-mono text-xs break-all">{path}</p>
                  {Object.entries(details.steps || {}).map(([step,status])=>{
                    const result=details.stepResults?.[step];
                    return <div key={step} className="text-sm"><span>{step.replace(/_/g,' ')}: {status.replace(/_/g,' ')}</span>
                      {result?.target && <span> · Sector {result.target.sectorId} / {result.target.categoryPath}</span>}
                      {result?.error && <p className="text-red-600">{result.error}</p>}
                      {Boolean(result?.reviewItems?.length) && <details className="mt-1">
                        <summary>{result!.reviewItems!.length} findings need review</summary>
                        <p>These uncertain findings are retained for review and are excluded from supported research outputs.</p>
                        <ul>{result!.reviewItems!.map(item=><li key={`${item.aspect}:${item.attribute}`}>
                          <strong>{item.attribute.replace(/_/g,' ')}</strong> ({item.aspect.replace(/_/g,' ')}): {item.reason}
                        </li>)}</ul>
                      </details>}
                      {result?.blockedBy?.map(dependency=><p key={dependency.step}>Depends on {dependency.step.replace(/_/g,' ')}: {dependency.status.replace(/_/g,' ')}{dependency.error ? ` — ${dependency.error}` : ''}</p>)}
                      {Boolean(result?.gaps?.length) && <details className="mt-1"><summary>Research gaps ({result!.gaps!.length})</summary>
                        {result!.gaps!.map((gap,index)=><ResearchGap key={index} gap={gap}/>)}
                      </details>}
                      {result?.delivery && 'acceptedBenefitIds' in result.delivery && <p>
                        Published benefits: {result.delivery.acceptedBenefitIds.length}/{result.delivery.requestedBenefitIds.length}
                      </p>}
                      {result?.delivery && 'aspects' in result.delivery && <details className="mt-1"><summary>Published research and gaps</summary>
                        {Object.entries(result.delivery.aspects).map(([aspect,outcome])=><div key={aspect}>
                          <strong>{aspect.replace(/_/g,' ')}</strong>: {outcome.published ? 'Published' : 'Not published'} · {outcome.status.replace(/_/g,' ')}
                          {outcome.error && <p className="text-red-600">{outcome.error}</p>}
                          {outcome.coverage?.gaps?.map((gap,index)=><ResearchGap key={index} gap={gap}/>)}
                        </div>)}
                      </details>}
                      {result?.sectorPersonaSync && <p>Sector persona sync: {result.sectorPersonaSync.status} {result.sectorPersonaSync.error}</p>}
                    </div>;
                  })}
                  {details.error && <p className="text-red-600">{details.error}</p>}
                </div>)}
                {batchResearchJob.lastError && (
                  <div className="mt-2 text-sm text-red-600">
                    Last error: {batchResearchJob.lastError}
                  </div>
                )}
                </details>
              </Card>
            )}
          </div>

          <DialogFooter className="shrink-0 gap-2 border-t pt-4 sm:gap-0">
            <Button variant="outline" onClick={() => setBatchResearchDialogOpen(false)}>
              Close
            </Button>
            {batchJobActive && (
              <Button variant="destructive" onClick={handleCancelBatchResearch}>
                <Square className="h-4 w-4 mr-2" />
                Cancel Job
              </Button>
            )}
            {(!batchJobActive || !batchBlocksAll) && (
              <Button
                onClick={() => {
                  // ⛔⛔ CONFIRM ON EVERY STEP THAT OVERWRITES, NOT JUST 'characteristics'.
                  // Gating on that one step let a run that rebuilds perceived benefits, re-enriches
                  // products, or overwrites validation state slip past the warning entirely.
                  // ⇒ CLASSIFIED AGAINST THE BACKEND, NOT THE STEP LABELS — what each step actually
                  // does, in backend-merged/routes/category-characteristics.js executeResearchStep:
                  //   characteristics    :2452 researchCharacteristics() — rebuilds the stored row.
                  //   perceived_benefits :2487 re-researches perceptions AND force-sets
                  //                            is_validated = true on sector_category_perceived_
                  //                            attributes, overwriting operator validation state.
                  //   extraction_hints   :2529 researchAndReenrichCategory() — researches hints AND
                  //                            RE-ENRICHES PRODUCTS. Widest blast radius of the five.
                  //   intelligence       :2501 researchAttributeValueInterpretations(). ⚠️ I did NOT
                  //                            verify its overwrite semantics. UNVERIFIED ON A
                  //                            DESTRUCTIVE PATH COUNTS AS DESTRUCTIVE — the reverse
                  //                            assumption is the one that ships silent data loss.
                  //   canonical          :2470 generateMappingsForCategory(forceRegenerate: FALSE)
                  //                            — verified NON-overwriting by that flag, so it does
                  //                            not arm the warning on its own. Warning on a step
                  //                            that overwrites nothing trains click-through.
                  const OVERWRITING_STEPS = ['characteristics', 'perceived_benefits', 'extraction_hints', 'intelligence'];
                  const destructive = batchResearchSteps.some((s) => OVERWRITING_STEPS.includes(s));
                  // ⛔ AND THE DIALOG MUST SAY WHICH OF THEM. It used to claim the run replaced
                  // EVERYTHING, buyer personas included, even when the only destructive step was
                  // perceived benefits. Overstating the blast radius erodes a warning exactly as
                  // fast as understating it — and this is the path where they most need to read.
                  setPendingBatchStepLabels(
                    batchResearchSteps.filter((s) => OVERWRITING_STEPS.includes(s)).map((s) =>
                      s === 'extraction_hints' && batchResearchOnly ? 'extraction hints (research only)' : BATCH_STEP_LABELS[s] || s)
                  );
                  // Resolve the selected paths before deciding which sector's research
                  // needs confirmation. The handler freezes that exact submission.
                  handleStartBatchResearch(destructive);
                }}
                disabled={selectedBatchCategories.length === 0 || batchResearchSteps.length === 0 || batchBlocksAll || selectedBatchOverlap || preparingBatchResearch || startingBatchResearch || !!unacknowledgedSubmission || researchRecoveryBlocked || !selectedSite?.id}
              >
                {startingBatchResearch || preparingBatchResearch ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {preparingBatchResearch ? 'Resolving sectors...' : 'Starting...'}
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Start Research ({selectedBatchTargets.length} categories)
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Knowledge Chat Dialog */}
      <Dialog open={chatDialogOpen} onOpenChange={(open) => {
        setChatDialogOpen(open);
        if (!open) {
          setChatMessage('');
          setChatParsedIntent(null);
          setChatHistory([]);
        }
      }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Talk to Knowledge
            </DialogTitle>
            <DialogDescription>
              Describe what you want to change in natural language. For example: "Add Unicycle as a product type" or "Remove Gravel Bike".
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Chat History */}
            {chatHistory.length > 0 && (
              <ScrollArea className="h-48 rounded-md border p-3">
                <div className="space-y-3">
                  {chatHistory.map((msg, idx) => (
                    <div key={idx} className={cn(
                      'p-2 rounded-lg text-sm',
                      msg.role === 'user' ? 'bg-primary/10 ml-8' : 'bg-muted mr-8'
                    )}>
                      {msg.content}
                      {/*
                        ⛔ This container used to be bg-green unconditionally, so a failure was
                        rendered as red text inside a green box. The colour is the first thing
                        read; it must follow the outcome, not the fact that a result exists.
                      */}
                      {msg.result && (() => {
                        const chrome = {
                          success: { box: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400', Icon: CheckCircle },
                          no_change: { box: 'bg-muted text-muted-foreground', Icon: AlertCircle },
                          failed: { box: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400', Icon: XCircle },
                        }[msg.result.severity];
                        return (
                          <div className={cn('mt-2 p-2 rounded text-xs', chrome.box)}>
                            <div className="flex items-center gap-1">
                              <chrome.Icon className="h-3 w-3" />
                              {msg.result.message}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}

            {/* Parsed Intent Confirmation */}
            {chatParsedIntent && (
              <Alert>
                <Wand2 className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-medium mb-1">{chatParsedIntent.confirmationMessage}</div>
                  <div className="text-xs text-muted-foreground">
                    Operation: <code>{chatParsedIntent.operation}</code> |
                    Type: <code>{chatParsedIntent.conceptType}</code> |
                    Name: <code>{chatParsedIntent.conceptName}</code>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Input */}
            <div className="flex gap-2">
              <Input
                placeholder="e.g., Add Unicycle as a product type"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && chatMessage.trim()) {
                    e.preventDefault();
                    handleChatSend();
                  }
                }}
                disabled={chatLoading || chatExecuting}
              />
              <Button
                onClick={handleChatSend}
                disabled={!chatMessage.trim() || chatLoading || chatExecuting}
              >
                {chatLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {chatParsedIntent && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setChatParsedIntent(null)}>
                Cancel
              </Button>
              <Button onClick={handleChatExecute} disabled={chatExecuting}>
                {chatExecuting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Executing...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {chatParsedIntent.operation === 'add_concept' ? 'Add' :
                     chatParsedIntent.operation === 'remove_concept' ? 'Remove' : 'Update'} {chatParsedIntent.conceptName}
                  </>
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/*
        Near-miss advisory confirmation (task-po9ac21icv fix (3)).

        The server refuses a near-duplicate canonical name with a 409 and names the attributes it
        collided with. This dialog is the ONLY place `confirm_near_miss: true` is sent. Both exits
        are explicit: cancelling leaves the form filled so the operator can rename to the existing
        attribute, confirming re-POSTs the same body with the flag. There is no timeout, no
        auto-confirm, and no "remember this choice" — every seated near-miss is an adjudicated one.
      */}
      <Dialog
        open={nearMissConfirm !== null}
        onOpenChange={(open) => { if (!open) setNearMissConfirm(null); }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Possible duplicate attribute
            </DialogTitle>
            <DialogDescription>
              {nearMissConfirm && (
                <>
                  <span className="font-mono font-medium">{nearMissConfirm.attributeName}</span>
                  {' looks like a near-duplicate of '}
                  {nearMissConfirm.nearMisses.length}
                  {nearMissConfirm.nearMisses.length === 1 ? ' attribute' : ' attributes'}
                  {' already registered in '}
                  <span className="font-mono">{nearMissConfirm.productCategory}</span>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-2">
              {nearMissConfirm?.nearMisses.map((m) => (
                <div key={m.name} className="rounded-md border p-3">
                  <div className="font-mono text-sm font-medium">{m.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{m.reason}</div>
                </div>
              ))}
            </div>

            <Alert>
              <AlertDescription className="text-sm">
                Seating both splits extraction across two canonicals: products carry the value under
                one name while comparison reads the other, which thins the table. Prefer reusing the
                existing attribute.
                <br />
                <br />
                This check is deliberately broad, so legitimate pairs do trip it — {' '}
                <span className="font-mono">material</span> vs{' '}
                <span className="font-mono">material_density</span>, {' '}
                <span className="font-mono">size</span> vs <span className="font-mono">size_unit</span>.
                If these are genuinely distinct facets, create it anyway.
                <br />
                <br />
                The names above are the ones <em>registered</em> in this category, which may include
                a <strong>retired</strong> one — the check reads all rows on purpose so it can catch
                a revive the uniqueness constraint cannot see. It cannot tell you which is which, so
                if you recognise a name you deprecated, do not reuse it: pick a distinct name, or
                revive it deliberately.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNearMissConfirm(null)}>
              Cancel — reuse the existing attribute
            </Button>
            <Button
              variant="destructive"
              disabled={savingAttribute}
              onClick={() => submitCreateAttribute(true)}
            >
              {savingAttribute ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create anyway — these are distinct facets'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ⛔ The catalog table's Refresh, on a row that ALREADY has research. Same rebuild endpoint
          as Re-Research All, so it needs the same warning. */}
      <RefreshConfirmDialog
        open={pendingTrigger !== null}
        onOpenChange={(o) => { if (!o) setPendingTrigger(null); }}
        scope="all"
        categoryPath={pendingTrigger?.path ?? ''}
        /* ⛔ THE SECTOR RESOLVED FOR THIS ROW, and the exact value handleTriggerResearch is handed
           below — not the detail panel's. */
        sectorId={pendingTrigger?.sectorId}
        /* The catalog row does not carry the persona list, so the count is genuinely unknown here —
           and unknown must say so rather than render a zero. */
        personaCount={null}
        loading={triggeringResearch !== null}
        onConfirm={() => {
          const pending = pendingTrigger;
          setPendingTrigger(null);
          if (pending) handleTriggerResearch(pending.path, pending.sectorId);
        }}
      />

      {/* ⛔ The "New category name" box, when the typed name matches a category that ALREADY holds
          research. The control says New; the endpoint replaces. */}
      <RefreshConfirmDialog
        open={pendingNewCategory !== null}
        onOpenChange={(o) => { if (!o) setPendingNewCategory(null); }}
        scope="all"
        categoryPath={pendingNewCategory ?? ''}
        /* Same sector the handler posts to — it does not resolve per category. */
        sectorId={sectorId}
        /* The typed-name path does not carry the persona list, so the count is genuinely unknown. */
        personaCount={null}
        /* ⛔ FROZEN, like the confirm below. Reading newCategoryName here would make the spinner
           track a category the operator has since typed over. */
        loading={pendingNewCategory !== null && triggeringResearch === pendingNewCategory}
        onConfirm={() => {
          const frozen = pendingNewCategory;
          setPendingNewCategory(null);
          if (frozen) handleResearchNewCategory(frozen);
        }}
      />

      {/* ⛔ Batch Start Research, when the selection contains already-researched categories and a
          step that rebuilds them. This one can overwrite MANY categories in a single click. */}
      <RefreshConfirmDialog
        open={pendingBatchPaths !== null}
        onOpenChange={(o) => { if (!o) {setPendingBatchPaths(null);setPendingBatchSubmission(null);} }}
        scope="all"
        categoryPath=""
        /* ⛔ THE SECTOR THE BATCH ENDPOINT IS ACTUALLY POSTED TO — same const the handler uses. */
        sectorId={batchWriteSectorId}
        personaCount={null}
        categoriesAffected={pendingBatchPaths ?? undefined}
        batchSteps={pendingBatchStepLabels}
        /* Personas come from the characteristics step. A run without it does not touch them, and
           claiming otherwise is the same erosion as claiming too little. */
        batchTouchesPersonas={pendingBatchSubmission?.researchSteps.includes('characteristics')}
        loading={startingBatchResearch}
        onConfirm={() => {
          const submission=pendingBatchSubmission;
          setPendingBatchPaths(null);setPendingBatchSubmission(null);
          if (submission && confirmedPreparationKeyRef.current===batchPreparationKeyRef.current) submitResearch(submission);
        }}
      />
    </div>
  );
}
